// src/components/DocWriterTable.js

/**
 * DocWriterTable
 *
 * UPDATED 2026-08-12 v14: functional range styling, cell borders, supplemental lines, and directional height resize.
 * The single-object ownership contract is preserved: DocWriterTable owns only the live
 * TABLE object, its cell/row/column model, draft edit dialog, and table-specific screen
 * controls. DocCanvasEditor remains the sole owner of complete document HTML, Letter
 * page flow, cross-page placement, document Undo/Redo history, Save/Preview
 * serialization, and whole-table movement. Table transactions continue to send only
 * one logical table envelope back to the host; they never replace or serialize the
 * surrounding document.
 *
 * v14 specifically:
 * - preserves every v13 ownership/stability guard and the isolated DIALOG-LOCAL Undo/Redo;
 * - fixes Selected-area styling so Apply to is persistent and every target actually receives
 *   alignment, font, color, and background changes instead of snapping back to Active cell;
 * - makes selected-cell/range Top/Right/Bottom/Left border overrides visually authoritative
 *   (inline !important), with live Solid/Double/Dashed/Dotted/None, width, and color updates;
 * - strengthens mouse-drag rectangular selection with pointer capture so horizontal,
 *   vertical, and block ranges can be highlighted and merged without Ctrl/Cmd-click;
 * - upgrades the single title/note model to repeatable top-title and bottom-note lines;
 *   every supplemental line has independent Left/Center/Right, Bold, Italic, Underline,
 *   and 3-96px font-size controls while inheriting the table/document text color;
 * - restores deterministic top/bottom middle-handle height collapse by resizing from the
 *   pointer-down row-height baseline instead of repeatedly measuring already-mutated rows;
 * - keeps the existing synchronized bottom preview scrollbar, Canvas transaction handoff,
 *   table visibility guards, whole-table movement, and document Undo/Redo separation intact.
 *
 * Dedicated native-table component/module for the Doc Writer canvas.
 *
 * Responsibilities:
 * - Recognize Markdown, HTML, AI-generated, pasted, reopened, and toolbar tables.
 * - Normalize every table into one editable native HTML structure.
 * - Add stable table, column, row, and cell IDs.
 * - Preserve explicit column widths and infer useful widths when none exist.
 * - Resize columns, rows, and the whole table without clamping cell content.
 * - Add/delete rows and columns, distribute columns, and toggle a header row.
 * - Split cells by rows/columns and merge rectangular cell selections safely.
 * - Expose a stable interaction model for move handles, insertion controls,
 *   context menus, and whole-table drag/drop owned by DocCanvasEditor.
 * - Handle Tab/Shift+Tab navigation and create a row from the final cell.
 * - Reassemble logical tables that were split across canvas pages and request
 *   repagination through the host editor callback.
 * - Own robust manual table construction and insertion fallback while allowing
 *   DocCanvasEditor to provide the preferred saved-caret placement adapter.
 *
 * This file intentionally has no React dependency. It owns the Doc Writer table
 * model, native-table behavior, and screen-only table controls. It may request a
 * host document snapshot for Undo metadata, but it never owns whole-document
 * serialization. DocCanvasEditor remains the document/page/history authority.
 */

export const DOC_TABLE_ATTR = "data-doc-table";
export const DOC_TABLE_ID_ATTR = "data-doc-table-id";
export const DOC_TABLE_SOURCE_ATTR = "data-doc-table-source";
export const DOC_COLUMN_ID_ATTR = "data-doc-column-id";
export const DOC_ROW_ID_ATTR = "data-doc-row-id";
export const DOC_CELL_ID_ATTR = "data-doc-cell-id";
export const DOC_TABLE_FRAGMENT_ATTR = "data-doc-table-fragment";
export const DOC_TABLE_SELECTED_ATTR = "data-doc-table-selected";
export const DOC_CELL_SELECTED_ATTR = "data-doc-cell-selected";
export const DOC_REPEAT_ONLY_ATTR = "data-doc-canvas-repeat-only";
export const DOC_CANVAS_PAGE_ATTR = "data-doc-canvas-page";
export const DOC_WRITER_TABLE_ATTR = "data-doc-writer-table";
export const DOC_WRITER_TABLE_VERSION_ATTR = "data-doc-writer-table-version";
export const DOC_WRITER_TABLE_UI_ATTR = "data-doc-writer-table-ui";
export const DOC_WRITER_TABLE_EDIT_OPEN_ATTR = "data-doc-writer-table-edit-open";
export const DOC_TABLE_MOVABLE_ATTR = "data-doc-table-movable";
export const DOC_TABLE_CONTEXT_MENU_ATTR = "data-doc-table-context-menu";
export const DOC_TABLE_INTERACTION_ATTR = "data-doc-table-interaction";
export const DOC_TABLE_CELL_INTERACTIVE_ATTR = "data-doc-table-cell-interactive";
export const DOC_TABLE_ALIGNMENT_ATTR = "data-doc-table-align";
export const DOC_TABLE_BORDER_WIDTH_ATTR = "data-doc-table-border-width";
export const DOC_TABLE_BORDER_STYLE_ATTR = "data-doc-table-border-style";
export const DOC_TABLE_BORDER_COLOR_ATTR = "data-doc-table-border-color";
export const DOC_TABLE_OUTER_BORDER_ATTR = "data-doc-table-outer-border";
export const DOC_TABLE_ROW_LINES_ATTR = "data-doc-table-row-lines";
export const DOC_TABLE_COLUMN_LINES_ATTR = "data-doc-table-column-lines";
// Border-and-spacing only: optional per-row/per-column overrides. These do not
// replace the four existing whole-table Show controls; they refine only the
// internal grid lines touching the active row or active column.
const DOC_TABLE_SELECTED_ROW_GRID_ATTR = "data-doc-table-selected-row-grid";
const DOC_TABLE_SELECTED_COLUMN_GRID_ATTR = "data-doc-table-selected-column-grid";
export const DOC_TABLE_PADDING_X_ATTR = "data-doc-table-padding-x";
export const DOC_TABLE_PADDING_Y_ATTR = "data-doc-table-padding-y";
export const DOC_TABLE_LINE_HEIGHT_ATTR = "data-doc-table-line-height";
export const DOC_TABLE_TITLE_ATTR = "data-doc-table-title";
export const DOC_TABLE_NOTE_ATTR = "data-doc-table-note";
export const DOC_TABLE_NOTE_ROW_ATTR = "data-doc-table-note-row";
export const DOC_TABLE_TITLE_LINE_ATTR = "data-doc-table-title-line";
export const DOC_TABLE_NOTE_LINE_ATTR = "data-doc-table-note-line";
export const DOC_TABLE_SUPPLEMENTAL_ID_ATTR = "data-doc-table-supplemental-id";
export const DOC_CELL_BORDER_TOP_ATTR = "data-doc-cell-border-top";
export const DOC_CELL_BORDER_RIGHT_ATTR = "data-doc-cell-border-right";
export const DOC_CELL_BORDER_BOTTOM_ATTR = "data-doc-cell-border-bottom";
export const DOC_CELL_BORDER_LEFT_ATTR = "data-doc-cell-border-left";
export const DOC_CELL_NUMBER_FORMAT_ATTR = "data-doc-cell-number-format";
export const DOC_CELL_NUMBER_VALUE_ATTR = "data-doc-cell-number-value";
export const DOC_CELL_HEADER_STYLE_CACHE_ATTR = "data-doc-table-header-style-cache";

// Legacy reopened documents may carry the former version marker. Normalize it
// away so new Doc Writer documents use only the DocWriterTable identity.
const LEGACY_TABLE_VERSION_ATTR = "data-doc-table-engine";

export const DOC_WRITER_TABLE_VERSION = "14";

export const TABLE_COMMANDS = Object.freeze({
  INSERT_ROW_ABOVE: "insert-row-above",
  INSERT_ROW_BELOW: "insert-row-below",
  INSERT_COLUMN_LEFT: "insert-column-left",
  INSERT_COLUMN_RIGHT: "insert-column-right",
  SPLIT_CELL_COLUMNS: "split-cell-columns",
  SPLIT_CELL_ROWS: "split-cell-rows",
  MERGE_CELLS: "merge-cells",
  DELETE_ROW: "delete-row",
  DELETE_COLUMN: "delete-column",
  DELETE_TABLE: "delete-table",
  DISTRIBUTE_COLUMNS: "distribute-columns",
  TOGGLE_HEADER_ROW: "toggle-header-row",
  ALIGN_LEFT: "align-left",
  ALIGN_CENTER: "align-center",
  ALIGN_RIGHT: "align-right",
});

// There is intentionally no 10 x 10 product or picker limit. The toolbar may
// accept values such as 20 x 20 and DocWriterTable will attempt to create them.
// These very generous safety ceilings exist only to prevent an accidental DOM
// explosion from malformed input; they are not normal UI limits.
export const TABLE_CREATION_MAX_ROWS = 1000;
export const TABLE_CREATION_MAX_COLUMNS = 250;
export const IMPORT_MAX_ROWS = TABLE_CREATION_MAX_ROWS;
export const IMPORT_MAX_COLUMNS = TABLE_CREATION_MAX_COLUMNS;
export const MANUAL_CREATE_MAX_ROWS = TABLE_CREATION_MAX_ROWS;
export const MANUAL_CREATE_MAX_COLUMNS = TABLE_CREATION_MAX_COLUMNS;

// Compatibility aliases for the current toolbar until it is upgraded next.
// They deliberately point to the generous creation limits, not to 10 x 10.
export const TOOLBAR_PICKER_MAX_ROWS = TABLE_CREATION_MAX_ROWS;
export const TOOLBAR_PICKER_MAX_COLUMNS = TABLE_CREATION_MAX_COLUMNS;
export const TOOLBAR_MAX_ROWS = TABLE_CREATION_MAX_ROWS;
export const TOOLBAR_MAX_COLUMNS = TABLE_CREATION_MAX_COLUMNS;
export const MIN_COLUMN_WIDTH_PX = 38;
export const MIN_TABLE_WIDTH_PX = 160;
export const MIN_ROW_HEIGHT_PX = 20;
export const TABLE_DRAG_AUTOSCROLL_EDGE_PX = 72;
export const TABLE_DRAG_AUTOSCROLL_MAX_STEP_PX = 32;

export const DOC_WRITER_TABLE_CAPABILITIES = Object.freeze({
  version: DOC_WRITER_TABLE_VERSION,
  wholeTableMove: true,
  insertRows: true,
  insertColumns: true,
  deleteRows: true,
  deleteColumns: true,
  splitCells: true,
  mergeCells: true,
  resizeRows: true,
  resizeColumns: true,
  resizeTable: true,
  contextMenuModel: true,
  hoverAffordanceModel: true,
  finalCellTabAddsRow: true,
  manualTableCreation: true,
  manualInsertionValidation: true,
  insertionFallback: true,
  singleCommitInsertion: true,
  tenByTenLimit: false,
  arbitraryManualDimensions: true,
  manualGrowthBeyondCreationSize: true,
  explicitTableDeleteOnly: true,
  safeCellBoundaryDelete: true,
  transactionMetadata: true,
  crossPageMoveModel: true,
  dragAutoScrollModel: true,
  professionalEditDialog: true,
  editableLivePreview: true,
  draftOnlyDialogEdits: true,
  previewColumnResize: true,
  previewRowResize: true,
  liveCanvasColumnResize: true,
  liveCanvasRowResize: true,
  stablePreviewHorizontalScroll: true,
  centeredDefaultPlacement: true,
  independentGridLines: true,
  atomicDraftCommit: true,
  canvasUndoRedoTransactions: true,
  draftUndoRedo: true,
  dragRangeSelection: true,
  editableTableTitle: true,
  editableBottomNote: true,
  perCellBorderSides: true,
  doubleAccountingLine: true,
  numericFormats: true,
  syncedBottomScrollbar: true,
  centeredCornerGlyphs: true,
  liveTableHoverHint: true,
  styleTargets: true,
  functionalStyleTargets: true,
  functionalCellBorderOverrides: true,
  multiSupplementalLines: true,
  supplementalLineFormatting: true,
  directionalHeightResize: true,
});

const TABLE_SOURCES = new Set([
  "ai",
  "markdown",
  "html",
  "paste",
  "reopened",
  "toolbar",
  "unknown",
]);

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}

function asFiniteNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function normalizeSource(source) {
  const value = String(source || "unknown").trim().toLowerCase();
  return TABLE_SOURCES.has(value) ? value : "unknown";
}

function elementFromNode(node) {
  if (!node) return null;
  return node.nodeType === 1 ? node : node.parentElement || null;
}

function ownerDocumentFrom(value, fallback = null) {
  if (value?.ownerDocument) return value.ownerDocument;
  if (fallback?.ownerDocument) return fallback.ownerDocument;
  if (typeof document !== "undefined") return document;
  return null;
}

export function createDocumentObjectId(prefix = "object") {
  try {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return `${prefix}-${crypto.randomUUID()}`;
    }
  } catch (_) {
    // Fall through to the deterministic-enough browser-safe fallback.
  }

  return `${prefix}-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

export function escapeCssValue(value) {
  const raw = String(value ?? "");
  try {
    if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
      return CSS.escape(raw);
    }
  } catch (_) {
    // Use the conservative fallback below.
  }
  return raw.replace(/["\\]/g, "\\$&");
}

function cleanCell(cell) {
  return String(cell ?? "")
    .trim()
    .replace(/^:+/, "")
    .replace(/:+$/, "")
    .replace(/\*\*/g, "")
    .replace(/__/g, "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/\\\|/g, "\u0000")
    .replace(/\s+/g, " ")
    .replace(/\u0000/g, "|")
    .trim();
}

function splitPipeRow(line) {
  let row = String(line || "").trim();
  if (row.startsWith("|")) row = row.slice(1);
  if (row.endsWith("|")) row = row.slice(0, -1);

  const cells = [];
  let current = "";
  let escaped = false;

  for (const character of row) {
    if (escaped) {
      current += character === "|" ? "\u0000" : `\\${character}`;
      escaped = false;
      continue;
    }

    if (character === "\\") {
      escaped = true;
      continue;
    }

    if (character === "|") {
      cells.push(cleanCell(current));
      current = "";
      continue;
    }

    current += character;
  }

  if (escaped) current += "\\";
  cells.push(cleanCell(current));
  return cells.map((cell) => cell.replace(/\u0000/g, "|"));
}

function isSeparatorRow(line) {
  return /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(
    String(line || "")
  );
}

function parseSeparatorAlignments(line) {
  return splitPipeRow(line).map((cell) => {
    const raw = String(cell || "").trim();
    const left = raw.startsWith(":");
    const right = raw.endsWith(":");
    if (left && right) return "center";
    if (right) return "right";
    if (left) return "left";
    return "left";
  });
}

function looksLikePipeRow(line) {
  const value = String(line || "").trim();
  if (!value || !value.includes("|")) return false;
  const normalized = value.replace(/^\|/, "").replace(/\|$/, "");
  return splitPipeRow(normalized).length >= 2;
}

function normalizeRows(headers, rows) {
  const headerCount = headers.length;
  return rows.map((row) => {
    const normalized = [...row];
    while (normalized.length < headerCount) normalized.push("");

    if (normalized.length > headerCount) {
      const keep = normalized.slice(0, Math.max(0, headerCount - 1));
      const mergedLast = normalized
        .slice(Math.max(0, headerCount - 1))
        .join(" | ");
      return headerCount === 1 ? [mergedLast] : [...keep, mergedLast];
    }

    return normalized;
  });
}

function inferSimpleTableWithoutSeparator(lines) {
  const pipeRows = lines.filter(looksLikePipeRow);
  if (pipeRows.length < 2) return null;

  const splitRows = pipeRows.map(splitPipeRow);
  const headers = splitRows[0] || [];
  if (headers.length < 2) return null;

  const usableRows = splitRows.slice(1).filter((row) => row.some(Boolean));
  if (!usableRows.length) return null;
  if (!usableRows.every((row) => row.length >= 2)) return null;

  return {
    headers,
    rows: normalizeRows(headers, usableRows),
    alignments: headers.map(() => "left"),
    hasSeparator: false,
  };
}

function stripStructuredTableMarker(value) {
  let raw = String(value || "").trim();

  // Canonical worker/export form:
  //   [table]
  //   | A | B |
  //   |---|---|
  //
  // Also accept a fenced `table` block without changing the enclosed source.
  raw = raw.replace(/^\s*\[table\]\s*(?:\r?\n)?/i, "");

  const fenced = raw.match(
    /^\s*```\s*table\s*(?:\r?\n)([\s\S]*?)(?:\r?\n)```\s*$/i
  );
  if (fenced) raw = String(fenced[1] || "").trim();

  return raw.trim();
}

function normalizeStructuredTableKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function splitStructuredTableLooseList(value) {
  return String(value || "")
    .split(/[,|]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function splitStructuredTablePipeList(value) {
  return String(value || "")
    .split("|")
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseStructuredTableInstruction(value) {
  const source = stripStructuredTableMarker(value);
  if (!source) return null;

  // JSON form:
  // {"headers":["Month","Revenue"],"rows":[["Jan","120000"]]}
  if (/^[{[]/.test(source)) {
    try {
      const parsed = JSON.parse(source);
      if (
        parsed &&
        typeof parsed === "object" &&
        !Array.isArray(parsed) &&
        Array.isArray(parsed.headers) &&
        Array.isArray(parsed.rows)
      ) {
        const headers = parsed.headers.map((item) => String(item ?? "").trim());
        const rows = parsed.rows
          .filter((row) => Array.isArray(row))
          .map((row) => row.map((item) => String(item ?? "").trim()));
        if (headers.length >= 2 && rows.length >= 1) {
          return {
            headers,
            rows: normalizeRows(headers, rows),
            alignments: Array.isArray(parsed.alignments)
              ? parsed.alignments.map((item) => String(item || "left").toLowerCase())
              : headers.map(() => "left"),
            kind: "json",
          };
        }
      }
    } catch (_) {}
  }

  // Match the backend/export renderer's forgiving key=value / key:value format.
  // Examples:
  // headers=Month,Revenue,Expense
  // rows=January,120000,82000|February,125000,84000
  const fields = {};
  String(source)
    .split(/[;\n]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .forEach((part) => {
      const match = part.match(
        /^([a-zA-Z_][a-zA-Z0-9_\-\s]*)\s*[:=]\s*(.+)$/s
      );
      if (!match) return;
      fields[normalizeStructuredTableKey(match[1])] = String(match[2] || "").trim();
    });

  const headers = splitStructuredTableLooseList(
    fields.headers || fields.columns || fields.cols || ""
  );
  const rowsRaw = String(
    fields.rows || fields.data || fields.values || ""
  ).trim();

  if (!headers.length || !rowsRaw) return null;

  const rows = splitStructuredTablePipeList(rowsRaw)
    .map((row) => splitStructuredTableLooseList(row))
    .filter((row) => row.length);

  if (headers.length < 2 || rows.length < 1) return null;

  return {
    headers,
    rows: normalizeRows(headers, rows),
    alignments: headers.map(() => "left"),
    kind: "instruction",
  };
}

function createTableFromStructuredInstruction(
  value,
  { source = "ai", documentValue = null, width = "100%" } = {}
) {
  const parsed = parseStructuredTableInstruction(value);
  if (!parsed) return null;

  return createCanvasTable(
    {
      source,
      headerRow: true,
      headers: parsed.headers,
      data: parsed.rows,
      alignments: parsed.alignments,
      width,
    },
    documentValue
  );
}


export function parseMarkdownTable(value) {
  const lines = stripStructuredTableMarker(value)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) {
    return {
      headers: [],
      rows: [],
      alignments: [],
      hasSeparator: false,
      valid: false,
    };
  }

  const separatorIndex = lines.findIndex(isSeparatorRow);
  if (separatorIndex <= 0) {
    const inferred = inferSimpleTableWithoutSeparator(lines);
    return inferred
      ? { ...inferred, valid: true }
      : {
          headers: [],
          rows: [],
          alignments: [],
          hasSeparator: false,
          valid: false,
        };
  }

  const headers = splitPipeRow(lines[separatorIndex - 1]);
  const alignments = parseSeparatorAlignments(lines[separatorIndex]);
  const rowLines = lines
    .slice(separatorIndex + 1)
    .filter((line) => looksLikePipeRow(line) && !isSeparatorRow(line));
  const rawRows = rowLines
    .map(splitPipeRow)
    .filter((row) => row.some((cell) => cell));
  const rows = normalizeRows(headers, rawRows);

  return {
    headers,
    rows,
    alignments: normalizeRows(headers, [alignments])[0] || [],
    hasSeparator: true,
    valid: headers.length >= 2 && rows.length >= 1,
  };
}

export function looksLikeMarkdownTable(value) {
  return parseMarkdownTable(value).valid;
}

export function isNumericLike(value) {
  const text = String(value || "").trim();
  if (!text) return false;
  return /^[$€£₹]?\s*-?\d{1,3}(?:,\d{3})*(?:\.\d+)?%?$|^-?\d+(?:\.\d+)?%?$/.test(
    text
  );
}

export function isDateLike(value) {
  const text = String(value || "").trim();
  if (!text) return false;
  return (
    /^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(text) ||
    /^\d{4}-\d{1,2}-\d{1,2}$/.test(text) ||
    /^(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)\b/i.test(text)
  );
}

export function isBooleanLike(value) {
  const text = String(value || "")
    .trim()
    .toLowerCase();
  return [
    "yes",
    "no",
    "true",
    "false",
    "present",
    "absent",
    "unknown",
    "n/a",
  ].includes(text);
}

export function columnStats(headers, rows) {
  return headers.map((header, columnIndex) => {
    const values = rows
      .map((row) => String(row[columnIndex] || "").trim())
      .filter(Boolean);
    const averageLength = values.length
      ? values.reduce((total, item) => total + item.length, 0) / values.length
      : 0;
    const maximumLength = values.reduce(
      (maximum, item) => Math.max(maximum, item.length),
      0
    );
    const numericRatio = values.length
      ? values.filter(isNumericLike).length / values.length
      : 0;
    const dateRatio = values.length
      ? values.filter(isDateLike).length / values.length
      : 0;
    const booleanRatio = values.length
      ? values.filter(isBooleanLike).length / values.length
      : 0;
    const headerLower = String(header || "").toLowerCase();

    let type = "medium";
    if (numericRatio >= 0.75) {
      type = "numeric";
    } else if (
      dateRatio >= 0.6 ||
      /(date|deadline|effective|expires|period)/i.test(headerLower)
    ) {
      type = "date";
    } else if (
      booleanRatio >= 0.75 ||
      /(status|present|missing|found|yes\/no|risk)/i.test(headerLower)
    ) {
      type = "status";
    } else if (
      maximumLength >= 120 ||
      averageLength >= 70 ||
      /(summary|description|explanation|finding|analysis|reason|notes?|details?|comment|issue|risk|recommendation)/i.test(
        headerLower
      )
    ) {
      type = "long";
    } else if (maximumLength <= 18 && averageLength <= 14) {
      type = "short";
    }

    return {
      index: columnIndex,
      header,
      type,
      averageLength,
      maximumLength,
      numericRatio,
      dateRatio,
      booleanRatio,
    };
  });
}

function inferColumnPercentages(headers, rows) {
  const stats = columnStats(headers, rows);
  if (!stats.length) return [];

  const weights = stats.map((column) => {
    const headerLength = Math.min(40, String(column.header || "").length);
    const contentFactor = Math.min(3, Math.max(0, column.averageLength / 35));
    const typeWeight = {
      numeric: 0.8,
      date: 1.05,
      status: 0.85,
      short: 0.8,
      medium: 1.25,
      long: 2.4,
    }[column.type];
    return Math.max(0.6, typeWeight + contentFactor + headerLength / 80);
  });

  const total = weights.reduce((sum, weight) => sum + weight, 0) || 1;
  const raw = weights.map((weight) => (weight / total) * 100);
  const minimum = stats.length <= 8 ? 7 : Math.max(2, 100 / stats.length / 2);
  const adjusted = raw.map((percentage) => Math.max(minimum, percentage));
  const adjustedTotal = adjusted.reduce((sum, percentage) => sum + percentage, 0);
  return adjusted.map((percentage) => (percentage / adjustedTotal) * 100);
}

function tableDataMatrix(table) {
  if (!table) return { headers: [], rows: [] };
  const headerRow = table.querySelector(":scope > thead > tr");
  const headers = headerRow
    ? Array.from(headerRow.cells || []).map((cell) => cell.textContent || "")
    : Array.from(table.rows?.[0]?.cells || []).map((cell) => cell.textContent || "");
  const bodyRows = Array.from(
    table.querySelectorAll(":scope > tbody > tr, :scope > tr")
  );
  const rows = bodyRows.map((row) =>
    Array.from(row.cells || []).map((cell) => cell.textContent || "")
  );
  return { headers, rows };
}

export function getTableColumnCount(table) {
  if (!table) return 0;
  return Array.from(table.rows || []).reduce((maximum, row) => {
    const count = Array.from(row.cells || []).reduce(
      (total, cell) => total + Math.max(1, Number(cell.colSpan || 1)),
      0
    );
    return Math.max(maximum, count);
  }, 0);
}

function findDirectChild(table, tagName) {
  return Array.from(table?.children || []).find(
    (child) => child.tagName === tagName
  );
}

function ensureTableSections(table) {
  if (!table) return;

  const directRows = Array.from(table.children || []).filter(
    (child) => child.tagName === "TR"
  );
  if (directRows.length) {
    let body = findDirectChild(table, "TBODY");
    if (!body) {
      body = table.ownerDocument.createElement("tbody");
      table.appendChild(body);
    }
    directRows.forEach((row) => body.appendChild(row));
  }

  if (!table.tBodies?.length && !findDirectChild(table, "THEAD")) {
    table.appendChild(table.ownerDocument.createElement("tbody"));
  }
}

function rowLogicalCellCount(row) {
  return Array.from(row?.cells || []).reduce(
    (total, cell) => total + Math.max(1, Number(cell.colSpan || 1)),
    0
  );
}

function normalizeSimpleRowLengths(table, columnCount) {
  if (!table || columnCount < 1) return;
  const hasSpans = Array.from(table.querySelectorAll("th, td")).some(
    (cell) => Number(cell.colSpan || 1) > 1 || Number(cell.rowSpan || 1) > 1
  );
  if (hasSpans) return;

  Array.from(table.rows || []).forEach((row) => {
    while (row.cells.length < columnCount) {
      const tag = row.parentElement?.tagName === "THEAD" ? "th" : "td";
      row.appendChild(table.ownerDocument.createElement(tag));
    }
    while (row.cells.length > columnCount) {
      const last = row.cells[row.cells.length - 1];
      const previous = row.cells[row.cells.length - 2];
      if (!previous || !last) break;
      const separator = previous.textContent?.trim() ? " | " : "";
      previous.appendChild(table.ownerDocument.createTextNode(separator));
      while (last.firstChild) previous.appendChild(last.firstChild);
      last.remove();
    }
  });
}

export function ensureTableColgroup(
  table,
  columnCount = getTableColumnCount(table),
  { inferWidths = true } = {}
) {
  if (!table || columnCount < 1) return null;

  let colgroup = findDirectChild(table, "COLGROUP");
  if (!colgroup) {
    colgroup = table.ownerDocument.createElement("colgroup");
    const insertionPoint = Array.from(table.children || []).find((child) =>
      ["THEAD", "TBODY", "TFOOT", "TR"].includes(child.tagName)
    );
    table.insertBefore(colgroup, insertionPoint || table.firstChild);
  }

  const columns = Array.from(colgroup.children || []).filter(
    (child) => child.tagName === "COL"
  );
  while (columns.length < columnCount) {
    const column = table.ownerDocument.createElement("col");
    colgroup.appendChild(column);
    columns.push(column);
  }
  while (columns.length > columnCount) columns.pop()?.remove();

  columns.forEach((column) => {
    if (!column.getAttribute(DOC_COLUMN_ID_ATTR)) {
      column.setAttribute(DOC_COLUMN_ID_ATTR, createDocumentObjectId("column"));
    }
  });

  const hasAnyWidth = columns.some((column) =>
    String(column.style.width || column.getAttribute("width") || "").trim()
  );

  if (!hasAnyWidth) {
    const matrix = tableDataMatrix(table);
    const percentages = inferWidths
      ? inferColumnPercentages(matrix.headers, matrix.rows)
      : [];
    columns.forEach((column, index) => {
      column.style.width = `${
        percentages[index] ?? 100 / Math.max(1, columnCount)
      }%`;
    });
  }

  return colgroup;
}

function normalizeCell(cell) {
  if (!cell) return;
  if (!cell.getAttribute(DOC_CELL_ID_ATTR)) {
    cell.setAttribute(DOC_CELL_ID_ATTR, createDocumentObjectId("cell"));
  }
  cell.setAttribute(DOC_TABLE_CELL_INTERACTIVE_ATTR, "true");

  cell.style.maxHeight = "none";
  cell.style.overflow = "visible";
  cell.style.textOverflow = "clip";
  cell.style.whiteSpace = "normal";
  cell.style.overflowWrap = "anywhere";
  cell.style.wordBreak = "normal";
  if (!cell.style.verticalAlign) cell.style.verticalAlign = "top";
  cell.removeAttribute("nowrap");

  if (!cell.childNodes.length) {
    cell.appendChild(cell.ownerDocument.createElement("br"));
  }
}

function normalizeRow(row) {
  if (!row) return;
  if (!row.getAttribute(DOC_ROW_ID_ATTR)) {
    row.setAttribute(DOC_ROW_ID_ATTR, createDocumentObjectId("row"));
  }
  row.style.maxHeight = "none";
  row.style.overflow = "visible";
  Array.from(row.cells || []).forEach(normalizeCell);
}

function tableColumnCountForSupplementalRows(table) {
  return Math.max(1, getTableColumnCount(table) || 1);
}

function tableTitleElement(table) {
  if (!table) return null;
  return (
    findDirectChild(table, "CAPTION") ||
    table.querySelector?.(`:scope > caption[${DOC_TABLE_TITLE_ATTR}="true"]`) ||
    null
  );
}

function supplementalId(element, prefix = "supplemental") {
  if (!element) return "";
  let id = String(element.getAttribute(DOC_TABLE_SUPPLEMENTAL_ID_ATTR) || "").trim();
  if (!id) {
    id = createDocumentObjectId(prefix);
    element.setAttribute(DOC_TABLE_SUPPLEMENTAL_ID_ATTR, id);
  }
  return id;
}

function normalizeSupplementalLine(line, kind = "title") {
  if (!line) return null;
  const normalizedKind = kind === "note" ? "note" : "title";
  line.setAttribute(
    normalizedKind === "title" ? DOC_TABLE_TITLE_LINE_ATTR : DOC_TABLE_NOTE_LINE_ATTR,
    "true"
  );
  supplementalId(line, normalizedKind === "title" ? "titleline" : "noteline");
  line.style.whiteSpace = "normal";
  line.style.overflowWrap = "anywhere";
  line.style.wordBreak = "normal";
  line.style.lineHeight = line.style.lineHeight || "1.35";
  line.style.textAlign = line.style.textAlign || (normalizedKind === "title" ? "center" : "left");
  line.style.fontWeight = line.style.fontWeight || (normalizedKind === "title" ? "700" : "400");
  line.style.fontStyle = line.style.fontStyle || "normal";
  line.style.textDecoration = line.style.textDecoration || "none";
  if (normalizedKind === "title") {
    line.style.display = "block";
    line.style.minHeight = line.style.minHeight || "18px";
    line.style.padding = line.style.padding || "1px 0";
  } else {
    line.style.border = "0";
    line.style.background = line.style.background || "transparent";
    line.style.minHeight = line.style.minHeight || "20px";
    line.style.padding = line.style.padding || "4px 0";
  }
  if (!line.childNodes.length) line.appendChild(line.ownerDocument.createElement("br"));
  return line;
}

function ensureCaptionTitleLines(caption) {
  if (!caption) return [];
  let lines = Array.from(
    caption.querySelectorAll(`:scope > [${DOC_TABLE_TITLE_LINE_ATTR}="true"]`)
  );
  if (!lines.length) {
    const line = caption.ownerDocument.createElement("div");
    line.setAttribute(DOC_TABLE_TITLE_LINE_ATTR, "true");
    while (caption.firstChild) line.appendChild(caption.firstChild);
    if (!line.childNodes.length) line.appendChild(caption.ownerDocument.createElement("br"));
    caption.appendChild(line);
    lines = [line];
  }
  lines.forEach((line) => normalizeSupplementalLine(line, "title"));
  return lines;
}

function tableTitleLines(table) {
  const caption = tableTitleElement(table);
  return caption ? ensureCaptionTitleLines(caption) : [];
}

function tableNoteCells(table) {
  if (!table) return [];
  return Array.from(
    table.querySelectorAll(
      `:scope > tfoot[${DOC_TABLE_NOTE_ATTR}="true"] > tr[${DOC_TABLE_NOTE_ROW_ATTR}="true"] > td`
    )
  );
}

function tableNoteCell(table) {
  return tableNoteCells(table)[0] || null;
}

function normalizeTableSupplementalPresentation(table) {
  if (!table) return false;
  const caption = tableTitleElement(table);
  if (caption) {
    caption.setAttribute(DOC_TABLE_TITLE_ATTR, "true");
    caption.style.captionSide = "top";
    caption.style.padding = caption.style.padding || "0 0 8px";
    caption.style.whiteSpace = "normal";
    caption.style.overflowWrap = "anywhere";
    ensureCaptionTitleLines(caption);
  }

  const notes = tableNoteCells(table);
  notes.forEach((note) => {
    const row = note.closest("tr");
    const foot = note.closest("tfoot");
    foot?.setAttribute(DOC_TABLE_NOTE_ATTR, "true");
    row?.setAttribute(DOC_TABLE_NOTE_ROW_ATTR, "true");
    note.setAttribute(DOC_TABLE_NOTE_LINE_ATTR, "true");
    supplementalId(note, "noteline");
    note.colSpan = tableColumnCountForSupplementalRows(table);
    note.setAttribute("colspan", String(note.colSpan));
    normalizeSupplementalLine(note, "note");
  });
  return Boolean(caption || notes.length);
}

function promoteSpanningTitleRowToCaption(table) {
  if (!table || tableTitleElement(table)) return tableTitleElement(table);
  const rows = Array.from(table.rows || []);
  if (rows.length < 2) return null;
  const firstRow = rows[0];
  const cells = Array.from(firstRow.cells || []);
  if (cells.length !== 1) return null;

  const columnCount = tableColumnCountForSupplementalRows(table);
  const cell = cells[0];
  const span = Math.max(1, Number(cell.colSpan || 1));
  const text = String(cell.textContent || "").trim();
  if (!text || span < columnCount) return null;

  const caption = table.ownerDocument.createElement("caption");
  caption.setAttribute(DOC_TABLE_TITLE_ATTR, "true");
  const line = table.ownerDocument.createElement("div");
  line.setAttribute(DOC_TABLE_TITLE_LINE_ATTR, "true");
  line.innerHTML = cell.innerHTML;
  if (cell.getAttribute("style")) line.setAttribute("style", cell.getAttribute("style"));
  normalizeSupplementalLine(line, "title");
  caption.appendChild(line);
  table.insertBefore(caption, table.firstChild);

  const section = firstRow.parentElement;
  firstRow.remove();
  if (section && ["THEAD", "TBODY", "TFOOT"].includes(section.tagName) && !section.rows.length) {
    section.remove();
  }
  normalizeTableSupplementalPresentation(table);
  return caption;
}

function ensureTableTitle(table, text = "") {
  if (!table) return null;
  let caption = tableTitleElement(table);
  if (!caption) {
    caption = table.ownerDocument.createElement("caption");
    caption.setAttribute(DOC_TABLE_TITLE_ATTR, "true");
    table.insertBefore(caption, table.firstChild);
  }
  let lines = ensureCaptionTitleLines(caption);
  if (text && lines.length === 1 && !String(lines[0].textContent || "").trim()) {
    lines[0].textContent = String(text);
  }
  normalizeTableSupplementalPresentation(table);
  return caption;
}

function appendTableTitleLine(table, text = "") {
  if (!table) return null;
  let caption = tableTitleElement(table);
  if (!caption) {
    caption = table.ownerDocument.createElement("caption");
    caption.setAttribute(DOC_TABLE_TITLE_ATTR, "true");
    caption.style.captionSide = "top";
    caption.style.padding = "0 0 8px";
    table.insertBefore(caption, table.firstChild);
  } else {
    // Normalize legacy caption text before adding a NEW independent line.
    ensureCaptionTitleLines(caption);
  }

  const line = table.ownerDocument.createElement("div");
  if (text) line.appendChild(table.ownerDocument.createTextNode(String(text)));
  else line.appendChild(table.ownerDocument.createElement("br"));
  caption.appendChild(line);
  normalizeSupplementalLine(line, "title");
  normalizeTableSupplementalPresentation(table);
  return line;
}

function removeTableTitleLine(table, lineId = "") {
  const caption = tableTitleElement(table);
  if (!caption) return false;
  const lines = tableTitleLines(table);
  const target = lineId
    ? lines.find((line) => line.getAttribute(DOC_TABLE_SUPPLEMENTAL_ID_ATTR) === lineId)
    : lines[lines.length - 1];
  if (!target) return false;
  target.remove();
  const remaining = Array.from(
    caption.querySelectorAll(`:scope > [${DOC_TABLE_TITLE_LINE_ATTR}="true"]`)
  );
  if (!remaining.length) caption.remove();
  return true;
}

function removeTableTitle(table) {
  const caption = tableTitleElement(table);
  if (!caption) return false;
  caption.remove();
  return true;
}

function ensureTableNoteFoot(table) {
  if (!table) return null;
  let foot = findDirectChild(table, "TFOOT");
  if (!foot) {
    foot = table.ownerDocument.createElement("tfoot");
    table.appendChild(foot);
  }
  foot.setAttribute(DOC_TABLE_NOTE_ATTR, "true");
  return foot;
}

function appendTableNoteLine(table, text = "") {
  if (!table) return null;
  const foot = ensureTableNoteFoot(table);
  if (!foot) return null;
  const row = table.ownerDocument.createElement("tr");
  row.setAttribute(DOC_TABLE_NOTE_ROW_ATTR, "true");
  const note = table.ownerDocument.createElement("td");
  note.setAttribute(DOC_CELL_ID_ATTR, createDocumentObjectId("cell"));
  note.setAttribute(DOC_TABLE_CELL_INTERACTIVE_ATTR, "true");
  note.setAttribute(DOC_TABLE_NOTE_LINE_ATTR, "true");
  supplementalId(note, "noteline");
  note.colSpan = tableColumnCountForSupplementalRows(table);
  note.setAttribute("colspan", String(note.colSpan));
  if (text) note.appendChild(table.ownerDocument.createTextNode(String(text)));
  else note.appendChild(table.ownerDocument.createElement("br"));
  row.appendChild(note);
  foot.appendChild(row);
  normalizeRow(row);
  normalizeSupplementalLine(note, "note");
  normalizeTableSupplementalPresentation(table);
  return note;
}

function ensureTableNote(table, text = "") {
  if (!table) return null;
  const existing = tableNoteCell(table);
  if (existing) {
    normalizeTableSupplementalPresentation(table);
    return existing;
  }
  return appendTableNoteLine(table, text);
}

function removeTableNoteLine(table, lineId = "") {
  if (!table) return false;
  const notes = tableNoteCells(table);
  const target = lineId
    ? notes.find((note) => note.getAttribute(DOC_TABLE_SUPPLEMENTAL_ID_ATTR) === lineId)
    : notes[notes.length - 1];
  if (!target) return false;
  const row = target.closest("tr");
  const foot = target.closest("tfoot");
  row?.remove();
  if (foot && !foot.rows.length) foot.remove();
  return true;
}

function removeTableNote(table) {
  if (!table) return false;
  const foot = findDirectChild(table, "TFOOT");
  if (!foot || foot.getAttribute(DOC_TABLE_NOTE_ATTR) !== "true") return false;
  foot.remove();
  return true;
}

const CELL_BORDER_ATTRIBUTE_BY_SIDE = Object.freeze({
  top: DOC_CELL_BORDER_TOP_ATTR,
  right: DOC_CELL_BORDER_RIGHT_ATTR,
  bottom: DOC_CELL_BORDER_BOTTOM_ATTR,
  left: DOC_CELL_BORDER_LEFT_ATTR,
});

function cellBorderAttribute(side) {
  return CELL_BORDER_ATTRIBUTE_BY_SIDE[String(side || "").toLowerCase()] || "";
}

function tableHasCellBorderOverrides(table) {
  if (!table?.querySelector) return false;
  return Boolean(
    table.querySelector(
      [
        `[${DOC_CELL_BORDER_TOP_ATTR}]`,
        `[${DOC_CELL_BORDER_RIGHT_ATTR}]`,
        `[${DOC_CELL_BORDER_BOTTOM_ATTR}]`,
        `[${DOC_CELL_BORDER_LEFT_ATTR}]`,
      ].join(",")
    )
  );
}

export function applyCellBorderOverrides(table) {
  if (!table?.querySelectorAll) return false;
  let changed = false;
  table.querySelectorAll("th,td").forEach((cell) => {
    Object.entries(CELL_BORDER_ATTRIBUTE_BY_SIDE).forEach(([side, attributeName]) => {
      if (!cell.hasAttribute(attributeName)) return;
      const token = String(cell.getAttribute(attributeName) || "").trim();
      const cssValue = !token || token === "none" ? "0px none transparent" : token;
      // Inline !important is deliberate: per-cell/range overrides are the most
      // specific table presentation choice and must beat generic Canvas/table-grid CSS.
      cell.style.setProperty(`border-${side}`, cssValue, "important");
      changed = true;
    });
  });
  return changed;
}

function parseNumericCellValue(cell) {
  if (!cell) return null;
  const stored = String(cell.getAttribute(DOC_CELL_NUMBER_VALUE_ATTR) || "").trim();
  if (stored && Number.isFinite(Number(stored))) return Number(stored);

  let text = String(cell.textContent || "").trim();
  if (!text) return null;
  let negative = false;
  if (/^\(.*\)$/.test(text)) {
    negative = true;
    text = text.slice(1, -1);
  }
  text = text
    .replace(/[$€£₹,\s]/g, "")
    .replace(/[^0-9.+-]/g, "");
  if (!text || !Number.isFinite(Number(text))) return null;
  const value = Number(text);
  return negative ? -Math.abs(value) : value;
}

function plainNumberString(value) {
  if (!Number.isFinite(value)) return "";
  if (Object.is(value, -0)) return "0";
  return Number.isInteger(value) ? String(value) : String(Number(value.toPrecision(15)));
}

function formatNumericCell(cell, format = "general") {
  if (!cell) return false;
  const normalized = ["general", "number", "accounting"].includes(format)
    ? format
    : "general";
  const value = parseNumericCellValue(cell);
  if (!Number.isFinite(value)) return false;

  cell.setAttribute(DOC_CELL_NUMBER_VALUE_ATTR, String(value));
  cell.setAttribute(DOC_CELL_NUMBER_FORMAT_ATTR, normalized);

  let output = plainNumberString(value);
  if (normalized === "number") {
    output = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } else if (normalized === "accounting") {
    output = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      currencySign: "accounting",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  cell.textContent = output;
  return true;
}

function normalizedTableAlignment(value, fallback = "center") {
  const normalized = String(value || "").trim().toLowerCase();
  return ["left", "center", "right"].includes(normalized) ? normalized : fallback;
}

export function applyTableAlignmentPresentation(table, alignment = null) {
  if (!table) return false;
  const normalized = normalizedTableAlignment(
    alignment || table.getAttribute(DOC_TABLE_ALIGNMENT_ATTR),
    "center"
  );

  table.setAttribute(DOC_TABLE_ALIGNMENT_ATTR, normalized);
  table.style.display = "table";

  // Canvas owns vertical document flow, but the TABLE owns its horizontal
  // placement. Use important longhands so the Canvas's generic
  // `margin: ... !important` page CSS cannot silently force every table left.
  if (normalized === "center") {
    table.style.setProperty("margin-left", "auto", "important");
    table.style.setProperty("margin-right", "auto", "important");
  } else if (normalized === "right") {
    table.style.setProperty("margin-left", "auto", "important");
    table.style.setProperty("margin-right", "0", "important");
  } else {
    table.style.setProperty("margin-left", "0", "important");
    table.style.setProperty("margin-right", "auto", "important");
  }

  return true;
}

function booleanTableSetting(table, attributeName, fallback = true) {
  if (!table) return Boolean(fallback);
  const raw = String(table.getAttribute(attributeName) || "").trim().toLowerCase();
  if (raw === "true") return true;
  if (raw === "false") return false;
  return Boolean(fallback);
}

function localGridOverride(element, attributeName) {
  if (!element) return "inherit";
  const value = String(element.getAttribute?.(attributeName) || "")
    .trim()
    .toLowerCase();
  return value === "show" || value === "hide" ? value : "inherit";
}

function gridBoundaryVisible(globalVisible, firstOverride = "inherit", secondOverride = "inherit") {
  // A local Hide must be authoritative so a selected row/column can truly clear
  // the surrounding table grid and allow per-cell border overrides to be seen.
  // Show can turn a local boundary back on when the corresponding global grid is off.
  if (firstOverride === "hide" || secondOverride === "hide") return false;
  if (firstOverride === "show" || secondOverride === "show") return true;
  return Boolean(globalVisible);
}

function tableBorderPresentation(table) {
  const sample = table?.querySelector?.("th,td") || null;
  const sampleStyle = sample?.style || null;
  const width = Math.max(
    0,
    asFiniteNumber(
      table?.getAttribute?.(DOC_TABLE_BORDER_WIDTH_ATTR) ||
        String(sampleStyle?.borderTopWidth || sampleStyle?.borderWidth || "").replace(/px$/i, ""),
      1
    )
  );
  const style =
    String(
      table?.getAttribute?.(DOC_TABLE_BORDER_STYLE_ATTR) ||
        sampleStyle?.borderTopStyle ||
        sampleStyle?.borderStyle ||
        "solid"
    ).trim() || "solid";
  const color =
    String(
      table?.getAttribute?.(DOC_TABLE_BORDER_COLOR_ATTR) ||
        sampleStyle?.borderTopColor ||
        sampleStyle?.borderColor ||
        "#cbd5e1"
    ).trim() || "#cbd5e1";

  return {
    width,
    style,
    color,
    token: width <= 0 || style === "none" ? "0px none transparent" : `${width}px ${style} ${color}`,
  };
}

function tableHasExplicitGridPresentation(table) {
  if (!table) return false;
  return [
    DOC_TABLE_OUTER_BORDER_ATTR,
    DOC_TABLE_ROW_LINES_ATTR,
    DOC_TABLE_COLUMN_LINES_ATTR,
    DOC_TABLE_BORDER_WIDTH_ATTR,
    DOC_TABLE_BORDER_STYLE_ATTR,
    DOC_TABLE_BORDER_COLOR_ATTR,
  ].some((attributeName) => table.hasAttribute(attributeName));
}

export function applyTableGridPresentation(table) {
  if (!table) return false;

  const { token } = tableBorderPresentation(table);
  const showOuter = booleanTableSetting(table, DOC_TABLE_OUTER_BORDER_ATTR, true);
  const showRows = booleanTableSetting(table, DOC_TABLE_ROW_LINES_ATTR, true);
  const showColumns = booleanTableSetting(table, DOC_TABLE_COLUMN_LINES_ATTR, true);
  const grid = buildTableGrid(table);
  const rowCount = grid.rows.length;
  const columnCount = grid.columnCount;
  const colgroup = ensureTableColgroup(table, columnCount, { inferWidths: false });
  const columns = Array.from(colgroup?.children || []).filter(
    (column) => column.tagName === "COL"
  );

  const rowBoundaryIsVisible = (boundaryIndex) =>
    gridBoundaryVisible(
      showRows,
      localGridOverride(
        grid.rows[boundaryIndex],
        DOC_TABLE_SELECTED_ROW_GRID_ATTR
      ),
      localGridOverride(
        grid.rows[boundaryIndex + 1],
        DOC_TABLE_SELECTED_ROW_GRID_ATTR
      )
    );

  const columnBoundaryIsVisible = (boundaryIndex) =>
    gridBoundaryVisible(
      showColumns,
      localGridOverride(
        columns[boundaryIndex],
        DOC_TABLE_SELECTED_COLUMN_GRID_ATTR
      ),
      localGridOverride(
        columns[boundaryIndex + 1],
        DOC_TABLE_SELECTED_COLUMN_GRID_ATTR
      )
    );

  grid.origins.forEach((origin) => {
    const cell = origin.cell;
    if (!cell) return;

    cell.style.setProperty("border-top", "0px none transparent");
    cell.style.setProperty("border-right", "0px none transparent");
    cell.style.setProperty("border-bottom", "0px none transparent");
    cell.style.setProperty("border-left", "0px none transparent");

    const rowStart = origin.rowIndex;
    const rowEnd = origin.rowIndex + Math.max(1, origin.rowSpan) - 1;
    const columnStart = origin.columnIndex;
    const columnEnd = origin.columnIndex + Math.max(1, origin.colSpan) - 1;

    if (showOuter && rowStart === 0) cell.style.setProperty("border-top", token);
    if (showOuter && rowEnd >= rowCount - 1) cell.style.setProperty("border-bottom", token);
    if (showOuter && columnStart === 0) cell.style.setProperty("border-left", token);
    if (showOuter && columnEnd >= columnCount - 1) cell.style.setProperty("border-right", token);

    if (rowEnd < rowCount - 1 && rowBoundaryIsVisible(rowEnd)) {
      cell.style.setProperty("border-bottom", token);
    }
    if (columnEnd < columnCount - 1 && columnBoundaryIsVisible(columnEnd)) {
      cell.style.setProperty("border-right", token);
    }
  });

  return true;
}

function applyExplicitTableSpacing(table) {
  if (!table) return false;

  if (
    table.hasAttribute(DOC_TABLE_PADDING_X_ATTR) ||
    table.hasAttribute(DOC_TABLE_PADDING_Y_ATTR)
  ) {
    const vertical = Math.max(
      0,
      asFiniteNumber(table.getAttribute(DOC_TABLE_PADDING_Y_ATTR), 7)
    );
    const horizontal = Math.max(
      0,
      asFiniteNumber(table.getAttribute(DOC_TABLE_PADDING_X_ATTR), 9)
    );
    table.querySelectorAll("th,td").forEach((cell) => {
      // Padding becomes table-owned only after the user explicitly chooses it.
      // Important is needed because Canvas has a generic compact-cell rule.
      cell.style.setProperty(
        "padding",
        `${vertical}px ${horizontal}px`,
        "important"
      );
    });
  }

  if (table.hasAttribute(DOC_TABLE_LINE_HEIGHT_ATTR)) {
    const lineHeight = clamp(
      asFiniteNumber(table.getAttribute(DOC_TABLE_LINE_HEIGHT_ATTR), 1.3),
      0.8,
      3
    );
    table.style.lineHeight = String(lineHeight);
    table.querySelectorAll("th,td").forEach((cell) => {
      cell.style.setProperty("line-height", String(lineHeight), "important");
    });
  }

  return true;
}

export function applyDocWriterTablePresentation(table, { preserveExplicit = true } = {}) {
  if (!table) return null;

  table.setAttribute(DOC_WRITER_TABLE_ATTR, "true");
  table.setAttribute(DOC_WRITER_TABLE_VERSION_ATTR, DOC_WRITER_TABLE_VERSION);
  table.removeAttribute(LEGACY_TABLE_VERSION_ATTR);

  // A normalized table is document content, never editor chrome. Recover from
  // stale/worker HTML that accidentally arrived hidden so a table-only document
  // cannot become a visually blank Canvas.
  table.removeAttribute("hidden");
  table.removeAttribute("aria-hidden");
  table.style.display = "table";
  table.style.visibility = "visible";
  table.style.opacity = "1";
  table.style.pointerEvents = "auto";

  table.style.borderCollapse = "collapse";
  table.style.borderSpacing = "0";
  table.style.maxWidth = "100%";
  table.style.boxSizing = "border-box";
  table.style.backgroundColor = table.style.backgroundColor || "#ffffff";
  table.style.color = table.style.color || "#111827";
  table.style.lineHeight = table.style.lineHeight || "1.3";
  table.style.overflow = "visible";

  // Professional report default: center the TABLE object inside the usable page
  // width. Canvas keeps ownership of page flow/top-bottom spacing; DocWriterTable
  // owns left/center/right placement and reapplies it on reopen/normalization.
  const storedAlignment = String(
    table.getAttribute(DOC_TABLE_ALIGNMENT_ATTR) || ""
  ).trim();
  applyTableAlignmentPresentation(table, storedAlignment || "center");

  const bodyRows = Array.from(table.querySelectorAll(":scope > tbody > tr"));
  const headerCells = Array.from(table.querySelectorAll(":scope > thead > tr > th"));

  headerCells.forEach((cell) => {
    if (!preserveExplicit || !cell.style.padding) cell.style.padding = "7px 9px";
    if (!preserveExplicit || !cell.style.border) cell.style.border = "1px solid #cbd5e1";
    if (!preserveExplicit || !cell.style.backgroundColor) {
      cell.style.backgroundColor = "#f1f5f9";
    }
    if (!preserveExplicit || !cell.style.color) cell.style.color = "#0f172a";
    if (!preserveExplicit || !cell.style.fontWeight) cell.style.fontWeight = "700";
    cell.style.verticalAlign = cell.style.verticalAlign || "middle";
  });

  bodyRows.forEach((row) => {
    Array.from(row.cells || []).forEach((cell) => {
      if (!preserveExplicit || !cell.style.padding) cell.style.padding = "7px 9px";
      if (!preserveExplicit || !cell.style.border) cell.style.border = "1px solid #cbd5e1";
      if (!preserveExplicit || !cell.style.backgroundColor) {
        cell.style.backgroundColor = "#ffffff";
      }
      cell.style.verticalAlign = cell.style.verticalAlign || "top";
    });
  });

  const head = findDirectChild(table, "THEAD");
  if (head) head.style.display = "table-header-group";

  normalizeTableSupplementalPresentation(table);

  // Only opt into the whole-table grid compositor when the table carries explicit
  // grid/border metadata. Per-cell border sides are then layered on top so a user
  // can make Excel/accounting-style exceptions without losing the global grid.
  if (tableHasExplicitGridPresentation(table)) {
    applyTableGridPresentation(table);
  }
  if (tableHasCellBorderOverrides(table)) {
    applyCellBorderOverrides(table);
  }
  applyExplicitTableSpacing(table);

  return table;
}

export function normalizeCanvasTable(
  table,
  {
    source = null,
    inferWidths = true,
    headerRow = null,
    preserveTableId = true,
  } = {}
) {
  if (!table || table.tagName !== "TABLE") return null;

  // A real TABLE is document content, never screen-only table chrome. Older
  // snapshots may have accidentally copied this marker onto the table itself;
  // remove it before any Canvas cleanup/serialization pass can misclassify the
  // entire table as UI and delete it.
  table.removeAttribute(DOC_WRITER_TABLE_UI_ATTR);

  const normalizedSource = normalizeSource(
    source || table.getAttribute(DOC_TABLE_SOURCE_ATTR)
  );
  table.setAttribute(DOC_TABLE_ATTR, "true");
  table.setAttribute(DOC_TABLE_SOURCE_ATTR, normalizedSource);
  table.setAttribute(DOC_WRITER_TABLE_ATTR, "true");
  table.setAttribute(DOC_WRITER_TABLE_VERSION_ATTR, DOC_WRITER_TABLE_VERSION);
  table.removeAttribute(LEGACY_TABLE_VERSION_ATTR);
  table.setAttribute(DOC_TABLE_MOVABLE_ATTR, "true");
  table.setAttribute(DOC_TABLE_CONTEXT_MENU_ATTR, "true");
  table.setAttribute(DOC_TABLE_INTERACTION_ATTR, "table-v4");
  table.setAttribute("draggable", "false");

  if (!preserveTableId || !table.getAttribute(DOC_TABLE_ID_ATTR)) {
    table.setAttribute(DOC_TABLE_ID_ATTR, createDocumentObjectId("table"));
  }

  ensureTableSections(table);

  if (headerRow === true && !findDirectChild(table, "THEAD")) {
    const firstBodyRow = table.querySelector(":scope > tbody > tr");
    if (firstBodyRow) {
      const head = table.ownerDocument.createElement("thead");
      Array.from(firstBodyRow.cells || []).forEach((cell) => {
        if (cell.tagName === "TH") return;
        const heading = table.ownerDocument.createElement("th");
        Array.from(cell.attributes || []).forEach((attribute) => {
          heading.setAttribute(attribute.name, attribute.value);
        });
        heading.innerHTML = cell.innerHTML;
        cell.replaceWith(heading);
      });
      head.appendChild(firstBodyRow);
      table.insertBefore(head, findDirectChild(table, "TBODY") || null);
    }
  }

  const columnCount = getTableColumnCount(table);
  normalizeSimpleRowLengths(table, columnCount);
  const colgroup = ensureTableColgroup(table, columnCount, { inferWidths });

  Array.from(table.rows || []).forEach(normalizeRow);
  normalizeTableSupplementalPresentation(table);
  applyDocWriterTablePresentation(table, { preserveExplicit: true });

  table.style.borderCollapse = "collapse";
  table.style.maxWidth = "100%";
  table.style.tableLayout = table.style.tableLayout || "fixed";
  if (!table.style.width) table.style.width = "100%";
  table.style.overflow = "visible";

  Array.from(colgroup?.children || []).forEach((column) => {
    column.removeAttribute("span");
  });

  return table;
}

function tableSourceTextFromNode(node) {
  if (!node) return "";

  // IMPORTANT: backend/PDF and Canvas share the protected-object contract.
  // A structured table may intentionally have no visible text because its
  // canonical specification lives in these attributes.
  const attributeSource = String(
    node.getAttribute?.("data-doc-block-canonical-source") ||
      node.getAttribute?.("data-doc-block-source") ||
      node.getAttribute?.("data-doc-table-source-text") ||
      node.getAttribute?.("data-table-source") ||
      ""
  ).trim();

  if (attributeSource) {
    return attributeSource
      .replace(/\u00a0/g, " ")
      .replace(/\r\n/g, "\n")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n[ \t]+/g, "\n")
      .trim();
  }

  // innerText normally preserves <br> and block boundaries in the live Canvas,
  // but detached/rehydrated AI HTML can report only textContent. Build a small
  // non-destructive clone fallback so table-only Markdown keeps its row breaks.
  let raw = "";
  try {
    raw = String(node.innerText || "");
  } catch (_) {}

  if (!raw.trim() && node.cloneNode) {
    try {
      const clone = node.cloneNode(true);
      clone.querySelectorAll?.("br").forEach((br) => {
        br.replaceWith(clone.ownerDocument.createTextNode("\n"));
      });
      clone
        .querySelectorAll?.("p,div,section,article,blockquote,pre")
        .forEach((block) => {
          if (block !== clone) {
            block.appendChild(clone.ownerDocument.createTextNode("\n"));
          }
        });
      raw = String(clone.textContent || "");
    } catch (_) {
      raw = String(node.textContent || "");
    }
  }

  return String(raw || node.textContent || "")
    .replace(/\u00a0/g, " ")
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n[ \t]+/g, "\n")
    .trim();
}

function importStandaloneMarkdownTables(root, options = {}) {
  if (!root?.querySelectorAll) return [];
  const inserted = [];
  const source = normalizeSource(options.source || "ai");
  const documentValue = root.ownerDocument || ownerDocumentFrom(root);
  if (!documentValue) return inserted;

  const candidates = Array.from(
    root.querySelectorAll(
      [
        "pre",
        '[data-doc-table-markdown="true"]',
        '[data-content-type="table"]',
        '[data-doc-structured-type="table"]',
        '[data-doc-block-type="table"]',
        '[data-doc-protected-block="true"][data-doc-block-source^="[table]"]',
        '[data-doc-protected-block="true"][data-doc-block-canonical-source^="[table]"]',
      ].join(",")
    )
  );

  candidates.forEach((node) => {
    if (!node || node.closest?.("table")) return;
    const raw = tableSourceTextFromNode(node);
    const parsed = parseMarkdownTable(raw);
    const replacementTable = parsed.valid
      ? createTableFromMarkdown(raw, {
          source,
          documentValue,
          width: "100%",
        })
      : createTableFromStructuredInstruction(raw, {
          source,
          documentValue,
          width: "100%",
        });
    if (!replacementTable) return;
    node.replaceWith(replacementTable);
    inserted.push(replacementTable);
  });

  // Some rich-text pipelines turn a Markdown table into several adjacent
  // paragraphs. Reassemble only groups that contain a valid Markdown separator
  // row, so ordinary prose containing a pipe character can never be converted.
  const blockParents = new Set([
    root,
    ...Array.from(root.querySelectorAll("div,section,article")),
  ]);
  blockParents.forEach((parent) => {
    const children = Array.from(parent.children || []);
    let index = 0;
    while (index < children.length) {
      const child = children[index];
      if (!child?.isConnected || !["P", "DIV"].includes(child.tagName) || child.querySelector?.("table")) {
        index += 1;
        continue;
      }
      if (!tableSourceTextFromNode(child).includes("|")) {
        index += 1;
        continue;
      }
      const group = [];
      let cursor = index;
      while (cursor < children.length) {
        const candidate = children[cursor];
        if (!candidate?.isConnected || !["P", "DIV"].includes(candidate.tagName)) break;
        const line = tableSourceTextFromNode(candidate);
        if (!line.includes("|")) break;
        group.push(candidate);
        cursor += 1;
      }
      if (group.length >= 2) {
        const raw = group.map(tableSourceTextFromNode).join("\n");
        const parsed = parseMarkdownTable(raw);
        if (parsed.valid) {
          const table = createTableFromMarkdown(raw, { source, documentValue, width: "100%" });
          if (table) {
            group[0].replaceWith(table);
            group.slice(1).forEach((node) => node.remove());
            inserted.push(table);
          }
        }
      }
      index = Math.max(cursor, index + 1);
    }
  });

  // Final conservative recovery for a table-only AI response placed in one rich
  // block (often a <p>/<div> with <br> row breaks). Convert only when the block
  // is overwhelmingly table-shaped so normal prose containing a pipe is untouched.
  Array.from(root.querySelectorAll("p,div,section,article,blockquote")).forEach((node) => {
    if (!node?.isConnected || node === root || node.closest?.("table") || node.querySelector?.("table")) {
      return;
    }
    const raw = tableSourceTextFromNode(node);
    const lines = raw.split(/\n+/).map((line) => line.trim()).filter(Boolean);
    const pipeLines = lines.filter((line) => line.includes("|"));
    const explicitTableMarker = /^\s*\[table\]\s*$/i.test(lines[0] || "");
    const tableDominant =
      pipeLines.length >= 2 &&
      (explicitTableMarker || pipeLines.length / Math.max(1, lines.length) >= 0.7);
    if (!tableDominant) return;

    const parsed = parseMarkdownTable(raw);
    const structured = parseStructuredTableInstruction(raw);
    if (!parsed.valid && !structured) return;
    const replacementTable = parsed.valid
      ? createTableFromMarkdown(raw, {
          source,
          documentValue,
          width: "100%",
        })
      : createTableFromStructuredInstruction(raw, {
          source,
          documentValue,
          width: "100%",
        });
    if (!replacementTable) return;
    node.replaceWith(replacementTable);
    inserted.push(replacementTable);
  });

  return inserted;
}

export function normalizeCanvasTables(root, options = {}) {
  if (!root?.querySelectorAll) return [];
  importStandaloneMarkdownTables(root, options);
  const tables = [];
  if (root.tagName === "TABLE") tables.push(root);
  root.querySelectorAll("table").forEach((table) => tables.push(table));
  const unique = Array.from(new Set(tables));
  return unique
    .map((table) => normalizeCanvasTable(table, options))
    .filter(Boolean);
}

function appendCellContent(cell, value) {
  const lines = String(value ?? "").split(/\n/);
  lines.forEach((line, index) => {
    if (index > 0) cell.appendChild(cell.ownerDocument.createElement("br"));
    if (line) cell.appendChild(cell.ownerDocument.createTextNode(line));
  });
  if (!cell.childNodes.length) cell.appendChild(cell.ownerDocument.createElement("br"));
}

export function createCanvasTable(
  {
    rows = 3,
    columns = 3,
    headerRow = true,
    source = "toolbar",
    headers = null,
    data = null,
    alignments = null,
    tableId = "",
    width = "100%",
  } = {},
  documentValue = null
) {
  const documentObject = documentValue || ownerDocumentFrom(null);
  if (!documentObject) {
    throw new Error("createCanvasTable requires a browser Document.");
  }

  const normalizedSource = normalizeSource(source);
  // Use the requested dimensions directly (for example 20 x 20). The only
  // ceiling is the generous malformed-input safety cap; there is no 10 x 10
  // product or picker restriction in DocWriterTable.
  const rowLimit =
    normalizedSource === "toolbar" ? MANUAL_CREATE_MAX_ROWS : IMPORT_MAX_ROWS;
  const columnLimit =
    normalizedSource === "toolbar"
      ? MANUAL_CREATE_MAX_COLUMNS
      : IMPORT_MAX_COLUMNS;

  const requestedRows = Array.isArray(data) ? data.length : rows;
  const requestedColumns = Array.isArray(headers)
    ? headers.length
    : Array.isArray(data)
    ? data.reduce((maximum, row) => Math.max(maximum, row?.length || 0), 0)
    : columns;

  const rowCount = clamp(
    Number.parseInt(String(requestedRows), 10) || 1,
    1,
    rowLimit
  );
  const columnCount = clamp(
    Number.parseInt(String(requestedColumns), 10) || 1,
    1,
    columnLimit
  );

  const table = documentObject.createElement("table");
  table.setAttribute(DOC_TABLE_ATTR, "true");
  table.setAttribute(
    DOC_TABLE_ID_ATTR,
    tableId || createDocumentObjectId("table")
  );
  table.setAttribute(DOC_TABLE_SOURCE_ATTR, normalizedSource);
  table.style.width = String(width || "100%");
  table.style.maxWidth = "100%";
  table.style.borderCollapse = "collapse";
  table.style.tableLayout = "fixed";

  const colgroup = documentObject.createElement("colgroup");
  table.appendChild(colgroup);

  const normalizedHeaders = Array.isArray(headers)
    ? normalizeRows(new Array(columnCount).fill(""), [headers])[0]
    : new Array(columnCount).fill("");
  const blankBodyRowCount = headerRow ? Math.max(0, rowCount - 1) : rowCount;
  const normalizedData = Array.isArray(data)
    ? normalizeRows(new Array(columnCount).fill(""), data).slice(0, rowLimit)
    : new Array(blankBodyRowCount)
        .fill(null)
        .map(() => new Array(columnCount).fill(""));

  if (headerRow) {
    const head = documentObject.createElement("thead");
    const row = documentObject.createElement("tr");
    for (let columnIndex = 0; columnIndex < columnCount; columnIndex += 1) {
      const cell = documentObject.createElement("th");
      appendCellContent(cell, normalizedHeaders[columnIndex] || "");
      if (alignments?.[columnIndex]) {
        cell.style.textAlign = alignments[columnIndex];
      }
      row.appendChild(cell);
    }
    head.appendChild(row);
    table.appendChild(head);
  }

  const body = documentObject.createElement("tbody");
  normalizedData.slice(0, rowCount).forEach((sourceRow) => {
    const row = documentObject.createElement("tr");
    for (let columnIndex = 0; columnIndex < columnCount; columnIndex += 1) {
      const cell = documentObject.createElement("td");
      appendCellContent(cell, sourceRow?.[columnIndex] || "");
      if (alignments?.[columnIndex]) {
        cell.style.textAlign = alignments[columnIndex];
      }
      row.appendChild(cell);
    }
    body.appendChild(row);
  });
  table.appendChild(body);

  normalizeCanvasTable(table, {
    source: normalizedSource,
    inferWidths: true,
    preserveTableId: true,
  });
  return table;
}

export function createTableFromMarkdown(
  value,
  { source = "ai", documentValue = null, width = "100%" } = {}
) {
  const parsed = parseMarkdownTable(value);
  if (!parsed.valid) return null;
  return createCanvasTable(
    {
      source,
      headerRow: true,
      headers: parsed.headers,
      data: parsed.rows,
      alignments: parsed.alignments,
      width,
    },
    documentValue
  );
}

function createTablesFromHtml(value, documentValue, source) {
  if (!documentValue) return [];
  const template = documentValue.createElement("template");
  template.innerHTML = String(value || "");
  const tables = Array.from(template.content.querySelectorAll("table"));
  return tables.map((table) => {
    const clone = table.cloneNode(true);
    normalizeCanvasTable(clone, { source, inferWidths: true });
    return clone;
  });
}

export function recognizeTableInput(
  value,
  { source = "ai", documentValue = null } = {}
) {
  const documentObject = documentValue || ownerDocumentFrom(value);

  if (value?.tagName === "TABLE") {
    const table = value.cloneNode(true);
    normalizeCanvasTable(table, { source, inferWidths: true });
    return { kind: "element", tables: [table], parsed: null };
  }

  if (Array.isArray(value)) {
    const tables = value
      .filter((item) => item?.tagName === "TABLE")
      .map((item) => {
        const table = item.cloneNode(true);
        normalizeCanvasTable(table, { source, inferWidths: true });
        return table;
      });
    return { kind: tables.length ? "elements" : "none", tables, parsed: null };
  }

  if (
    value &&
    typeof value === "object" &&
    Array.isArray(value.headers) &&
    Array.isArray(value.rows)
  ) {
    const table = createCanvasTable(
      {
        source,
        headerRow: true,
        headers: value.headers,
        data: value.rows,
        alignments: value.alignments,
      },
      documentObject
    );
    return { kind: "structured", tables: [table], parsed: value };
  }

  const raw = String(value || "");
  if (/<table\b/i.test(raw)) {
    const tables = createTablesFromHtml(raw, documentObject, source);
    return { kind: tables.length ? "html" : "none", tables, parsed: null };
  }

  const parsed = parseMarkdownTable(raw);
  if (parsed.valid) {
    const table = createCanvasTable(
      {
        source,
        headerRow: true,
        headers: parsed.headers,
        data: parsed.rows,
        alignments: parsed.alignments,
      },
      documentObject
    );
    return { kind: "markdown", tables: [table], parsed };
  }

  const structured = parseStructuredTableInstruction(raw);
  if (structured) {
    const table = createCanvasTable(
      {
        source,
        headerRow: true,
        headers: structured.headers,
        data: structured.rows,
        alignments: structured.alignments,
      },
      documentObject
    );
    return { kind: "structured-text", tables: [table], parsed: structured };
  }

  return { kind: "none", tables: [], parsed };
}

export function getLogicalTableFragments(root, tableOrId) {
  if (!root) return [];
  const tableId =
    typeof tableOrId === "string"
      ? tableOrId
      : tableOrId?.getAttribute?.(DOC_TABLE_ID_ATTR) || "";
  if (!tableId) return [];
  return Array.from(
    root.querySelectorAll(
      `table[${DOC_TABLE_ID_ATTR}="${escapeCssValue(tableId)}"]`
    )
  );
}

export function getLogicalTableCells(root, tableOrId) {
  const seen = new Set();
  const cells = [];
  getLogicalTableFragments(root, tableOrId).forEach((table) => {
    Array.from(table.querySelectorAll("th, td")).forEach((cell) => {
      if (cell.closest(`[${DOC_REPEAT_ONLY_ATTR}="true"]`)) return;
      const id = cell.getAttribute(DOC_CELL_ID_ATTR) || "";
      if (id && seen.has(id)) return;
      if (id) seen.add(id);
      cells.push(cell);
    });
  });
  return cells;
}

function cloneFirstDirectChild(fragments, tagName) {
  for (const table of fragments) {
    const child = findDirectChild(table, tagName);
    if (child) return child.cloneNode(true);
  }
  return null;
}

function stripFragmentOnlyMarkup(root) {
  if (!root?.querySelectorAll) return;
  root.querySelectorAll(`[${DOC_REPEAT_ONLY_ATTR}="true"]`).forEach((node) => {
    node.removeAttribute(DOC_REPEAT_ONLY_ATTR);
  });
  root.querySelectorAll("*").forEach((element) => {
    element.removeAttribute(DOC_TABLE_FRAGMENT_ATTR);
    element.removeAttribute(DOC_TABLE_SELECTED_ATTR);
    element.removeAttribute(DOC_CELL_SELECTED_ATTR);
  });
}

export function reassembleLogicalTable(
  root,
  tableOrId,
  { removeFragments = true, normalize = true } = {}
) {
  const fragments = getLogicalTableFragments(root, tableOrId);
  if (!fragments.length) return null;

  const tableId = fragments[0].getAttribute(DOC_TABLE_ID_ATTR) || "";
  if (fragments.length === 1) {
    const table = fragments[0];
    stripFragmentOnlyMarkup(table);
    return normalize
      ? normalizeCanvasTable(table, {
          source: table.getAttribute(DOC_TABLE_SOURCE_ATTR) || "reopened",
          inferWidths: false,
        })
      : table;
  }

  const documentValue = fragments[0].ownerDocument;
  const logicalTable = fragments[0].cloneNode(false);
  logicalTable.setAttribute(DOC_TABLE_ID_ATTR, tableId);
  logicalTable.removeAttribute(DOC_TABLE_FRAGMENT_ATTR);
  logicalTable.removeAttribute(DOC_TABLE_SELECTED_ATTR);

  const caption = cloneFirstDirectChild(fragments, "CAPTION");
  const colgroup = cloneFirstDirectChild(fragments, "COLGROUP");
  const thead = cloneFirstDirectChild(fragments, "THEAD");
  const tfoot = cloneFirstDirectChild([...fragments].reverse(), "TFOOT");
  if (caption) logicalTable.appendChild(caption);
  if (colgroup) logicalTable.appendChild(colgroup);
  if (thead) logicalTable.appendChild(thead);

  const body = documentValue.createElement("tbody");
  const seenRows = new Set();

  fragments.forEach((fragment) => {
    const rows = Array.from(
      fragment.querySelectorAll(":scope > tbody > tr, :scope > tr")
    );
    rows.forEach((row) => {
      if (row.closest(`[${DOC_REPEAT_ONLY_ATTR}="true"]`)) return;
      const rowId = row.getAttribute(DOC_ROW_ID_ATTR) || "";
      const key = rowId || row.outerHTML;
      if (seenRows.has(key)) return;
      seenRows.add(key);
      body.appendChild(row.cloneNode(true));
    });
  });

  logicalTable.appendChild(body);
  if (tfoot) logicalTable.appendChild(tfoot);
  stripFragmentOnlyMarkup(logicalTable);

  const first = fragments[0];
  first.replaceWith(logicalTable);
  if (removeFragments) fragments.slice(1).forEach((fragment) => fragment.remove());

  return normalize
    ? normalizeCanvasTable(logicalTable, {
        source:
          logicalTable.getAttribute(DOC_TABLE_SOURCE_ATTR) || "reopened",
        inferWidths: false,
      })
    : logicalTable;
}

export function reassembleAllLogicalTables(root, options = {}) {
  if (!root?.querySelectorAll) return [];
  const ids = Array.from(root.querySelectorAll(`table[${DOC_TABLE_ID_ATTR}]`))
    .map((table) => table.getAttribute(DOC_TABLE_ID_ATTR))
    .filter(Boolean);
  return Array.from(new Set(ids))
    .map((tableId) => reassembleLogicalTable(root, tableId, options))
    .filter(Boolean);
}

export function serializeCanvasTableDocument(root) {
  if (!root) return "";
  const documentValue = root.ownerDocument;
  const holder = documentValue.createElement("div");

  Array.from(root.childNodes || []).forEach((node) => {
    if (
      node.nodeType === 1 &&
      node.getAttribute?.(DOC_CANVAS_PAGE_ATTR) === "true"
    ) {
      Array.from(node.childNodes || []).forEach((child) => {
        holder.appendChild(child.cloneNode(true));
      });
    } else {
      holder.appendChild(node.cloneNode(true));
    }
  });

  holder
    .querySelectorAll(`[${DOC_REPEAT_ONLY_ATTR}="true"]`)
    .forEach((node) => node.remove());
  reassembleAllLogicalTables(holder, { normalize: true });
  stripFragmentOnlyMarkup(holder);
  normalizeCanvasTables(holder, { source: "reopened", inferWidths: false });
  holder.querySelectorAll("*").forEach((element) => {
    element.removeAttribute(DOC_TABLE_SELECTED_ATTR);
    element.removeAttribute(DOC_CELL_SELECTED_ATTR);
    element.removeAttribute(DOC_WRITER_TABLE_EDIT_OPEN_ATTR);
    element.removeAttribute(DOC_TABLE_INTERACTION_ATTR);
    element.removeAttribute("data-doc-table-dragging");
    element.removeAttribute("data-doc-dragging");
  });
  holder.querySelectorAll(`[${DOC_WRITER_TABLE_UI_ATTR}]`).forEach((node) => node.remove());
  return holder.innerHTML;
}

function findRowById(table, rowId) {
  if (!table || !rowId) return null;
  return table.querySelector(
    `tr[${DOC_ROW_ID_ATTR}="${escapeCssValue(rowId)}"]`
  );
}

function findCellById(table, cellId) {
  if (!table || !cellId) return null;
  return table.querySelector(
    `[${DOC_CELL_ID_ATTR}="${escapeCssValue(cellId)}"]`
  );
}

function positiveSpan(cell, propertyName) {
  const raw = Number.parseInt(String(cell?.[propertyName] || 1), 10);
  return Number.isFinite(raw) && raw > 0 ? raw : 1;
}

function setCellSpan(cell, propertyName, attributeName, value) {
  if (!cell) return;
  const next = Math.max(1, Number.parseInt(String(value || 1), 10) || 1);
  cell[propertyName] = next;
  if (next <= 1) cell.removeAttribute(attributeName);
  else cell.setAttribute(attributeName, String(next));
}

export function buildTableGrid(table) {
  // The optional bottom note belongs to the table object but is not part of the
  // structural row/column grid. Keeping it out of this matrix prevents merge,
  // header, row/column deletion, and selection math from treating a note as data.
  const rows = Array.from(table?.rows || []).filter(
    (row) => row.getAttribute?.(DOC_TABLE_NOTE_ROW_ATTR) !== "true"
  );
  const matrix = [];
  const origins = [];

  rows.forEach((row, rowIndex) => {
    if (!matrix[rowIndex]) matrix[rowIndex] = [];
    let logicalColumn = 0;

    Array.from(row.cells || []).forEach((cell) => {
      while (matrix[rowIndex][logicalColumn]) logicalColumn += 1;

      const rowSpan = positiveSpan(cell, "rowSpan");
      const colSpan = positiveSpan(cell, "colSpan");
      const origin = {
        cell,
        row,
        rowIndex,
        columnIndex: logicalColumn,
        rowSpan,
        colSpan,
      };
      origins.push(origin);

      for (let rowOffset = 0; rowOffset < rowSpan; rowOffset += 1) {
        const targetRow = rowIndex + rowOffset;
        if (!matrix[targetRow]) matrix[targetRow] = [];
        for (let columnOffset = 0; columnOffset < colSpan; columnOffset += 1) {
          const targetColumn = logicalColumn + columnOffset;
          if (!matrix[targetRow][targetColumn]) {
            matrix[targetRow][targetColumn] = origin;
          }
        }
      }

      logicalColumn += colSpan;
    });
  });

  const columnCount = Math.max(
    0,
    ...matrix.map((row) => row?.length || 0),
    ...origins.map((origin) => origin.columnIndex + origin.colSpan)
  );

  return { rows, matrix, origins, columnCount };
}

function originForCell(grid, cell) {
  if (!grid || !cell) return null;
  return grid.origins.find((origin) => origin.cell === cell) || null;
}

function makeBlankTableCell(documentValue, tagName = "td", templateCell = null) {
  const cell = documentValue.createElement(String(tagName || "td").toLowerCase());
  if (templateCell) {
    Array.from(templateCell.attributes || []).forEach((attribute) => {
      const name = String(attribute.name || "").toLowerCase();
      if (
        name === DOC_CELL_ID_ATTR ||
        name === DOC_CELL_SELECTED_ATTR ||
        name === "rowspan" ||
        name === "colspan"
      ) {
        return;
      }
      try {
        cell.setAttribute(attribute.name, attribute.value);
      } catch (_) {}
    });
  }
  cell.setAttribute(DOC_CELL_ID_ATTR, createDocumentObjectId("cell"));
  cell.setAttribute(DOC_TABLE_CELL_INTERACTIVE_ATTR, "true");
  cell.appendChild(documentValue.createElement("br"));
  normalizeCell(cell);
  return cell;
}

function insertCellAtLogicalColumn(table, row, logicalColumn, cell) {
  if (!table || !row || !cell) return false;
  const grid = buildTableGrid(table);
  const directOrigins = grid.origins
    .filter((origin) => origin.row === row)
    .sort((a, b) => a.columnIndex - b.columnIndex);
  const reference = directOrigins.find(
    (origin) => origin.columnIndex >= logicalColumn && origin.cell !== cell
  );
  row.insertBefore(cell, reference?.cell || null);
  return true;
}

function ensurePrimaryBody(table) {
  if (table.tBodies?.[0]) return table.tBodies[0];
  const body = table.ownerDocument.createElement("tbody");
  const foot = findDirectChild(table, "TFOOT");
  table.insertBefore(body, foot || null);
  return body;
}

function rowInsertionPlan(table, reference, where) {
  const grid = buildTableGrid(table);
  const rows = grid.rows;
  const referenceIndex = reference ? rows.indexOf(reference) : -1;
  const referenceSection = reference?.parentElement?.tagName || "";

  if (!reference) {
    const body = ensurePrimaryBody(table);
    return {
      grid,
      body,
      beforeNode: null,
      insertionIndex: rows.length,
    };
  }

  if (referenceSection === "THEAD") {
    const body = ensurePrimaryBody(table);
    const firstBodyRow = body.rows?.[0] || null;
    const insertionIndex = firstBodyRow
      ? rows.indexOf(firstBodyRow)
      : Math.max(0, referenceIndex + 1);
    return { grid, body, beforeNode: firstBodyRow, insertionIndex };
  }

  const body = referenceSection === "TBODY" ? reference.parentElement : ensurePrimaryBody(table);
  if (where === "above" && reference.parentElement === body) {
    return {
      grid,
      body,
      beforeNode: reference,
      insertionIndex: Math.max(0, referenceIndex),
    };
  }

  const next = reference.parentElement === body ? reference.nextElementSibling : null;
  return {
    grid,
    body,
    beforeNode: next,
    insertionIndex: Math.max(0, referenceIndex + 1),
  };
}

function nearestCreatedCellId(createdByColumn, requestedColumn) {
  const entries = Array.from(createdByColumn.entries()).sort((a, b) => a[0] - b[0]);
  if (!entries.length) return "";
  const requested = Number.isInteger(requestedColumn) ? requestedColumn : entries[0][0];
  let best = entries[0];
  let bestDistance = Math.abs(best[0] - requested);
  entries.forEach((entry) => {
    const distance = Math.abs(entry[0] - requested);
    if (distance < bestDistance) {
      best = entry;
      bestDistance = distance;
    }
  });
  return best[1];
}

export function addTableRow(
  table,
  { where = "below", rowId = "", cellId = "", focusColumn = null } = {}
) {
  if (!table) return { changed: false, focusCellId: "" };
  normalizeCanvasTable(table, { inferWidths: false });

  const sourceCell = findCellById(table, cellId);
  const reference =
    findRowById(table, rowId) ||
    sourceCell?.closest("tr") ||
    Array.from(table.querySelectorAll(":scope > tbody > tr")).at(-1) ||
    null;
  const plan = rowInsertionPlan(table, reference, where);
  const { grid, body, beforeNode, insertionIndex } = plan;
  const columnCount = Math.max(1, grid.columnCount || getTableColumnCount(table));

  const crossing = [];
  const crossingCells = new Set();
  grid.origins.forEach((origin) => {
    if (
      origin.rowIndex < insertionIndex &&
      origin.rowIndex + origin.rowSpan > insertionIndex &&
      !crossingCells.has(origin.cell)
    ) {
      crossingCells.add(origin.cell);
      crossing.push(origin);
      setCellSpan(origin.cell, "rowSpan", "rowspan", origin.rowSpan + 1);
    }
  });

  const coveredColumns = new Set();
  crossing.forEach((origin) => {
    for (
      let column = origin.columnIndex;
      column < origin.columnIndex + origin.colSpan;
      column += 1
    ) {
      coveredColumns.add(column);
    }
  });

  const row = table.ownerDocument.createElement("tr");
  row.setAttribute(DOC_ROW_ID_ATTR, createDocumentObjectId("row"));
  const createdByColumn = new Map();

  for (let column = 0; column < columnCount; column += 1) {
    if (coveredColumns.has(column)) continue;
    const cell = makeBlankTableCell(table.ownerDocument, "td");
    row.appendChild(cell);
    createdByColumn.set(column, cell.getAttribute(DOC_CELL_ID_ATTR) || "");
  }

  body.insertBefore(row, beforeNode || null);
  normalizeRow(row);
  normalizeCanvasTable(table, { inferWidths: false });

  const sourceGrid = buildTableGrid(table);
  const sourceOrigin = originForCell(sourceGrid, sourceCell);
  const requestedColumn = Number.isInteger(focusColumn)
    ? focusColumn
    : sourceOrigin?.columnIndex ?? sourceCell?.cellIndex ?? 0;

  return {
    changed: true,
    focusCellId: nearestCreatedCellId(createdByColumn, requestedColumn),
    rowId: row.getAttribute(DOC_ROW_ID_ATTR) || "",
    insertionIndex,
  };
}

function applyInsertedColumnWidths(table, oldWidths, insertionIndex, donorIndex) {
  if (!Array.isArray(oldWidths) || !oldWidths.length) {
    distributeTableColumns(table);
    return;
  }

  const donor = clamp(donorIndex, 0, oldWidths.length - 1);
  const donorWidth = Math.max(MIN_COLUMN_WIDTH_PX, oldWidths[donor] || MIN_COLUMN_WIDTH_PX);
  if (donorWidth < MIN_COLUMN_WIDTH_PX * 2) {
    distributeTableColumns(table);
    return;
  }

  const insertedWidth = donorWidth / 2;
  const widths = [...oldWidths];
  widths[donor] = donorWidth - insertedWidth;
  widths.splice(clamp(insertionIndex, 0, widths.length), 0, insertedWidth);
  applyColumnWidths(table, widths, { unit: "px" });
}

export function addTableColumn(
  table,
  { where = "right", cellId = "", columnIndex = null } = {}
) {
  if (!table) return { changed: false, focusCellId: "" };
  normalizeCanvasTable(table, { inferWidths: false });

  const source = findCellById(table, cellId);
  const grid = buildTableGrid(table);
  const oldColumnCount = Math.max(1, grid.columnCount);
  const sourceOrigin = originForCell(grid, source);
  const explicitIndex = Number.isInteger(columnIndex)
    ? clamp(columnIndex, 0, oldColumnCount - 1)
    : null;
  const sourceStart = explicitIndex ?? sourceOrigin?.columnIndex ?? oldColumnCount - 1;
  const sourceWidth = explicitIndex == null ? sourceOrigin?.colSpan || 1 : 1;
  const insertionIndex = clamp(
    where === "right" ? sourceStart + sourceWidth : sourceStart,
    0,
    oldColumnCount
  );
  const oldWidths = readColumnWidthsPx(table);

  const colgroup = ensureTableColgroup(table, oldColumnCount, { inferWidths: false });
  const newColumn = table.ownerDocument.createElement("col");
  newColumn.setAttribute(DOC_COLUMN_ID_ATTR, createDocumentObjectId("column"));
  colgroup.insertBefore(newColumn, colgroup.children[insertionIndex] || null);

  const expanded = new Set();
  let focusCellId = "";
  grid.rows.forEach((row, rowIndex) => {
    const left = insertionIndex > 0 ? grid.matrix[rowIndex]?.[insertionIndex - 1] : null;
    const right = grid.matrix[rowIndex]?.[insertionIndex] || null;

    if (left && right && left.cell === right.cell) {
      if (!expanded.has(left.cell)) {
        expanded.add(left.cell);
        setCellSpan(left.cell, "colSpan", "colspan", left.colSpan + 1);
      }
      return;
    }

    const tagName = row.parentElement?.tagName === "THEAD" ? "th" : "td";
    const cell = makeBlankTableCell(table.ownerDocument, tagName, source);
    insertCellAtLogicalColumn(table, row, insertionIndex, cell);
    if (row === source?.parentElement) {
      focusCellId = cell.getAttribute(DOC_CELL_ID_ATTR) || "";
    }
  });

  normalizeCanvasTable(table, { inferWidths: false });
  applyInsertedColumnWidths(
    table,
    oldWidths,
    insertionIndex,
    clamp(sourceStart, 0, oldColumnCount - 1)
  );

  if (!focusCellId) {
    const refreshed = buildTableGrid(table);
    const rowIndex = sourceOrigin?.rowIndex ?? 0;
    focusCellId =
      refreshed.matrix[rowIndex]?.[Math.min(insertionIndex, refreshed.columnCount - 1)]?.cell
        ?.getAttribute(DOC_CELL_ID_ATTR) || "";
  }

  return { changed: true, focusCellId, insertionIndex };
}

function findFocusCellAfterRowMutation(table, preferredRowIndex, logicalColumn) {
  const grid = buildTableGrid(table);
  if (!grid.rows.length) return "";
  const rowIndex = clamp(preferredRowIndex, 0, grid.rows.length - 1);
  const columnIndex = clamp(logicalColumn, 0, Math.max(0, grid.columnCount - 1));
  return (
    grid.matrix[rowIndex]?.[columnIndex]?.cell?.getAttribute(DOC_CELL_ID_ATTR) ||
    grid.rows[rowIndex]?.querySelector?.(`[${DOC_CELL_ID_ATTR}]`)?.getAttribute(DOC_CELL_ID_ATTR) ||
    ""
  );
}

export function deleteTableRow(table, { rowId = "", cellId = "" } = {}) {
  if (!table) return { changed: false, focusCellId: "", removeTable: false };
  normalizeCanvasTable(table, { inferWidths: false });
  const sourceCell = findCellById(table, cellId);
  const row = findRowById(table, rowId) || sourceCell?.closest("tr") || null;
  if (!row) return { changed: false, focusCellId: "", removeTable: false };

  const grid = buildTableGrid(table);
  const rowIndex = grid.rows.indexOf(row);
  if (rowIndex < 0) return { changed: false, focusCellId: "", removeTable: false };
  // Structural row deletion must never implicitly become "Delete table".
  // Preserve the final row; removing the entire table is an explicit command.
  if (grid.rows.length <= 1) {
    return {
      changed: false,
      focusCellId: sourceCell?.getAttribute(DOC_CELL_ID_ATTR) || "",
      removeTable: false,
      reason: "minimum-row-preserved",
    };
  }

  const sourceOrigin = originForCell(grid, sourceCell);
  const logicalColumn = sourceOrigin?.columnIndex ?? 0;
  const nextRow = grid.rows[rowIndex + 1] || null;
  const processed = new Set();

  grid.origins.forEach((origin) => {
    if (processed.has(origin.cell)) return;
    const startsBefore = origin.rowIndex < rowIndex;
    const coversDeletedRow =
      origin.rowIndex <= rowIndex && origin.rowIndex + origin.rowSpan > rowIndex;
    const startsOnDeletedRow = origin.rowIndex === rowIndex;

    if (startsBefore && coversDeletedRow && origin.rowSpan > 1) {
      processed.add(origin.cell);
      setCellSpan(origin.cell, "rowSpan", "rowspan", origin.rowSpan - 1);
      return;
    }

    if (startsOnDeletedRow && origin.rowSpan > 1 && nextRow) {
      processed.add(origin.cell);
      const migrated = origin.cell;
      setCellSpan(migrated, "rowSpan", "rowspan", origin.rowSpan - 1);
      insertCellAtLogicalColumn(table, nextRow, origin.columnIndex, migrated);
    }
  });

  row.remove();
  normalizeCanvasTable(table, { inferWidths: false });
  const preferredRow = Math.min(rowIndex, Math.max(0, table.rows.length - 1));
  return {
    changed: true,
    focusCellId: findFocusCellAfterRowMutation(table, preferredRow, logicalColumn),
    removeTable: false,
  };
}

export function deleteTableColumn(
  table,
  { cellId = "", columnIndex = null } = {}
) {
  if (!table) return { changed: false, focusCellId: "", removeTable: false };
  normalizeCanvasTable(table, { inferWidths: false });
  const source = findCellById(table, cellId);
  const grid = buildTableGrid(table);
  const sourceOrigin = originForCell(grid, source);
  const index = Number.isInteger(columnIndex)
    ? clamp(columnIndex, 0, Math.max(0, grid.columnCount - 1))
    : sourceOrigin?.columnIndex ?? -1;
  if (index < 0) return { changed: false, focusCellId: "", removeTable: false };

  // Structural column deletion must never implicitly become "Delete table".
  // Preserve the final column; removing the entire table is an explicit command.
  if (grid.columnCount <= 1) {
    return {
      changed: false,
      focusCellId: source?.getAttribute(DOC_CELL_ID_ATTR) || "",
      removeTable: false,
      reason: "minimum-column-preserved",
    };
  }

  const oldWidths = readColumnWidthsPx(table);
  const sourceRowIndex = sourceOrigin?.rowIndex ?? 0;
  const affected = new Set();
  grid.origins.forEach((origin) => {
    if (
      origin.columnIndex <= index &&
      origin.columnIndex + origin.colSpan > index &&
      !affected.has(origin.cell)
    ) {
      affected.add(origin.cell);
      if (origin.colSpan > 1) {
        setCellSpan(origin.cell, "colSpan", "colspan", origin.colSpan - 1);
      } else {
        origin.cell.remove();
      }
    }
  });

  const colgroup = findDirectChild(table, "COLGROUP");
  colgroup?.children[index]?.remove();
  normalizeCanvasTable(table, { inferWidths: false });

  if (oldWidths.length > 1) {
    const widths = [...oldWidths];
    const removed = widths.splice(index, 1)[0] || 0;
    const recipient = index > 0 ? index - 1 : 0;
    if (widths[recipient] != null) widths[recipient] += removed;
    applyColumnWidths(table, widths, { unit: "px" });
  } else {
    distributeTableColumns(table);
  }

  const refreshed = buildTableGrid(table);
  const focusColumn = clamp(index, 0, Math.max(0, refreshed.columnCount - 1));
  const focusCellId =
    refreshed.matrix[Math.min(sourceRowIndex, refreshed.rows.length - 1)]?.[focusColumn]?.cell
      ?.getAttribute(DOC_CELL_ID_ATTR) || "";
  return { changed: true, focusCellId, removeTable: false };
}

function appendMergedCellContent(master, cells) {
  if (!master || !cells?.length) return;
  cells.forEach((cell) => {
    if (!cell || cell === master) return;
    const meaningful = String(cell.textContent || "").trim() || cell.querySelector?.("img,svg,math");
    if (!meaningful) return;
    if (String(master.textContent || "").trim() || master.querySelector?.("img,svg,math")) {
      master.appendChild(master.ownerDocument.createElement("br"));
    } else if (
      master.childNodes.length === 1 &&
      master.firstChild?.nodeName === "BR"
    ) {
      master.replaceChildren();
    }
    while (cell.firstChild) master.appendChild(cell.firstChild);
  });
  if (!master.childNodes.length) master.appendChild(master.ownerDocument.createElement("br"));
}

export function mergeTableCells(table, { cellIds = [] } = {}) {
  if (!table || !Array.isArray(cellIds)) {
    return { changed: false, focusCellId: "", reason: "invalid-selection" };
  }
  normalizeCanvasTable(table, { inferWidths: false });
  const grid = buildTableGrid(table);
  const selected = Array.from(new Set(cellIds))
    .map((id) => findCellById(table, id))
    .filter(Boolean)
    .map((cell) => originForCell(grid, cell))
    .filter(Boolean);
  const unique = Array.from(new Map(selected.map((origin) => [origin.cell, origin])).values());
  if (unique.length < 2) {
    return { changed: false, focusCellId: unique[0]?.cell?.getAttribute(DOC_CELL_ID_ATTR) || "", reason: "select-multiple-cells" };
  }

  const sections = new Set(unique.map((origin) => origin.row.parentElement?.tagName || ""));
  if (sections.size > 1) {
    return { changed: false, focusCellId: "", reason: "cross-section-merge-not-supported" };
  }

  const minRow = Math.min(...unique.map((origin) => origin.rowIndex));
  const minColumn = Math.min(...unique.map((origin) => origin.columnIndex));
  const maxRow = Math.max(...unique.map((origin) => origin.rowIndex + origin.rowSpan - 1));
  const maxColumn = Math.max(...unique.map((origin) => origin.columnIndex + origin.colSpan - 1));
  const selectedCells = new Set(unique.map((origin) => origin.cell));

  for (let rowIndex = minRow; rowIndex <= maxRow; rowIndex += 1) {
    for (let columnIndex = minColumn; columnIndex <= maxColumn; columnIndex += 1) {
      const origin = grid.matrix[rowIndex]?.[columnIndex];
      if (!origin || !selectedCells.has(origin.cell)) {
        return { changed: false, focusCellId: "", reason: "selection-must-form-rectangle" };
      }
    }
  }

  const master =
    unique.find(
      (origin) => origin.rowIndex === minRow && origin.columnIndex === minColumn
    ) || unique[0];
  const others = unique
    .filter((origin) => origin.cell !== master.cell)
    .sort((a, b) => a.rowIndex - b.rowIndex || a.columnIndex - b.columnIndex);

  appendMergedCellContent(master.cell, others.map((origin) => origin.cell));
  setCellSpan(master.cell, "rowSpan", "rowspan", maxRow - minRow + 1);
  setCellSpan(master.cell, "colSpan", "colspan", maxColumn - minColumn + 1);
  others.forEach((origin) => origin.cell.remove());
  normalizeCanvasTable(table, { inferWidths: false });

  return {
    changed: true,
    focusCellId: master.cell.getAttribute(DOC_CELL_ID_ATTR) || "",
    rowSpan: maxRow - minRow + 1,
    colSpan: maxColumn - minColumn + 1,
  };
}

export function splitTableCell(
  table,
  { cellId = "", direction = "columns" } = {}
) {
  if (!table) return { changed: false, focusCellId: "", reason: "missing-table" };
  normalizeCanvasTable(table, { inferWidths: false });
  const source = findCellById(table, cellId);
  if (!source) return { changed: false, focusCellId: "", reason: "missing-cell" };

  const grid = buildTableGrid(table);
  const origin = originForCell(grid, source);
  if (!origin) return { changed: false, focusCellId: "", reason: "missing-grid-origin" };
  const mode = String(direction || "columns").toLowerCase();

  if (["columns", "column", "vertical", "left-right", "left_right"].includes(mode)) {
    const newCell = makeBlankTableCell(source.ownerDocument, source.tagName, source);

    if (origin.colSpan > 1) {
      const leftSpan = Math.ceil(origin.colSpan / 2);
      const rightSpan = origin.colSpan - leftSpan;
      setCellSpan(source, "colSpan", "colspan", leftSpan);
      setCellSpan(newCell, "colSpan", "colspan", Math.max(1, rightSpan));
      setCellSpan(newCell, "rowSpan", "rowspan", origin.rowSpan);
      source.parentElement?.insertBefore(newCell, source.nextSibling);
      normalizeCanvasTable(table, { inferWidths: false });
      return {
        changed: true,
        focusCellId: newCell.getAttribute(DOC_CELL_ID_ATTR) || "",
        direction: "columns",
      };
    }

    const insertionIndex = origin.columnIndex + 1;
    const oldWidths = readColumnWidthsPx(table);
    const oldColumnCount = grid.columnCount;
    const colgroup = ensureTableColgroup(table, oldColumnCount, { inferWidths: false });
    const newColumn = table.ownerDocument.createElement("col");
    newColumn.setAttribute(DOC_COLUMN_ID_ATTR, createDocumentObjectId("column"));
    colgroup.insertBefore(newColumn, colgroup.children[insertionIndex] || null);

    setCellSpan(newCell, "rowSpan", "rowspan", origin.rowSpan);
    source.parentElement?.insertBefore(newCell, source.nextSibling);

    const expanded = new Set();
    grid.rows.forEach((row, rowIndex) => {
      if (
        rowIndex >= origin.rowIndex &&
        rowIndex < origin.rowIndex + origin.rowSpan
      ) {
        return;
      }
      const left = grid.matrix[rowIndex]?.[insertionIndex - 1] || null;
      if (left && left.cell !== source && !expanded.has(left.cell)) {
        expanded.add(left.cell);
        setCellSpan(left.cell, "colSpan", "colspan", left.colSpan + 1);
      }
    });

    normalizeCanvasTable(table, { inferWidths: false });
    applyInsertedColumnWidths(table, oldWidths, insertionIndex, origin.columnIndex);
    return {
      changed: true,
      focusCellId: newCell.getAttribute(DOC_CELL_ID_ATTR) || "",
      direction: "columns",
    };
  }

  if (["rows", "row", "horizontal", "top-bottom", "top_bottom"].includes(mode)) {
    const newCell = makeBlankTableCell(source.ownerDocument, source.tagName, source);
    setCellSpan(newCell, "colSpan", "colspan", origin.colSpan);

    if (origin.rowSpan > 1) {
      const topSpan = Math.ceil(origin.rowSpan / 2);
      const bottomSpan = origin.rowSpan - topSpan;
      const targetRow = grid.rows[origin.rowIndex + topSpan];
      if (!targetRow || bottomSpan < 1) {
        return { changed: false, focusCellId: "", reason: "cannot-split-rowspan" };
      }
      setCellSpan(source, "rowSpan", "rowspan", topSpan);
      setCellSpan(newCell, "rowSpan", "rowspan", bottomSpan);
      insertCellAtLogicalColumn(table, targetRow, origin.columnIndex, newCell);
      normalizeCanvasTable(table, { inferWidths: false });
      return {
        changed: true,
        focusCellId: newCell.getAttribute(DOC_CELL_ID_ATTR) || "",
        direction: "rows",
      };
    }

    const row = origin.row;
    const newRow = table.ownerDocument.createElement("tr");
    newRow.setAttribute(DOC_ROW_ID_ATTR, createDocumentObjectId("row"));
    const occupyingOrigins = new Set(
      (grid.matrix[origin.rowIndex] || [])
        .filter(Boolean)
        .map((entry) => entry.cell)
    );
    occupyingOrigins.forEach((cell) => {
      if (cell === source) return;
      const occupiedOrigin = originForCell(grid, cell);
      if (!occupiedOrigin) return;
      setCellSpan(
        cell,
        "rowSpan",
        "rowspan",
        occupiedOrigin.rowSpan + 1
      );
    });

    newRow.appendChild(newCell);
    row.parentElement?.insertBefore(newRow, row.nextSibling);
    normalizeRow(newRow);
    normalizeCanvasTable(table, { inferWidths: false });
    return {
      changed: true,
      focusCellId: newCell.getAttribute(DOC_CELL_ID_ATTR) || "",
      direction: "rows",
      rowId: newRow.getAttribute(DOC_ROW_ID_ATTR) || "",
    };
  }

  return { changed: false, focusCellId: "", reason: "unsupported-split-direction" };
}

function nearestCanvasDropAnchor(root, node) {
  let element = elementFromNode(node);
  if (!root || !element || !root.contains(element)) return null;
  const page = element.closest?.(`[${DOC_CANVAS_PAGE_ATTR}="true"]`) || null;
  const boundary = page && root.contains(page) ? page : root;
  if (element === boundary) return boundary;
  while (element?.parentElement && element.parentElement !== boundary) {
    element = element.parentElement;
  }
  return element || boundary;
}

export function resolveTableDropTargetAtPoint(
  root,
  clientX,
  clientY,
  { tableId = "" } = {}
) {
  if (!root?.ownerDocument) return null;
  const documentValue = root.ownerDocument;
  const view = documentValue.defaultView;
  const elementFromPoint = documentValue.elementFromPoint?.bind(documentValue);
  if (typeof elementFromPoint !== "function") return null;

  const x = Number(clientX);
  const y = Number(clientY);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

  const hit = elementFromPoint(x, y);
  if (!hit || !root.contains(hit)) return null;

  const sourceFragments = tableId
    ? getLogicalTableFragments(root, tableId)
    : [];
  if (
    sourceFragments.some(
      (fragment) => fragment === hit || fragment.contains?.(hit)
    )
  ) {
    return null;
  }

  const anchor = nearestCanvasDropAnchor(root, hit);
  if (!anchor) return null;
  return {
    targetNode: anchor,
    hit,
    page:
      anchor.getAttribute?.(DOC_CANVAS_PAGE_ATTR) === "true"
        ? anchor
        : anchor.closest?.(`[${DOC_CANVAS_PAGE_ATTR}="true"]`) || null,
    view,
  };
}

export function getTableDragAutoScrollDelta(
  pointerY,
  scrollRect,
  {
    edgeSize = TABLE_DRAG_AUTOSCROLL_EDGE_PX,
    maxStep = TABLE_DRAG_AUTOSCROLL_MAX_STEP_PX,
  } = {}
) {
  const y = Number(pointerY);
  const top = Number(scrollRect?.top);
  const bottom = Number(scrollRect?.bottom);
  const edge = Math.max(16, Number(edgeSize) || TABLE_DRAG_AUTOSCROLL_EDGE_PX);
  const maximum = Math.max(1, Number(maxStep) || TABLE_DRAG_AUTOSCROLL_MAX_STEP_PX);
  if (![y, top, bottom].every(Number.isFinite) || bottom <= top) return 0;

  if (y < top + edge) {
    const strength = clamp((top + edge - y) / edge, 0, 1);
    return -Math.max(1, Math.round(maximum * strength * strength));
  }
  if (y > bottom - edge) {
    const strength = clamp((y - (bottom - edge)) / edge, 0, 1);
    return Math.max(1, Math.round(maximum * strength * strength));
  }
  return 0;
}

export function moveLogicalTable(
  root,
  tableOrId,
  targetNode,
  { position = "before" } = {}
) {
  if (!root || !targetNode) return { changed: false, table: null, reason: "missing-target" };
  const tableId =
    typeof tableOrId === "string"
      ? tableOrId
      : tableOrId?.getAttribute?.(DOC_TABLE_ID_ATTR) || "";
  if (!tableId) return { changed: false, table: null, reason: "missing-table-id" };

  const fragments = getLogicalTableFragments(root, tableId);
  if (!fragments.length) return { changed: false, table: null, reason: "table-not-found" };
  const sourceContainsTarget = fragments.some(
    (fragment) => fragment === targetNode || fragment.contains?.(targetNode)
  );
  if (sourceContainsTarget) {
    return { changed: false, table: fragments[0], reason: "target-inside-source-table" };
  }

  const anchor = nearestCanvasDropAnchor(root, targetNode);
  if (!anchor) return { changed: false, table: fragments[0], reason: "invalid-drop-target" };
  const logicalTable = reassembleLogicalTable(root, tableId, {
    removeFragments: true,
    normalize: true,
  });
  if (!logicalTable) return { changed: false, table: null, reason: "reassembly-failed" };

  if (anchor === logicalTable || logicalTable.contains(anchor)) {
    return { changed: false, table: logicalTable, reason: "target-inside-source-table" };
  }

  const normalizedPosition = String(position || "before").toLowerCase();
  if (anchor.getAttribute?.(DOC_CANVAS_PAGE_ATTR) === "true") {
    if (["start", "inside-start", "inside_start", "before"].includes(normalizedPosition)) {
      anchor.insertBefore(logicalTable, anchor.firstChild || null);
    } else {
      anchor.appendChild(logicalTable);
    }
  } else if (["after", "below"].includes(normalizedPosition)) {
    anchor.parentNode?.insertBefore(logicalTable, anchor.nextSibling || null);
  } else {
    anchor.parentNode?.insertBefore(logicalTable, anchor);
  }

  normalizeCanvasTable(logicalTable, { inferWidths: false });
  return { changed: true, table: logicalTable, tableId };
}

export function getTableContextActions(
  table,
  cell = null,
  { selectedCellIds = [] } = {}
) {
  const hasTable = Boolean(table);
  const hasCell = Boolean(cell && table?.contains?.(cell));
  const selectedCount = Array.isArray(selectedCellIds)
    ? Array.from(new Set(selectedCellIds.filter(Boolean))).length
    : 0;
  return [
    { id: TABLE_COMMANDS.INSERT_ROW_ABOVE, label: "Insert row above", enabled: hasCell },
    { id: TABLE_COMMANDS.INSERT_ROW_BELOW, label: "Insert row below", enabled: hasCell },
    { id: TABLE_COMMANDS.INSERT_COLUMN_LEFT, label: "Insert column left", enabled: hasCell },
    { id: TABLE_COMMANDS.INSERT_COLUMN_RIGHT, label: "Insert column right", enabled: hasCell },
    { id: TABLE_COMMANDS.SPLIT_CELL_COLUMNS, label: "Split cell into columns", enabled: hasCell },
    { id: TABLE_COMMANDS.SPLIT_CELL_ROWS, label: "Split cell into rows", enabled: hasCell },
    { id: TABLE_COMMANDS.MERGE_CELLS, label: "Merge selected cells", enabled: hasTable && selectedCount >= 2 },
    { id: TABLE_COMMANDS.DISTRIBUTE_COLUMNS, label: "Distribute columns evenly", enabled: hasTable },
    { id: TABLE_COMMANDS.TOGGLE_HEADER_ROW, label: "Toggle header row", enabled: hasTable },
    { id: TABLE_COMMANDS.ALIGN_LEFT, label: "Align table left", enabled: hasTable },
    { id: TABLE_COMMANDS.ALIGN_CENTER, label: "Align table center", enabled: hasTable },
    { id: TABLE_COMMANDS.ALIGN_RIGHT, label: "Align table right", enabled: hasTable },
    { id: TABLE_COMMANDS.DELETE_ROW, label: "Delete row", enabled: hasCell, destructive: true },
    { id: TABLE_COMMANDS.DELETE_COLUMN, label: "Delete column", enabled: hasCell, destructive: true },
    { id: TABLE_COMMANDS.DELETE_TABLE, label: "Delete table", enabled: hasTable, destructive: true },
  ];
}

export function getTableInteractionModel(
  table,
  cell = null,
  { selectedCellIds = [] } = {}
) {
  const grid = table ? buildTableGrid(table) : { rows: [], columnCount: 0, origins: [] };
  const origin = table && cell ? originForCell(grid, cell) : null;
  return {
    version: DOC_WRITER_TABLE_VERSION,
    active: Boolean(table),
    tableId: table?.getAttribute?.(DOC_TABLE_ID_ATTR) || "",
    cellId: cell?.getAttribute?.(DOC_CELL_ID_ATTR) || "",
    rowId: cell?.closest?.("tr")?.getAttribute?.(DOC_ROW_ID_ATTR) || "",
    rowIndex: origin?.rowIndex ?? -1,
    columnIndex: origin?.columnIndex ?? -1,
    rowCount: grid.rows.length,
    columnCount: grid.columnCount,
    affordances: {
      moveHandle: Boolean(table),
      moveHandleAnchor: "top-left",
      moveHandleCursor: "grab",
      wholeTableDrag: Boolean(table),
      crossPageMove: Boolean(table),
      autoScrollWhileDragging: Boolean(table),
      rowInsertControls: Boolean(cell),
      columnInsertControls: Boolean(cell),
      rowResize: Boolean(cell),
      columnResize: Boolean(cell),
      tableResize: Boolean(table),
      contextMenu: Boolean(table),
      splitCell: Boolean(cell),
      mergeCells: Array.isArray(selectedCellIds) && selectedCellIds.length >= 2,
    },
    actions: getTableContextActions(table, cell, { selectedCellIds }),
  };
}

export function createDocWriterTableControls({
  getRoot,
  tableApi,
  onRequestMove = null,
  onRequestResize = null,
  confirmDeleteTable = null,
  onEditingChange = null,
} = {}) {
  const resolveRoot = () =>
    typeof getRoot === "function" ? getRoot() : getRoot || null;

  const documentValue = resolveRoot()?.ownerDocument ||
    (typeof document !== "undefined" ? document : null);
  const view = documentValue?.defaultView ||
    (typeof window !== "undefined" ? window : null);

  if (!documentValue || !view || !tableApi) {
    return {
      refresh() {},
      close() {},
      destroy() {},
      getState: () => ({ activeTableId: "", editOpen: false }),
    };
  }

  let activeTableId = "";
  let activeCellId = "";
  let activeRowId = "";
  let activeColumnIndex = -1;
  let editOpen = false;
  let dragCleanup = null;
  let resizeCleanup = null;
  let previewTable = null;
  let previewCellId = "";
  let previewRowId = "";
  let previewColumnIndex = -1;
  let previewSelectedCellIds = new Set();
  let previewSelectionAnchorId = "";
  let previewSelectionDrag = null;
  let previewDirty = false;
  let previewInitialHtml = "";
  let previewUndoStack = [];
  let previewRedoStack = [];
  let previewHistoryRestoring = false;
  const PREVIEW_HISTORY_LIMIT = 120;
  let editBeforeHtml = "";
  let previewResizeCleanup = null;
  let previewColumnResizeCleanup = null;
  let previewRowResizeCleanup = null;
  let liveColumnResizeCleanup = null;
  let liveRowResizeCleanup = null;
  let liveHoverCell = null;
  let tableWidthUnit = "%";
  let previewBorderToolStyle = "solid";
  let previewBorderToolWidth = 1;
  let previewBorderToolColor = "#0f172a";
  let previewStyleTarget = "cell";
  let previewSupplementalId = "";
  let previewSupplementalKind = "";

  const notifyEditingChange = (open, detail = {}) => {
    try {
      onEditingChange?.(Boolean(open), {
        tableId: activeTableId,
        ...detail,
      });
    } catch (_) {}
  };

  const chrome = documentValue.createElement("div");
  chrome.setAttribute(DOC_WRITER_TABLE_UI_ATTR, "chrome");
  chrome.setAttribute("contenteditable", "false");
  Object.assign(chrome.style, {
    position: "fixed",
    zIndex: "2147482000",
    display: "none",
    pointerEvents: "none",
    height: "34px",
    boxSizing: "border-box",
  });

  const handle = documentValue.createElement("button");
  handle.type = "button";
  handle.textContent = "⠿";
  handle.title = "Drag to move table";
  handle.setAttribute("aria-label", "Move table");
  handle.setAttribute(DOC_WRITER_TABLE_UI_ATTR, "drag-handle");
  Object.assign(handle.style, {
    position: "absolute",
    left: "0",
    top: "0",
    width: "38px",
    minWidth: "38px",
    height: "34px",
    minHeight: "34px",
    padding: "0",
    border: "1px solid rgba(15,23,42,.15)",
    borderRadius: "9px",
    background: "rgba(255,255,255,.97)",
    color: "#475569",
    boxShadow: "0 3px 10px rgba(15,23,42,0.10)",
    fontSize: "17px",
    fontWeight: "800",
    cursor: "grab",
    pointerEvents: "auto",
    userSelect: "none",
    touchAction: "none",
    boxSizing: "border-box",
  });

  const editButton = documentValue.createElement("button");
  editButton.type = "button";
  editButton.textContent = "Edit";
  editButton.title = "Edit table";
  editButton.setAttribute("aria-label", "Edit table");
  editButton.setAttribute(DOC_WRITER_TABLE_UI_ATTR, "edit-button");
  Object.assign(editButton.style, {
    position: "absolute",
    right: "0",
    top: "0",
    height: "34px",
    minHeight: "34px",
    padding: "0 14px",
    border: "1px solid rgba(15,23,42,.15)",
    borderRadius: "9px",
    background: "rgba(255,255,255,.97)",
    color: "#1E2A78",
    boxShadow: "0 3px 10px rgba(15,23,42,0.10)",
    fontSize: "12px",
    fontWeight: "850",
    cursor: "pointer",
    pointerEvents: "auto",
    boxSizing: "border-box",
  });

  const resizeHandleDefinitions = [
    { handle: "nw", label: "↖", cursor: "nwse-resize", kind: "corner" },
    { handle: "ne", label: "↗", cursor: "nesw-resize", kind: "corner" },
    { handle: "sw", label: "↙", cursor: "nesw-resize", kind: "corner" },
    { handle: "se", label: "↘", cursor: "nwse-resize", kind: "corner" },
    { handle: "n", label: "", cursor: "ns-resize", kind: "edge" },
    { handle: "s", label: "", cursor: "ns-resize", kind: "edge" },
  ];

  const resizeHandles = resizeHandleDefinitions.map((definition) => {
    const button = documentValue.createElement("button");
    button.type = "button";
    button.title = definition.kind === "edge" ? "Drag to resize table height" : "Drag to resize table";
    button.setAttribute("aria-label", definition.kind === "edge" ? "Resize table height" : "Resize table");
    button.setAttribute(DOC_WRITER_TABLE_UI_ATTR, `resize-handle-${definition.handle}`);
    button.dataset.docWriterTableResizeHandle = definition.handle;

    if (definition.kind === "corner") {
      const glyph = documentValue.createElement("span");
      glyph.textContent = definition.label;
      glyph.setAttribute("aria-hidden", "true");
      Object.assign(glyph.style, {
        pointerEvents: "none",
        lineHeight: "1",
        display: "block",
      });
      button.appendChild(glyph);
      Object.assign(button.style, {
        position: "fixed",
        zIndex: "2147482000",
        display: "none",
        width: "24px",
        height: "24px",
        padding: "0",
        border: "1px solid rgba(30,42,120,0.16)",
        borderRadius: "7px",
        background: "rgba(255,255,255,0.96)",
        color: "rgba(30,42,120,0.88)",
        boxShadow: "0 3px 10px rgba(15,23,42,0.10)",
        fontSize: "15px",
        fontWeight: "900",
        lineHeight: "1",
        placeItems: "center",
        alignItems: "center",
        justifyContent: "center",
        cursor: definition.cursor,
        pointerEvents: "auto",
        userSelect: "none",
        touchAction: "none",
        boxSizing: "border-box",
      });
    } else {
      const line = documentValue.createElement("span");
      Object.assign(line.style, {
        display: "block",
        width: "36px",
        height: "3px",
        borderRadius: "999px",
        background: "rgba(30,42,120,0.26)",
        pointerEvents: "none",
        transition: "width 120ms ease, height 120ms ease, background 120ms ease",
      });
      button.appendChild(line);
      Object.assign(button.style, {
        position: "fixed",
        zIndex: "2147482000",
        display: "none",
        width: "120px",
        height: "14px",
        padding: "0",
        border: "0",
        background: "transparent",
        alignItems: "center",
        justifyContent: "center",
        cursor: definition.cursor,
        pointerEvents: "auto",
        userSelect: "none",
        touchAction: "none",
      });
      button.addEventListener("mouseenter", () => {
        line.style.width = "58px";
        line.style.height = "4px";
        line.style.background = "rgba(30,42,120,0.55)";
      });
      button.addEventListener("mouseleave", () => {
        line.style.width = "36px";
        line.style.height = "3px";
        line.style.background = "rgba(30,42,120,0.26)";
      });
    }

    return { ...definition, button };
  });

  const resizeTooltip = documentValue.createElement("div");
  resizeTooltip.textContent = "Resize table";
  resizeTooltip.setAttribute(DOC_WRITER_TABLE_UI_ATTR, "resize-tooltip");
  Object.assign(resizeTooltip.style, {
    position: "fixed",
    zIndex: "2147482001",
    display: "none",
    padding: "4px 7px",
    borderRadius: "6px",
    background: "#0f172a",
    color: "#ffffff",
    fontSize: "10px",
    fontWeight: "800",
    lineHeight: "1",
    pointerEvents: "none",
    whiteSpace: "nowrap",
    boxShadow: "0 4px 12px rgba(15,23,42,.20)",
  });

  const editHint = documentValue.createElement("div");
  editHint.textContent = "You can edit the table";
  editHint.setAttribute(DOC_WRITER_TABLE_UI_ATTR, "hover-edit-hint");
  editHint.setAttribute("contenteditable", "false");
  Object.assign(editHint.style, {
    position: "fixed",
    zIndex: "2147481999",
    display: "none",
    pointerEvents: "none",
    padding: "5px 8px",
    borderRadius: "7px",
    border: "1px solid rgba(148,163,184,.42)",
    background: "rgba(255,255,255,.98)",
    color: "#334155",
    boxShadow: "0 4px 14px rgba(15,23,42,.12)",
    fontSize: "10px",
    fontWeight: "800",
    lineHeight: "1.2",
    whiteSpace: "nowrap",
  });

  const overlay = documentValue.createElement("div");
  overlay.setAttribute(DOC_WRITER_TABLE_UI_ATTR, "edit-overlay");
  overlay.setAttribute("contenteditable", "false");
  Object.assign(overlay.style, {
    position: "fixed",
    inset: "0",
    zIndex: "2147483000",
    display: "none",
    placeItems: "center",
    padding: "20px",
    boxSizing: "border-box",
    background: "rgba(15,23,42,0.30)",
  });

  const dialog = documentValue.createElement("div");
  dialog.setAttribute("role", "dialog");
  dialog.setAttribute("aria-modal", "true");
  dialog.setAttribute("aria-label", "Edit table");
  Object.assign(dialog.style, {
    width: "min(1180px, 96vw)",
    maxHeight: "90vh",
    minHeight: "0",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    background: "#ffffff",
    borderRadius: "16px",
    boxShadow: "0 24px 70px rgba(15,23,42,0.28)",
    border: "1px solid rgba(148,163,184,0.35)",
    color: "#0f172a",
    fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  });
  overlay.appendChild(dialog);

  const dialogHeader = documentValue.createElement("div");
  Object.assign(dialogHeader.style, {
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    padding: "17px 18px 15px",
    borderBottom: "1px solid rgba(148,163,184,0.22)",
    background: "#ffffff",
  });
  const headerCopy = documentValue.createElement("div");
  const dialogTitle = documentValue.createElement("div");
  dialogTitle.textContent = "Edit table";
  Object.assign(dialogTitle.style, { fontWeight: "900", color: "#0f172a", fontSize: "16px" });
  const dialogHint = documentValue.createElement("div");
  dialogHint.textContent = "Edit the live preview freely. The document table changes only when you choose Save changes.";
  Object.assign(dialogHint.style, { fontSize: "12px", color: "#475569", marginTop: "2px" });
  headerCopy.appendChild(dialogTitle);
  headerCopy.appendChild(dialogHint);
  const undoButton = documentValue.createElement("button");
  undoButton.type = "button";
  undoButton.textContent = "Undo";
  undoButton.title = "Undo the last unsaved table-preview change";
  undoButton.setAttribute("aria-label", "Undo preview change");

  const redoButton = documentValue.createElement("button");
  redoButton.type = "button";
  redoButton.textContent = "Redo";
  redoButton.title = "Redo the last unsaved table-preview change";
  redoButton.setAttribute("aria-label", "Redo preview change");

  const closeButton = documentValue.createElement("button");
  closeButton.type = "button";
  closeButton.textContent = "Close";

  const headerActions = documentValue.createElement("div");
  Object.assign(headerActions.style, {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    flex: "0 0 auto",
  });
  headerActions.appendChild(undoButton);
  headerActions.appendChild(redoButton);
  headerActions.appendChild(closeButton);

  dialogHeader.appendChild(headerCopy);
  dialogHeader.appendChild(headerActions);
  dialog.appendChild(dialogHeader);

  const dialogBody = documentValue.createElement("div");
  Object.assign(dialogBody.style, {
    flex: "1 1 auto",
    minHeight: "0",
    overflowY: "auto",
    overflowX: "hidden",
    scrollbarGutter: "stable",
    padding: "16px 18px 18px",
    background: "#ffffff",
  });
  dialog.appendChild(dialogBody);

  const bodyGrid = documentValue.createElement("div");
  Object.assign(bodyGrid.style, {
    display: "grid",
    gridTemplateColumns: "minmax(310px, 390px) minmax(0, 1fr)",
    gap: "18px",
    alignItems: "start",
  });
  dialogBody.appendChild(bodyGrid);

  const controlsPane = documentValue.createElement("div");
  Object.assign(controlsPane.style, { minWidth: "0", display: "grid", gap: "12px" });
  bodyGrid.appendChild(controlsPane);

  const previewPane = documentValue.createElement("div");
  Object.assign(previewPane.style, {
    minWidth: "0",
    position: "sticky",
    top: "0",
    alignSelf: "start",
  });
  bodyGrid.appendChild(previewPane);

  const previewHeading = documentValue.createElement("div");
  previewHeading.textContent = "Live preview";
  Object.assign(previewHeading.style, { fontSize: "12px", fontWeight: "900", color: "#0f172a", marginBottom: "6px" });
  const previewSubheading = documentValue.createElement("div");
  previewSubheading.textContent = "Click a cell to type or delete. Drag cell edges for row/column sizing, or use the four corners and top/bottom table edges. The preview shows its own bottom scrollbar when the table is wider and its right scrollbar when the table is taller.";
  Object.assign(previewSubheading.style, { fontSize: "11px", color: "#64748b", lineHeight: "1.45", marginBottom: "9px" });
  previewPane.appendChild(previewHeading);
  previewPane.appendChild(previewSubheading);

  const previewViewport = documentValue.createElement("div");
  previewViewport.setAttribute(DOC_WRITER_TABLE_UI_ATTR, "preview-viewport");
  Object.assign(previewViewport.style, {
    position: "relative",
    maxHeight: "min(64vh, 650px)",
    minHeight: "320px",
    overflowY: "auto",
    overflowX: "hidden",
    scrollbarGutter: "stable",
    overscrollBehavior: "contain",
    padding: "18px 26px 34px 18px",
    border: "1px solid #dbe3ee",
    borderRadius: "12px",
    background: "#f8fafc",
    boxSizing: "border-box",
  });
  previewPane.appendChild(previewViewport);

  const previewStage = documentValue.createElement("div");
  Object.assign(previewStage.style, {
    position: "relative",
    // The stage is the preview's available width, not a max-content measuring
    // surface. A narrow table therefore stays narrow; only the TABLE itself may
    // create horizontal overflow, which makes previewViewport expose its bottom
    // scrollbar exactly when the table is wider than the pane.
    width: "100%",
    minWidth: "0",
    minHeight: "260px",
    padding: "14px",
    boxSizing: "border-box",
    background: "#ffffff",
    borderRadius: "10px",
    overflow: "visible",
  });
  previewViewport.appendChild(previewStage);

  // Keep a dedicated synchronized horizontal scrollbar visible immediately below
  // the preview viewport. This does not depend on the browser deciding whether to
  // paint an overlay scrollbar inside the two-axis scroller, and it remains
  // available even when a tall table is vertically scrolled.
  const previewHorizontalScroller = documentValue.createElement("div");
  previewHorizontalScroller.setAttribute(
    DOC_WRITER_TABLE_UI_ATTR,
    "preview-horizontal-scrollbar"
  );
  Object.assign(previewHorizontalScroller.style, {
    display: "none",
    width: "100%",
    height: "17px",
    overflowX: "scroll",
    overflowY: "hidden",
    marginTop: "6px",
    border: "1px solid #e2e8f0",
    borderRadius: "7px",
    background: "#ffffff",
    boxSizing: "border-box",
    scrollbarGutter: "stable",
  });
  const previewHorizontalSpacer = documentValue.createElement("div");
  Object.assign(previewHorizontalSpacer.style, {
    width: "0px",
    height: "1px",
    pointerEvents: "none",
  });
  previewHorizontalScroller.appendChild(previewHorizontalSpacer);
  previewPane.appendChild(previewHorizontalScroller);

  const previewResizeTooltip = documentValue.createElement("div");
  previewResizeTooltip.textContent = "Resize table";
  Object.assign(previewResizeTooltip.style, {
    position: "absolute",
    display: "none",
    padding: "4px 7px",
    borderRadius: "6px",
    background: "#0f172a",
    color: "#ffffff",
    fontSize: "10px",
    fontWeight: "800",
    lineHeight: "1",
    pointerEvents: "none",
    whiteSpace: "nowrap",
    boxShadow: "0 4px 12px rgba(15,23,42,.20)",
    zIndex: "9",
  });
  previewStage.appendChild(previewResizeTooltip);

  const previewResizeHandles = resizeHandleDefinitions.map((definition) => {
    const button = documentValue.createElement("button");
    button.type = "button";
    button.setAttribute(DOC_WRITER_TABLE_UI_ATTR, `preview-resize-${definition.handle}`);
    button.dataset.docWriterTableResizeHandle = definition.handle;
    button.title = definition.kind === "edge" ? "Drag to resize table height" : "Drag to resize table";

    if (definition.kind === "corner") {
      const glyph = documentValue.createElement("span");
      glyph.textContent = definition.label;
      glyph.setAttribute("aria-hidden", "true");
      Object.assign(glyph.style, {
        pointerEvents: "none",
        lineHeight: "1",
        display: "block",
      });
      button.appendChild(glyph);
      Object.assign(button.style, {
        position: "absolute",
        display: "none",
        width: "24px",
        height: "24px",
        padding: "0",
        border: "1px solid rgba(30,42,120,0.16)",
        borderRadius: "7px",
        background: "rgba(255,255,255,0.96)",
        color: "rgba(30,42,120,0.88)",
        boxShadow: "0 3px 10px rgba(15,23,42,0.10)",
        fontSize: "15px",
        fontWeight: "900",
        lineHeight: "1",
        placeItems: "center",
        alignItems: "center",
        justifyContent: "center",
        cursor: definition.cursor,
        zIndex: "7",
        touchAction: "none",
        boxSizing: "border-box",
      });
    } else {
      const line = documentValue.createElement("span");
      Object.assign(line.style, {
        display: "block",
        width: "36px",
        height: "3px",
        borderRadius: "999px",
        background: "rgba(30,42,120,0.26)",
        pointerEvents: "none",
        transition: "width 120ms ease, height 120ms ease, background 120ms ease",
      });
      button.appendChild(line);
      Object.assign(button.style, {
        position: "absolute",
        display: "none",
        width: "120px",
        height: "14px",
        padding: "0",
        border: "0",
        background: "transparent",
        alignItems: "center",
        justifyContent: "center",
        cursor: "ns-resize",
        zIndex: "7",
        touchAction: "none",
      });
      button.addEventListener("mouseenter", () => {
        line.style.width = "58px";
        line.style.height = "4px";
        line.style.background = "rgba(30,42,120,0.55)";
      });
      button.addEventListener("mouseleave", () => {
        line.style.width = "36px";
        line.style.height = "3px";
        line.style.background = "rgba(30,42,120,0.26)";
      });
    }

    previewStage.appendChild(button);
    return { ...definition, button };
  });

  const previewStatus = documentValue.createElement("div");
  Object.assign(previewStatus.style, {
    marginTop: "8px",
    padding: "8px 10px",
    borderRadius: "9px",
    background: "#f8fafc",
    color: "#475569",
    fontSize: "11px",
    lineHeight: "1.4",
    border: "1px solid #e2e8f0",
  });
  previewPane.appendChild(previewStatus);

  const dialogFooter = documentValue.createElement("div");
  Object.assign(dialogFooter.style, {
    flex: "0 0 auto",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    padding: "13px 18px 15px",
    borderTop: "1px solid rgba(148,163,184,0.22)",
    background: "#ffffff",
  });
  const deleteTableButton = documentValue.createElement("button");
  deleteTableButton.type = "button";
  deleteTableButton.textContent = "Delete table";
  const footerRight = documentValue.createElement("div");
  Object.assign(footerRight.style, { display: "flex", gap: "8px" });
  const cancelButton = documentValue.createElement("button");
  cancelButton.type = "button";
  cancelButton.textContent = "Cancel";
  const saveButton = documentValue.createElement("button");
  saveButton.type = "button";
  saveButton.textContent = "Save changes";
  footerRight.appendChild(cancelButton);
  footerRight.appendChild(saveButton);
  dialogFooter.appendChild(deleteTableButton);
  dialogFooter.appendChild(footerRight);
  dialog.appendChild(dialogFooter);

  // In-app delete confirmation. This deliberately avoids window.confirm/browser
  // dialogs so the confirmation stays inside the Doc Writer workspace and cannot
  // be affected by popup blocking.
  let deleteConfirmOpen = false;
  const deleteConfirmLayer = documentValue.createElement("div");
  deleteConfirmLayer.setAttribute(DOC_WRITER_TABLE_UI_ATTR, "delete-confirm-layer");
  deleteConfirmLayer.setAttribute("contenteditable", "false");
  Object.assign(deleteConfirmLayer.style, {
    position: "fixed",
    inset: "0",
    zIndex: "2147483100",
    display: "none",
    placeItems: "center",
    padding: "20px",
    boxSizing: "border-box",
    background: "rgba(15,23,42,0.38)",
  });

  const deleteConfirmCard = documentValue.createElement("div");
  deleteConfirmCard.setAttribute("role", "alertdialog");
  deleteConfirmCard.setAttribute("aria-modal", "true");
  deleteConfirmCard.setAttribute("aria-label", "Confirm delete table");
  Object.assign(deleteConfirmCard.style, {
    width: "min(420px, calc(100vw - 40px))",
    padding: "20px",
    borderRadius: "14px",
    border: "1px solid rgba(148,163,184,0.35)",
    background: "#ffffff",
    boxShadow: "0 24px 70px rgba(15,23,42,0.30)",
    color: "#0f172a",
    boxSizing: "border-box",
  });

  const deleteConfirmTitle = documentValue.createElement("div");
  deleteConfirmTitle.textContent = "Delete table?";
  Object.assign(deleteConfirmTitle.style, {
    fontSize: "16px",
    fontWeight: "900",
    lineHeight: "1.25",
  });

  const deleteConfirmCopy = documentValue.createElement("div");
  deleteConfirmCopy.textContent =
    "This will remove the table from the document. This action can be undone from the Canvas after the delete is committed.";
  Object.assign(deleteConfirmCopy.style, {
    marginTop: "7px",
    fontSize: "12px",
    lineHeight: "1.5",
    color: "#475569",
  });

  const deleteConfirmActions = documentValue.createElement("div");
  Object.assign(deleteConfirmActions.style, {
    marginTop: "18px",
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
  });

  const deleteConfirmCancelButton = documentValue.createElement("button");
  deleteConfirmCancelButton.type = "button";
  deleteConfirmCancelButton.textContent = "Cancel";

  const deleteConfirmDeleteButton = documentValue.createElement("button");
  deleteConfirmDeleteButton.type = "button";
  deleteConfirmDeleteButton.textContent = "Delete table";

  deleteConfirmActions.appendChild(deleteConfirmCancelButton);
  deleteConfirmActions.appendChild(deleteConfirmDeleteButton);
  deleteConfirmCard.appendChild(deleteConfirmTitle);
  deleteConfirmCard.appendChild(deleteConfirmCopy);
  deleteConfirmCard.appendChild(deleteConfirmActions);
  deleteConfirmLayer.appendChild(deleteConfirmCard);
  overlay.appendChild(deleteConfirmLayer);

  chrome.appendChild(handle);
  chrome.appendChild(editButton);
  documentValue.body.appendChild(chrome);
  resizeHandles.forEach(({ button }) => documentValue.body.appendChild(button));
  documentValue.body.appendChild(resizeTooltip);
  documentValue.body.appendChild(editHint);
  documentValue.body.appendChild(overlay);

  const baseButtonStyle = {
    minHeight: "32px",
    padding: "7px 10px",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    background: "#ffffff",
    color: "#0f172a",
    fontSize: "12px",
    fontWeight: "800",
    cursor: "pointer",
  };
  [closeButton, cancelButton, undoButton, redoButton].forEach((button) =>
    Object.assign(button.style, baseButtonStyle)
  );
  Object.assign(undoButton.style, { minWidth: "66px" });
  Object.assign(redoButton.style, { minWidth: "66px" });
  Object.assign(saveButton.style, { ...baseButtonStyle, background: "#1E2A78", borderColor: "#1E2A78", color: "#ffffff" });
  Object.assign(deleteTableButton.style, { ...baseButtonStyle, color: "#b91c1c", borderColor: "#fecaca", background: "#fff7f7" });
  Object.assign(deleteConfirmCancelButton.style, baseButtonStyle);
  Object.assign(deleteConfirmDeleteButton.style, {
    ...baseButtonStyle,
    background: "#b91c1c",
    borderColor: "#b91c1c",
    color: "#ffffff",
  });

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    padding: "8px 10px",
    fontSize: "12px",
    color: "#0f172a",
    background: "#ffffff",
    outline: "none",
  };

  function makeSection(title, hint = "") {
    const section = documentValue.createElement("section");
    Object.assign(section.style, {
      border: "1px solid #e2e8f0",
      borderRadius: "12px",
      padding: "12px",
      background: "#ffffff",
    });
    const heading = documentValue.createElement("div");
    heading.textContent = title;
    Object.assign(heading.style, { fontSize: "12px", fontWeight: "900", color: "#0f172a" });
    section.appendChild(heading);
    if (hint) {
      const copy = documentValue.createElement("div");
      copy.textContent = hint;
      Object.assign(copy.style, { fontSize: "11px", lineHeight: "1.4", color: "#64748b", marginTop: "3px" });
      section.appendChild(copy);
    }
    const body = documentValue.createElement("div");
    Object.assign(body.style, { marginTop: "10px", display: "grid", gap: "9px" });
    section.appendChild(body);
    controlsPane.appendChild(section);
    return body;
  }

  function makeRow(parent, columns = "repeat(2, minmax(0, 1fr))") {
    const row = documentValue.createElement("div");
    Object.assign(row.style, { display: "grid", gridTemplateColumns: columns, gap: "8px", alignItems: "end" });
    parent.appendChild(row);
    return row;
  }

  function makeButton(
    parent,
    label,
    handler,
    { destructive = false, title = "", history = true } = {}
  ) {
    const button = documentValue.createElement("button");
    button.type = "button";
    button.textContent = label;
    if (title) button.title = title;
    Object.assign(button.style, {
      ...baseButtonStyle,
      color: destructive ? "#b91c1c" : "#0f172a",
      borderColor: destructive ? "#fecaca" : "#cbd5e1",
      background: destructive ? "#fff7f7" : "#ffffff",
    });
    button.addEventListener("pointerdown", (event) => event.stopPropagation());
    button.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (history) capturePreviewUndoCheckpoint();
      handler?.();
    });
    parent.appendChild(button);
    return button;
  }

  function makeLabel(text) {
    const label = documentValue.createElement("label");
    Object.assign(label.style, { display: "grid", gap: "5px", fontSize: "11px", fontWeight: "800", color: "#475569" });
    const caption = documentValue.createElement("span");
    caption.textContent = text;
    label.appendChild(caption);
    return label;
  }

  function makeSelect(
    parent,
    labelText,
    options,
    getValue,
    onChange,
    { history = true } = {}
  ) {
    const label = makeLabel(labelText);
    const select = documentValue.createElement("select");
    Object.assign(select.style, inputStyle);
    options.forEach(([value, text]) => {
      const option = documentValue.createElement("option");
      option.value = value;
      option.textContent = text;
      select.appendChild(option);
    });
    const sync = () => { select.value = String(getValue?.() ?? ""); };
    select.addEventListener("change", () => {
      if (history) capturePreviewUndoCheckpoint();
      onChange?.(select.value);
      previewDirty = true;
      refreshPreviewUi();
    });
    label.appendChild(select);
    parent.appendChild(label);
    sync();
    return { element: select, sync };
  }

  function makeNumberField(parent, labelText, {
    getValue,
    onCommit,
    min = null,
    max = null,
    step = 1,
    suffix = "",
    history = true,
  } = {}) {
    const label = makeLabel(labelText);
    const input = documentValue.createElement("input");
    input.type = "number";
    input.step = String(step);
    if (min != null) input.min = String(min);
    if (max != null) input.max = String(max);
    Object.assign(input.style, inputStyle);
    const currentValue = () => getValue?.();
    const sync = () => {
      // Never rewrite the number while the user is typing. This allows a field
      // to be cleared completely and replaced digit-by-digit without the preview
      // clamping/restoring the old number between keystrokes.
      if (documentValue.activeElement === input) return;
      const value = currentValue();
      input.value = value == null || !Number.isFinite(Number(value)) ? "" : String(Math.round(Number(value) * 1000) / 1000);
    };
    const apply = ({ force = false } = {}) => {
      const raw = String(input.value ?? "").trim();
      if (!raw) {
        if (force) sync();
        return;
      }
      let value = Number(raw);
      if (!Number.isFinite(value)) {
        if (force) sync();
        return;
      }
      if (min != null) value = Math.max(Number(min), value);
      if (max != null) value = Math.min(Number(max), value);
      if (history) capturePreviewUndoCheckpoint();
      const committedValue = onCommit?.(value);
      if (Number.isFinite(Number(committedValue))) {
        value = Number(committedValue);
      }
      previewDirty = true;
      refreshPreviewUi();
      if (force) input.value = String(Math.round(value * 1000) / 1000);
    };
    input.addEventListener("input", () => {
      // Important: empty is a valid temporary editing state. Do not restore the
      // old value while the user is deleting and typing a replacement number.
      if (String(input.value).trim() === "") return;
      apply({ force: false });
    });
    input.addEventListener("blur", () => apply({ force: true }));
    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        apply({ force: true });
        input.blur();
      }
    });
    label.appendChild(input);
    if (suffix) {
      const hint = documentValue.createElement("span");
      hint.textContent = suffix;
      Object.assign(hint.style, { fontSize: "10px", fontWeight: "600", color: "#94a3b8" });
      label.appendChild(hint);
    }
    parent.appendChild(label);
    sync();
    return { element: input, sync };
  }

  function safePickerColor(value, fallback = "#ffffff") {
    const raw = String(value || "").trim();
    if (/^#[0-9a-f]{6}$/i.test(raw)) return raw;
    if (/^#[0-9a-f]{3}$/i.test(raw)) {
      return `#${raw.slice(1).split("").map((character) => character + character).join("")}`;
    }
    return fallback;
  }

  function makeColorField(
    parent,
    labelText,
    getValue,
    onChange,
    fallback = "#ffffff",
    { history = true } = {}
  ) {
    const label = makeLabel(labelText);
    const row = documentValue.createElement("div");
    Object.assign(row.style, { display: "grid", gridTemplateColumns: "38px minmax(0,1fr)", gap: "7px" });
    const picker = documentValue.createElement("input");
    picker.type = "color";
    Object.assign(picker.style, { width: "38px", height: "34px", padding: "2px", border: "1px solid #cbd5e1", borderRadius: "8px", background: "#ffffff" });
    const textInput = documentValue.createElement("input");
    textInput.type = "text";
    textInput.placeholder = "#ffffff, rgb(...), rgba(...), hsl(...), transparent";
    Object.assign(textInput.style, inputStyle);
    const sync = () => {
      const value = String(getValue?.() || fallback);
      textInput.value = value;
      picker.value = safePickerColor(value, fallback);
    };
    const commit = (value) => {
      if (history) capturePreviewUndoCheckpoint();
      onChange?.(String(value || "").trim());
      previewDirty = true;
      refreshPreviewUi();
    };
    picker.addEventListener("input", () => { textInput.value = picker.value; commit(picker.value); });
    textInput.addEventListener("change", () => commit(textInput.value));
    textInput.addEventListener("keydown", (event) => {
      if (event.key === "Enter") { event.preventDefault(); commit(textInput.value); textInput.blur(); }
    });
    row.appendChild(picker);
    row.appendChild(textInput);
    label.appendChild(row);
    parent.appendChild(label);
    sync();
    return { picker, textInput, sync };
  }

  function makeCheckbox(parent, labelText, getValue, onChange) {
    const label = documentValue.createElement("label");
    Object.assign(label.style, { display: "flex", alignItems: "center", gap: "7px", fontSize: "11px", fontWeight: "800", color: "#475569", cursor: "pointer" });
    const input = documentValue.createElement("input");
    input.type = "checkbox";
    input.checked = Boolean(getValue?.());
    input.addEventListener("change", () => {
      capturePreviewUndoCheckpoint();
      onChange?.(input.checked);
      previewDirty = true;
      refreshPreviewUi();
    });
    label.appendChild(input);
    label.appendChild(documentValue.createTextNode(labelText));
    parent.appendChild(label);
    return { element: input, sync: () => { input.checked = Boolean(getValue?.()); } };
  }

  function previewGrid() {
    return previewTable ? buildTableGrid(previewTable) : { rows: [], matrix: [], origins: [], columnCount: 0 };
  }

  function previewCell() {
    return previewTable && previewCellId ? findCellById(previewTable, previewCellId) : null;
  }

  function previewRow() {
    const cell = previewCell();
    return previewTable && previewRowId
      ? findRowById(previewTable, previewRowId)
      : cell?.closest?.("tr") || null;
  }

  function previewOrigin() {
    const cell = previewCell();
    return cell ? originForCell(previewGrid(), cell) : null;
  }

  function tableForControls() {
    const root = resolveRoot();
    if (!root || !activeTableId) return null;
    const context = tableApi.getActiveContext?.() || {};
    if (context.tableId === activeTableId && context.table) return context.table;
    return tableById(root, activeTableId);
  }

  function setFromTableAndCell(table, cell = null) {
    if (!table) return;
    activeTableId = table.getAttribute(DOC_TABLE_ID_ATTR) || activeTableId;
    if (cell && table.contains(cell)) {
      activeCellId = cell.getAttribute(DOC_CELL_ID_ATTR) || "";
      activeRowId = cell.closest("tr")?.getAttribute(DOC_ROW_ID_ATTR) || "";
      const grid = buildTableGrid(table);
      activeColumnIndex = originForCell(grid, cell)?.columnIndex ?? cell.cellIndex ?? -1;
    }
  }

  function createLogicalPreviewClone(tableId) {
    const root = resolveRoot();
    if (!root || !tableId) return null;
    const fragments = tableApi.getLogicalFragments?.(tableId) || [];
    if (!fragments.length) return null;
    const holder = documentValue.createElement("div");
    fragments.forEach((fragment) => holder.appendChild(fragment.cloneNode(true)));
    let table = reassembleLogicalTable(holder, tableId, { removeFragments: true, normalize: true });
    if (!table) table = holder.querySelector("table");
    if (!table) return null;
    normalizeCanvasTable(table, {
      source: table.getAttribute(DOC_TABLE_SOURCE_ATTR) || "reopened",
      inferWidths: false,
      preserveTableId: true,
    });
    table.style.maxWidth = "none";
    return table;
  }

  function stripPreviewOnlyState(table) {
    if (!table) return;
    table.removeAttribute("data-doc-table-preview");
    table.querySelectorAll(`th,td,caption,[${DOC_TABLE_TITLE_LINE_ATTR}="true"]`).forEach((cell) => {
      cell.removeAttribute("contenteditable");
      cell.removeAttribute("spellcheck");
      cell.removeAttribute("data-doc-table-preview-selected");
      cell.removeAttribute("data-doc-table-preview-multiselect");
      cell.style.removeProperty("outline");
      cell.style.removeProperty("outline-offset");
      cell.style.removeProperty("cursor");
    });
  }

  function previewComparableHtml(table = previewTable) {
    if (!table) return "";
    try {
      const clone = table.cloneNode(true);
      stripPreviewOnlyState(clone);
      return clone.outerHTML;
    } catch (_) {
      return "";
    }
  }

  function previewHistorySnapshot() {
    if (!previewTable) return null;
    const html = previewComparableHtml(previewTable);
    if (!html) return null;
    return {
      html,
      cellId: previewCellId,
      rowId: previewRowId,
      columnIndex: previewColumnIndex,
      selectedCellIds: Array.from(previewSelectedCellIds),
      selectionAnchorId: previewSelectionAnchorId,
      widthUnit: tableWidthUnit,
      styleTarget: previewStyleTarget,
      supplementalId: previewSupplementalId,
      supplementalKind: previewSupplementalKind,
    };
  }

  function refreshPreviewHistoryButtons() {
    const setState = (button, enabled) => {
      if (!button) return;
      button.disabled = !enabled;
      button.style.opacity = enabled ? "1" : "0.45";
      button.style.cursor = enabled ? "pointer" : "not-allowed";
    };
    setState(undoButton, previewUndoStack.length > 0);
    setState(redoButton, previewRedoStack.length > 0);
  }

  function capturePreviewUndoCheckpoint() {
    if (!editOpen || !previewTable || previewHistoryRestoring) return false;
    const snapshot = previewHistorySnapshot();
    if (!snapshot) return false;
    const last = previewUndoStack[previewUndoStack.length - 1];
    if (last?.html === snapshot.html) return false;
    previewUndoStack.push(snapshot);
    if (previewUndoStack.length > PREVIEW_HISTORY_LIMIT) {
      previewUndoStack.splice(0, previewUndoStack.length - PREVIEW_HISTORY_LIMIT);
    }
    previewRedoStack = [];
    refreshPreviewHistoryButtons();
    return true;
  }

  function restorePreviewHistorySnapshot(snapshot) {
    if (!snapshot?.html) return false;
    const template = documentValue.createElement("template");
    template.innerHTML = String(snapshot.html).trim();
    const nextTable = template.content.querySelector("table");
    if (!nextTable) return false;

    previewHistoryRestoring = true;
    try {
      if (previewTable?.isConnected) previewTable.remove();
      previewTable = nextTable.cloneNode(true);
      previewStage.insertBefore(previewTable, previewResizeTooltip);
      normalizeCanvasTable(previewTable, {
        source: previewTable.getAttribute(DOC_TABLE_SOURCE_ATTR) || "reopened",
        inferWidths: false,
        preserveTableId: true,
      });
      previewTable.style.maxWidth = "none";
      tableWidthUnit = snapshot.widthUnit || currentTableWidthSpec().unit;
      previewStyleTarget = snapshot.styleTarget || "cell";
      previewSupplementalId = snapshot.supplementalId || "";
      previewSupplementalKind = snapshot.supplementalKind || "";

      previewCellId = snapshot.cellId || "";
      previewRowId = snapshot.rowId || "";
      previewColumnIndex = Number.isInteger(snapshot.columnIndex)
        ? snapshot.columnIndex
        : -1;
      previewSelectedCellIds = new Set(snapshot.selectedCellIds || []);
      previewSelectionAnchorId =
        snapshot.selectionAnchorId || previewCellId || "";

      preparePreviewCells();

      const availableIds = new Set(
        Array.from(previewTable.querySelectorAll("th,td"))
          .map((cell) => cell.getAttribute(DOC_CELL_ID_ATTR) || "")
          .filter(Boolean)
      );
      previewSelectedCellIds = new Set(
        Array.from(previewSelectedCellIds).filter((id) => availableIds.has(id))
      );
      if (previewCellId && !availableIds.has(previewCellId)) previewCellId = "";
      if (!previewCellId) {
        const firstCell = previewTable.querySelector("th,td");
        previewCellId = firstCell?.getAttribute(DOC_CELL_ID_ATTR) || "";
      }
      if (!previewSelectedCellIds.size && previewCellId) {
        previewSelectedCellIds = new Set([previewCellId]);
      }
      previewSelectionAnchorId =
        previewSelectionAnchorId && availableIds.has(previewSelectionAnchorId)
          ? previewSelectionAnchorId
          : previewCellId;

      const active = previewCellId
        ? findCellById(previewTable, previewCellId)
        : null;
      if (active) {
        const origin = originForCell(previewGrid(), active);
        previewRowId =
          active.closest("tr")?.getAttribute(DOC_ROW_ID_ATTR) || "";
        previewColumnIndex =
          origin?.columnIndex ?? active.cellIndex ?? -1;
      }

      markPreviewSelection();
      refreshPreviewUi();
      return true;
    } finally {
      previewHistoryRestoring = false;
    }
  }

  function undoPreviewChange() {
    if (!editOpen || !previewUndoStack.length || !previewTable) return false;
    const current = previewHistorySnapshot();
    const target = previewUndoStack.pop();
    if (current) previewRedoStack.push(current);
    const restored = restorePreviewHistorySnapshot(target);
    if (!restored) {
      if (current) previewRedoStack.pop();
      if (target) previewUndoStack.push(target);
    }
    refreshPreviewHistoryButtons();
    return restored;
  }

  function redoPreviewChange() {
    if (!editOpen || !previewRedoStack.length || !previewTable) return false;
    const current = previewHistorySnapshot();
    const target = previewRedoStack.pop();
    if (current) previewUndoStack.push(current);
    const restored = restorePreviewHistorySnapshot(target);
    if (!restored) {
      if (current) previewUndoStack.pop();
      if (target) previewRedoStack.push(target);
    }
    refreshPreviewHistoryButtons();
    return restored;
  }

  function markPreviewSelection() {
    if (!previewTable) return;
    previewTable.querySelectorAll("th,td").forEach((cell) => {
      const id = cell.getAttribute(DOC_CELL_ID_ATTR) || "";
      cell.removeAttribute("data-doc-table-preview-selected");
      cell.removeAttribute("data-doc-table-preview-multiselect");
      cell.style.outline = "";
      cell.style.outlineOffset = "";
      if (id && id === previewCellId) {
        cell.setAttribute("data-doc-table-preview-selected", "true");
        cell.style.outline = "2px solid rgba(30,42,120,.72)";
        cell.style.outlineOffset = "-2px";
      } else if (id && previewSelectedCellIds.has(id)) {
        cell.setAttribute("data-doc-table-preview-multiselect", "true");
        cell.style.outline = "2px solid rgba(37,99,235,.34)";
        cell.style.outlineOffset = "-2px";
      }
    });
  }

  function selectedPreviewCells() {
    if (!previewTable) return [];
    const ids = previewSelectedCellIds.size
      ? Array.from(previewSelectedCellIds)
      : previewCellId
      ? [previewCellId]
      : [];
    return ids.map((id) => findCellById(previewTable, id)).filter(Boolean);
  }

  function previewSupplementalElement(element = null) {
    if (!previewTable) return null;
    if (element && previewTable.contains(element)) {
      const titleLine = element.closest?.(`[${DOC_TABLE_TITLE_LINE_ATTR}="true"]`);
      if (titleLine && previewTable.contains(titleLine)) return titleLine;
      const noteLine = element.closest?.(`[${DOC_TABLE_NOTE_LINE_ATTR}="true"]`);
      if (noteLine && previewTable.contains(noteLine)) return noteLine;
    }
    if (!previewSupplementalId) return null;
    return previewTable.querySelector(
      `[${DOC_TABLE_SUPPLEMENTAL_ID_ATTR}="${escapeCssValue(previewSupplementalId)}"]`
    );
  }

  function setActivePreviewSupplemental(element) {
    const supplemental = previewSupplementalElement(element);
    if (!supplemental) {
      previewSupplementalId = "";
      previewSupplementalKind = "";
      return null;
    }
    previewSupplementalId = supplementalId(
      supplemental,
      supplemental.hasAttribute(DOC_TABLE_TITLE_LINE_ATTR) ? "titleline" : "noteline"
    );
    previewSupplementalKind = supplemental.hasAttribute(DOC_TABLE_TITLE_LINE_ATTR)
      ? "title"
      : "note";
    refreshPreviewUi();
    return supplemental;
  }

  function activePreviewSupplemental() {
    return previewSupplementalElement();
  }

  function applySupplementalStyle(property, value) {
    const supplemental = activePreviewSupplemental();
    if (!supplemental) return false;
    supplemental.style[property] = value;
    previewDirty = true;
    return true;
  }

  function previewCellIsSupplemental(cell) {
    if (!cell) return false;
    return Boolean(
      cell.closest?.(`tr[${DOC_TABLE_NOTE_ROW_ATTR}="true"]`) ||
      cell.closest?.(`tfoot[${DOC_TABLE_NOTE_ATTR}="true"]`)
    );
  }

  function rectangularPreviewSelection(anchorId, targetId) {
    if (!previewTable || !anchorId || !targetId) return new Set();
    const grid = previewGrid();
    const anchor = originForCell(grid, findCellById(previewTable, anchorId));
    const target = originForCell(grid, findCellById(previewTable, targetId));
    if (!anchor || !target) return new Set();

    // Merge never crosses THEAD/TBODY sections, so keep the drag selection in the
    // section where it started. This gives predictable Excel-like highlighting and
    // prevents a title/header/body drag from creating an invalid merge selection.
    if (anchor.row.parentElement?.tagName !== target.row.parentElement?.tagName) {
      return new Set([targetId]);
    }

    const minRow = Math.min(anchor.rowIndex, target.rowIndex);
    const maxRow = Math.max(
      anchor.rowIndex + Math.max(1, anchor.rowSpan) - 1,
      target.rowIndex + Math.max(1, target.rowSpan) - 1
    );
    const minColumn = Math.min(anchor.columnIndex, target.columnIndex);
    const maxColumn = Math.max(
      anchor.columnIndex + Math.max(1, anchor.colSpan) - 1,
      target.columnIndex + Math.max(1, target.colSpan) - 1
    );

    const selected = new Set();
    grid.origins.forEach((origin) => {
      if (origin.row.parentElement?.tagName !== anchor.row.parentElement?.tagName) return;
      const rowEnd = origin.rowIndex + Math.max(1, origin.rowSpan) - 1;
      const columnEnd = origin.columnIndex + Math.max(1, origin.colSpan) - 1;
      const intersects =
        origin.rowIndex <= maxRow &&
        rowEnd >= minRow &&
        origin.columnIndex <= maxColumn &&
        columnEnd >= minColumn;
      if (intersects) {
        const id = origin.cell.getAttribute(DOC_CELL_ID_ATTR) || "";
        if (id) selected.add(id);
      }
    });
    return selected;
  }

  function selectPreviewRectangle(anchorId, targetId) {
    const selected = rectangularPreviewSelection(anchorId, targetId);
    if (!selected.size) return false;
    previewSelectedCellIds = selected;
    previewCellId = targetId;
    const cell = findCellById(previewTable, targetId);
    if (cell) {
      const origin = originForCell(previewGrid(), cell);
      previewRowId = cell.closest("tr")?.getAttribute(DOC_ROW_ID_ATTR) || "";
      previewColumnIndex = origin?.columnIndex ?? cell.cellIndex ?? -1;
    }
    markPreviewSelection();
    refreshPreviewUi();
    return true;
  }

  function updatePreviewSelection(
    cell,
    { toggle = false, extend = false, preserveAnchor = false } = {}
  ) {
    if (!previewTable || !cell || !previewTable.contains(cell)) return;
    if (previewCellIsSupplemental(cell)) return;
    const id = cell.getAttribute(DOC_CELL_ID_ATTR) || "";
    if (!id) return;
    const grid = previewGrid();
    const origin = originForCell(grid, cell);
    previewCellId = id;
    previewRowId = cell.closest("tr")?.getAttribute(DOC_ROW_ID_ATTR) || "";
    previewColumnIndex = origin?.columnIndex ?? cell.cellIndex ?? -1;

    if (extend && previewSelectionAnchorId) {
      previewSelectedCellIds = rectangularPreviewSelection(
        previewSelectionAnchorId,
        id
      );
    } else if (toggle) {
      if (previewSelectedCellIds.has(id)) previewSelectedCellIds.delete(id);
      else previewSelectedCellIds.add(id);
      if (!previewSelectionAnchorId) previewSelectionAnchorId = id;
    } else {
      previewSelectedCellIds = new Set([id]);
      if (!preserveAnchor) previewSelectionAnchorId = id;
    }

    if (!previewSelectedCellIds.size) previewSelectedCellIds.add(id);
    markPreviewSelection();
    refreshPreviewUi();
  }

  function focusPreviewCell(cellId, atEnd = false) {
    if (!previewTable || !cellId) return;
    const cell = findCellById(previewTable, cellId);
    if (!cell) return;
    updatePreviewSelection(cell);
    try {
      cell.focus({ preventScroll: true });
      placeCaretInCell(cell, atEnd);
    } catch (_) {}
  }

  function preparePreviewCells() {
    if (!previewTable) return;
    normalizeCanvasTable(previewTable, { inferWidths: false, preserveTableId: true });
    normalizeTableSupplementalPresentation(previewTable);

    // Canvas caps live document tables to the printable page width. The dialog
    // preview is different: it must faithfully expose an explicitly wider px
    // table and let the synchronized bottom scroller expose horizontal movement.
    previewTable.style.maxWidth = "none";

    previewTable.setAttribute("data-doc-table-preview", "true");
    const caption = tableTitleElement(previewTable);
    if (caption) {
      caption.setAttribute("contenteditable", "false");
      caption.setAttribute("spellcheck", "false");
    }
    previewTable
      .querySelectorAll(`th,td,[${DOC_TABLE_TITLE_LINE_ATTR}="true"]`)
      .forEach((cell) => {
        cell.setAttribute("contenteditable", "true");
        cell.setAttribute("spellcheck", "false");
      });
    markPreviewSelection();
    refreshPreviewHorizontalScroll();
  }

  function currentTableWidthSpec() {
    if (!previewTable) return { value: 100, unit: "%" };
    const raw = String(previewTable.style.width || "").trim();
    if (raw.endsWith("%")) return { value: asFiniteNumber(raw.slice(0, -1), 100), unit: "%" };
    if (raw.endsWith("px")) return { value: asFiniteNumber(raw.slice(0, -2), previewTable.getBoundingClientRect().width || 600), unit: "px" };
    return { value: previewTable.getBoundingClientRect().width || 600, unit: "px" };
  }

  function currentTableHeight() {
    if (!previewTable) return null;
    const rows = Array.from(previewTable.rows || []);
    if (!rows.length) return 0;
    return rows.reduce(
      (total, row) =>
        total +
        Math.max(
          MIN_ROW_HEIGHT_PX,
          row.getBoundingClientRect?.().height ||
            asFiniteNumber(String(row.style.height || "").replace(/px$/i, ""), MIN_ROW_HEIGHT_PX)
        ),
      0
    );
  }

  function selectedColumnWidth() {
    if (!previewTable || previewColumnIndex < 0) return null;
    return readColumnWidthsPx(previewTable)[previewColumnIndex] ?? null;
  }

  function selectedRowHeight() {
    const row = previewRow();
    return row?.getBoundingClientRect?.().height || asFiniteNumber(String(row?.style?.height || "").replace(/px$/, ""), MIN_ROW_HEIGHT_PX);
  }

  function targetCells(target = previewStyleTarget || "cell") {
    if (!previewTable) return [];
    if (target === "selection") return selectedPreviewCells();
    if (target === "header") return Array.from(previewTable.querySelectorAll(":scope > thead th"));
    if (target === "body") return Array.from(previewTable.querySelectorAll(":scope > tbody td"));
    if (target === "row") return Array.from(previewRow()?.cells || []);
    if (target === "column") {
      if (previewColumnIndex < 0) return [];
      const grid = previewGrid();
      const seen = new Set();
      const cells = [];
      grid.rows.forEach((_, rowIndex) => {
        const origin = grid.matrix[rowIndex]?.[previewColumnIndex];
        if (origin?.cell && !seen.has(origin.cell)) { seen.add(origin.cell); cells.push(origin.cell); }
      });
      return cells;
    }
    if (target === "cell") return previewCell() ? [previewCell()] : [];
    return Array.from(previewTable.querySelectorAll("th,td"));
  }

  function sampleTargetCell(target = previewStyleTarget || "cell") {
    return targetCells(target)[0] || null;
  }

  function applyTargetStyle(property, value) {
    targetCells().forEach((cell) => { cell.style[property] = value; });
    previewDirty = true;
  }

  function applyBodyAlternating(enabled, color = null) {
    if (!previewTable) return;
    const chosen = color || previewTable.getAttribute("data-doc-table-alt-color") || "#f8fafc";
    const bodyColor = previewTable.getAttribute("data-doc-table-body-color") ||
      previewTable.querySelector(":scope > tbody td")?.style.backgroundColor || "#ffffff";
    previewTable.setAttribute("data-doc-table-alt-rows", enabled ? "true" : "false");
    previewTable.setAttribute("data-doc-table-alt-color", chosen);
    Array.from(previewTable.querySelectorAll(":scope > tbody > tr")).forEach((row, index) => {
      Array.from(row.cells || []).forEach((cell) => {
        cell.style.backgroundColor = enabled && index % 2 === 1 ? chosen : bodyColor;
      });
    });
  }

  function ensurePreviewBorderMetadata() {
    if (!previewTable) return;
    const presentation = tableBorderPresentation(previewTable);
    if (!previewTable.hasAttribute(DOC_TABLE_BORDER_WIDTH_ATTR)) {
      previewTable.setAttribute(
        DOC_TABLE_BORDER_WIDTH_ATTR,
        String(presentation.width)
      );
    }
    if (!previewTable.hasAttribute(DOC_TABLE_BORDER_STYLE_ATTR)) {
      previewTable.setAttribute(
        DOC_TABLE_BORDER_STYLE_ATTR,
        presentation.style || "solid"
      );
    }
    if (!previewTable.hasAttribute(DOC_TABLE_BORDER_COLOR_ATTR)) {
      previewTable.setAttribute(
        DOC_TABLE_BORDER_COLOR_ATTR,
        presentation.color || "#cbd5e1"
      );
    }
  }

  let selectedBorderStyleControl = null;
  let selectedBorderWidthControl = null;
  let selectedBorderColorControl = null;
  let selectedNumberFormatControl = null;

  function applyBorders() {
    if (!previewTable) return;
    ensurePreviewBorderMetadata();
    applyTableGridPresentation(previewTable);
    applyCellBorderOverrides(previewTable);
  }

  function selectedBorderStyleToken(style = null, width = null, color = null) {
    const chosenStyle = String(
      style ||
        previewBorderToolStyle ||
        previewTable?.getAttribute(DOC_TABLE_BORDER_STYLE_ATTR) ||
        "solid"
    ).trim();
    const chosenWidth = Math.max(
      0,
      asFiniteNumber(
        width ?? previewBorderToolWidth,
        Math.max(
          1,
          asFiniteNumber(previewTable?.getAttribute(DOC_TABLE_BORDER_WIDTH_ATTR), 1)
        )
      )
    );
    const chosenColor = String(
      color ||
        previewBorderToolColor ||
        previewTable?.getAttribute(DOC_TABLE_BORDER_COLOR_ATTR) ||
        "#0f172a"
    ).trim() || "#0f172a";
    if (chosenStyle === "none" || chosenWidth <= 0) return "none";
    return `${chosenWidth}px ${chosenStyle} ${chosenColor}`;
  }

  function setSelectedBorderSide(side, { force = null, token = null } = {}) {
    const attributeName = cellBorderAttribute(side);
    if (!attributeName) return false;
    const cells = selectedPreviewCells();
    if (!cells.length) return false;

    // Toggle the explicit override, not the inherited/global grid. This makes the
    // first click always APPLY the chosen border style instead of unexpectedly
    // turning off a border that only came from the whole-table grid.
    const currentlyEnabled = cells.every((cell) => {
      if (!cell.hasAttribute(attributeName)) return false;
      const current = String(cell.getAttribute(attributeName) || "").trim();
      return Boolean(current && current !== "none");
    });

    const enable = force == null ? !currentlyEnabled : Boolean(force);
    const nextToken = enable ? token || selectedBorderStyleToken() : "none";
    cells.forEach((cell) => cell.setAttribute(attributeName, nextToken));
    applyCellBorderOverrides(previewTable);
    previewDirty = true;
    return true;
  }

  function setSelectedAllBorderSides(enabled) {
    ["top", "right", "bottom", "left"].forEach((side) =>
      setSelectedBorderSide(side, { force: enabled })
    );
  }

  function setSelectedDoubleBottom() {
    const width = Math.max(3, asFiniteNumber(previewBorderToolWidth, 3));
    const color = String(previewBorderToolColor || "#0f172a").trim() || "#0f172a";
    setSelectedBorderSide("bottom", {
      force: true,
      token: `${width}px double ${color}`,
    });
  }

  function restyleSelectedExplicitBorders() {
    if (!previewTable) return false;
    const cells = selectedPreviewCells();
    if (!cells.length) return false;
    const token = selectedBorderStyleToken();
    let changed = false;
    ["top", "right", "bottom", "left"].forEach((side) => {
      const attributeName = cellBorderAttribute(side);
      cells.forEach((cell) => {
        if (!cell.hasAttribute(attributeName)) return;
        const current = String(cell.getAttribute(attributeName) || "").trim();
        if (!current || current === "none") return;
        cell.setAttribute(attributeName, token);
        changed = true;
      });
    });
    if (changed) {
      applyCellBorderOverrides(previewTable);
      previewDirty = true;
    }
    return changed;
  }

  function selectedBorderSideEnabled(side) {
    const attributeName = cellBorderAttribute(side);
    const cells = selectedPreviewCells();
    if (!attributeName || !cells.length) return false;
    return cells.every((cell) => {
      if (!cell.hasAttribute(attributeName)) return false;
      const token = String(cell.getAttribute(attributeName) || "").trim();
      return Boolean(token && token !== "none");
    });
  }

  function applySelectedNumberFormat(format) {
    const cells = selectedPreviewCells();
    if (!cells.length) return false;
    let changed = false;
    cells.forEach((cell) => {
      if (previewCellIsSupplemental(cell)) return;
      changed = formatNumericCell(cell, format) || changed;
    });
    if (changed) {
      previewDirty = true;
      refreshPreviewUi();
    }
    return changed;
  }

  function selectedNumberFormat() {
    const cells = selectedPreviewCells();
    if (!cells.length) return "general";
    const values = new Set(
      cells.map(
        (cell) =>
          String(cell.getAttribute(DOC_CELL_NUMBER_FORMAT_ATTR) || "general")
            .trim()
            .toLowerCase() || "general"
      )
    );
    return values.size === 1 ? Array.from(values)[0] : "general";
  }

  function applyCellPadding() {
    if (!previewTable) return;
    const vertical = Math.max(
      0,
      asFiniteNumber(previewTable.getAttribute(DOC_TABLE_PADDING_Y_ATTR), 7)
    );
    const horizontal = Math.max(
      0,
      asFiniteNumber(previewTable.getAttribute(DOC_TABLE_PADDING_X_ATTR), 9)
    );
    previewTable.querySelectorAll("th,td").forEach((cell) => {
      cell.style.setProperty(
        "padding",
        `${vertical}px ${horizontal}px`,
        "important"
      );
    });
  }

  function applyTableLineHeight(value = null) {
    if (!previewTable) return null;
    const lineHeight = clamp(
      value == null
        ? asFiniteNumber(previewTable.getAttribute(DOC_TABLE_LINE_HEIGHT_ATTR), 1.3)
        : asFiniteNumber(value, 1.3),
      0.8,
      3
    );
    previewTable.setAttribute(DOC_TABLE_LINE_HEIGHT_ATTR, String(lineHeight));
    previewTable.style.lineHeight = String(lineHeight);
    previewTable.querySelectorAll("th,td").forEach((cell) => {
      cell.style.setProperty("line-height", String(lineHeight), "important");
    });
    return lineHeight;
  }

  function resizeSelectedColumnTo(width) {
    if (!previewTable || previewColumnIndex < 0) return;
    const widths = readColumnWidthsPx(previewTable);
    if (previewColumnIndex >= widths.length) return;
    widths[previewColumnIndex] = Math.max(MIN_COLUMN_WIDTH_PX, width);
    applyColumnWidths(previewTable, widths, { unit: "px" });
    previewDirty = true;
  }

  function selectedCellCoordinate() {
    const origin = previewOrigin();
    if (!origin) return "No cell selected";
    const columnNumber = origin.columnIndex + 1;
    const rowNumber = origin.rowIndex + 1;
    let letters = "";
    let number = columnNumber;
    while (number > 0) {
      number -= 1;
      letters = String.fromCharCode(65 + (number % 26)) + letters;
      number = Math.floor(number / 26);
    }
    return `Cell ${letters}${rowNumber} · Row ${rowNumber} · Column ${columnNumber}`;
  }

  const structureSection = makeSection(
    "Rows, columns & cells",
    "Click one cell, Ctrl/Cmd-click to add/remove cells, Shift-click to extend, or drag across cells for an Excel-like rectangular selection before Merge."
  );
  const structureButtons = makeRow(structureSection, "repeat(2, minmax(0, 1fr))");
  makeButton(structureButtons, "Row above", () => {
    if (!previewTable || !previewCell()) return;
    const result = addTableRow(previewTable, { where: "above", rowId: previewRowId, cellId: previewCellId, focusColumn: previewColumnIndex });
    preparePreviewCells(); previewDirty = previewDirty || result.changed; focusPreviewCell(result.focusCellId || previewCellId);
  });
  makeButton(structureButtons, "Row below", () => {
    if (!previewTable || !previewCell()) return;
    const result = addTableRow(previewTable, { where: "below", rowId: previewRowId, cellId: previewCellId, focusColumn: previewColumnIndex });
    preparePreviewCells(); previewDirty = previewDirty || result.changed; focusPreviewCell(result.focusCellId || previewCellId);
  });
  makeButton(structureButtons, "Column left", () => {
    if (!previewTable || !previewCell()) return;
    const result = addTableColumn(previewTable, { where: "left", cellId: previewCellId, columnIndex: previewColumnIndex });
    preparePreviewCells(); previewDirty = previewDirty || result.changed; focusPreviewCell(result.focusCellId || previewCellId);
  });
  makeButton(structureButtons, "Column right", () => {
    if (!previewTable || !previewCell()) return;
    const result = addTableColumn(previewTable, { where: "right", cellId: previewCellId, columnIndex: previewColumnIndex });
    preparePreviewCells(); previewDirty = previewDirty || result.changed; focusPreviewCell(result.focusCellId || previewCellId);
  });
  makeButton(structureButtons, "Delete row", () => {
    if (!previewTable || !previewCell()) return;
    const result = deleteTableRow(previewTable, { rowId: previewRowId, cellId: previewCellId });
    preparePreviewCells(); previewDirty = previewDirty || result.changed; focusPreviewCell(result.focusCellId || "");
  }, { destructive: true });
  makeButton(structureButtons, "Delete column", () => {
    if (!previewTable || !previewCell()) return;
    const result = deleteTableColumn(previewTable, { cellId: previewCellId, columnIndex: previewColumnIndex });
    preparePreviewCells(); previewDirty = previewDirty || result.changed; focusPreviewCell(result.focusCellId || "");
  }, { destructive: true });
  makeButton(structureButtons, "Split columns", () => {
    if (!previewTable || !previewCell()) return;
    const result = splitTableCell(previewTable, { cellId: previewCellId, direction: "columns" });
    preparePreviewCells(); previewDirty = previewDirty || result.changed; focusPreviewCell(result.focusCellId || previewCellId);
  });
  makeButton(structureButtons, "Split rows", () => {
    if (!previewTable || !previewCell()) return;
    const result = splitTableCell(previewTable, { cellId: previewCellId, direction: "rows" });
    preparePreviewCells(); previewDirty = previewDirty || result.changed; focusPreviewCell(result.focusCellId || previewCellId);
  });
  makeButton(structureButtons, "Merge selected", () => {
    if (!previewTable) return;
    const result = mergeTableCells(previewTable, { cellIds: Array.from(previewSelectedCellIds) });
    preparePreviewCells(); previewDirty = previewDirty || result.changed; focusPreviewCell(result.focusCellId || previewCellId);
  });
  makeButton(structureButtons, "Toggle header", () => {
    if (!previewTable) return;
    previewDirty = toggleTableHeaderRow(previewTable) || previewDirty;
    preparePreviewCells(); refreshPreviewUi();
  });
  makeButton(structureButtons, "Distribute columns", () => {
    if (!previewTable) return;
    previewDirty = distributeTableColumns(previewTable) || previewDirty;
    refreshPreviewUi();
  });

  const titleSection = makeSection(
    "Top titles and bottom notes",
    "Add as many independent lines as needed. Click a title/note line in the preview to make it active, then use the formatting controls below. These remain draft-only until Save changes."
  );
  const titleButtons = makeRow(titleSection, "repeat(2, minmax(0,1fr))");
  makeButton(titleButtons, "Add top title line", () => {
    if (!previewTable) return;
    const line = appendTableTitleLine(previewTable);
    preparePreviewCells();
    previewDirty = true;
    setActivePreviewSupplemental(line);
    try {
      line?.focus?.({ preventScroll: true });
      placeCaretInCell(line, true);
    } catch (_) {}
    refreshPreviewUi();
  });
  makeButton(titleButtons, "Remove active top line", () => {
    if (!previewTable) return;
    const id = previewSupplementalKind === "title" ? previewSupplementalId : "";
    previewDirty = removeTableTitleLine(previewTable, id) || previewDirty;
    previewSupplementalId = "";
    previewSupplementalKind = "";
    preparePreviewCells();
    refreshPreviewUi();
  }, { destructive: true });

  const noteButtons = makeRow(titleSection, "repeat(2, minmax(0,1fr))");
  makeButton(noteButtons, "Add bottom note line", () => {
    if (!previewTable) return;
    const note = appendTableNoteLine(previewTable);
    preparePreviewCells();
    previewDirty = true;
    setActivePreviewSupplemental(note);
    try {
      note?.focus?.({ preventScroll: true });
      placeCaretInCell(note, true);
    } catch (_) {}
    refreshPreviewUi();
  });
  makeButton(noteButtons, "Remove active bottom line", () => {
    if (!previewTable) return;
    const id = previewSupplementalKind === "note" ? previewSupplementalId : "";
    previewDirty = removeTableNoteLine(previewTable, id) || previewDirty;
    previewSupplementalId = "";
    previewSupplementalKind = "";
    preparePreviewCells();
    refreshPreviewUi();
  }, { destructive: true });

  const supplementalStatus = documentValue.createElement("div");
  Object.assign(supplementalStatus.style, {
    padding: "7px 9px",
    borderRadius: "8px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    color: "#64748b",
    fontSize: "10px",
    fontWeight: "700",
  });
  supplementalStatus.textContent = "Click a top title or bottom note line to format it.";
  titleSection.appendChild(supplementalStatus);

  const supplementalGrid = makeRow(titleSection, "repeat(2, minmax(0,1fr))");
  const supplementalAlignControl = makeSelect(
    supplementalGrid,
    "Active line alignment",
    [["left", "Left"], ["center", "Center"], ["right", "Right"]],
    () => activePreviewSupplemental()?.style.textAlign ||
      (previewSupplementalKind === "title" ? "center" : "left"),
    (value) => applySupplementalStyle("textAlign", value)
  );
  const supplementalFontSizeControl = makeNumberField(
    supplementalGrid,
    "Active line font size (px)",
    {
      getValue: () => {
        const line = activePreviewSupplemental();
        if (!line) return previewSupplementalKind === "title" ? 14 : 12;
        const raw = String(line.style.fontSize || "").replace(/px$/i, "");
        if (raw && Number.isFinite(Number(raw))) return Number(raw);
        return asFiniteNumber(view.getComputedStyle(line).fontSize.replace(/px$/i, ""),
          previewSupplementalKind === "title" ? 14 : 12);
      },
      min: 3,
      max: 96,
      step: 1,
      onCommit: (value) => {
        applySupplementalStyle("fontSize", `${value}px`);
        return value;
      },
    }
  );
  const supplementalStyleRow = makeRow(titleSection, "repeat(3, minmax(0,1fr))");
  const supplementalBoldControl = makeCheckbox(
    supplementalStyleRow,
    "Bold",
    () => {
      const line = activePreviewSupplemental();
      return Boolean(line && asFiniteNumber(line.style.fontWeight || view.getComputedStyle(line).fontWeight, 400) >= 600);
    },
    (checked) => applySupplementalStyle("fontWeight", checked ? "700" : "400")
  );
  const supplementalItalicControl = makeCheckbox(
    supplementalStyleRow,
    "Italic",
    () => {
      const line = activePreviewSupplemental();
      return Boolean(line && String(line.style.fontStyle || view.getComputedStyle(line).fontStyle).toLowerCase() === "italic");
    },
    (checked) => applySupplementalStyle("fontStyle", checked ? "italic" : "normal")
  );
  const supplementalUnderlineControl = makeCheckbox(
    supplementalStyleRow,
    "Underline",
    () => {
      const line = activePreviewSupplemental();
      const decoration = line
        ? String(line.style.textDecoration || view.getComputedStyle(line).textDecorationLine || "")
        : "";
      return decoration.includes("underline");
    },
    (checked) => applySupplementalStyle("textDecoration", checked ? "underline" : "none")
  );

  const sizeSection = makeSection(
    "Size and placement",
    `Every numeric box supports manual delete-and-type plus browser up/down arrows. Safe minimums keep the table selectable: ${MIN_TABLE_WIDTH_PX}px table width, ${MIN_COLUMN_WIDTH_PX}px columns, ${MIN_ROW_HEIGHT_PX}px rows.`
  );
  const presetRow = makeRow(sizeSection, "repeat(4, minmax(0,1fr))");
  [[60, "Small"], [75, "Medium"], [90, "Wide"], [100, "Full"]].forEach(([width, label]) => {
    makeButton(presetRow, label, () => {
      if (!previewTable) return;
      tableWidthUnit = "%";
      resizeTableWidth(previewTable, { width, unit: "%" });
      previewDirty = true;
      widthUnitControl?.sync?.(); widthValueControl?.sync?.();
      refreshPreviewUi();
    });
  });
  const sizeGrid = makeRow(sizeSection, "repeat(2, minmax(0,1fr))");
  let widthUnitControl = null;
  let widthValueControl = null;
  widthUnitControl = makeSelect(sizeGrid, "Width unit", [["%", "% of page"], ["px", "Pixels"]], () => tableWidthUnit, (value) => {
    tableWidthUnit = value;
    if (widthValueControl?.element) {
      widthValueControl.element.min = value === "%" ? "10" : String(MIN_TABLE_WIDTH_PX);
      widthValueControl.element.max = value === "%" ? "100" : "";
    }
    const spec = currentTableWidthSpec();
    if (value === "%" && spec.unit === "px") {
      resizeTableWidth(previewTable, { width: 100, unit: "%" });
    } else if (value === "px" && spec.unit === "%") {
      resizeTableWidth(previewTable, { width: previewTable.getBoundingClientRect().width || 600, unit: "px" });
      previewTable.style.maxWidth = "none";
    }
    widthValueControl?.sync?.();
  });
  widthValueControl = makeNumberField(sizeGrid, "Table width", {
    getValue: () => currentTableWidthSpec().value,
    min: 10,
    max: null,
    step: 1,
    onCommit: (value) => {
      if (!previewTable) return;
      const normalizedValue = tableWidthUnit === "%"
        ? clamp(value, 10, 100)
        : Math.max(MIN_TABLE_WIDTH_PX, value);
      resizeTableWidth(previewTable, { width: normalizedValue, unit: tableWidthUnit });
      if (tableWidthUnit === "px") previewTable.style.maxWidth = "none";
      return normalizedValue;
    },
  });
  if (widthValueControl?.element) {
    widthValueControl.element.min = tableWidthUnit === "%" ? "10" : String(MIN_TABLE_WIDTH_PX);
    widthValueControl.element.max = tableWidthUnit === "%" ? "100" : "";
  }
  const alignmentControl = makeSelect(sizeGrid, "Table alignment", [["left", "Left"], ["center", "Center"], ["right", "Right"]], () => previewTable?.getAttribute(DOC_TABLE_ALIGNMENT_ATTR) || "center", (value) => {
    if (previewTable) setTableAlignment(previewTable, value);
  });
  const tableHeightControl = makeNumberField(sizeGrid, "Table height (px)", {
    getValue: currentTableHeight,
    min: MIN_ROW_HEIGHT_PX,
    step: 1,
    onCommit: (value) => {
      if (!previewTable) return null;
      resizeTableHeight(previewTable, { height: value });
      return currentTableHeight();
    },
  });
  const columnWidthControl = makeNumberField(sizeGrid, "Selected column width (px)", {
    getValue: selectedColumnWidth,
    min: MIN_COLUMN_WIDTH_PX,
    step: 1,
    onCommit: resizeSelectedColumnTo,
  });
  const rowHeightControl = makeNumberField(sizeGrid, "Selected row height (px)", {
    getValue: selectedRowHeight,
    min: MIN_ROW_HEIGHT_PX,
    step: 1,
    onCommit: (value) => {
      if (!previewTable || !previewRow()) return;
      resizeTableRow(previewTable, { rowId: previewRowId, height: value });
    },
  });

  const baseStyleSection = makeSection("Table appearance", "Whole-table defaults. You can override a selected row, column, or cell in the next section.");
  const baseStyleGrid = makeRow(baseStyleSection, "repeat(2, minmax(0,1fr))");
  const tableFontSizeControl = makeNumberField(baseStyleGrid, "Font size (px)", {
    getValue: () => asFiniteNumber(String(sampleTargetCell("table")?.style.fontSize || view.getComputedStyle(sampleTargetCell("table") || documentValue.body).fontSize).replace(/px$/, ""), 12),
    min: 6,
    max: 72,
    step: 1,
    onCommit: (value) => { if (previewTable) previewTable.querySelectorAll("th,td").forEach((cell) => { cell.style.fontSize = `${value}px`; }); },
  });
  const lineHeightControl = makeNumberField(baseStyleGrid, "Line height", {
    getValue: () =>
      asFiniteNumber(
        previewTable?.getAttribute(DOC_TABLE_LINE_HEIGHT_ATTR) ||
          previewTable?.style.lineHeight,
        1.3
      ),
    min: 0.8,
    max: 3,
    step: 0.05,
    onCommit: (value) => applyTableLineHeight(value),
  });
  const fontFamilyControl = makeSelect(baseStyleGrid, "Font", [
    ["Arial, sans-serif", "Arial"],
    ["Georgia, serif", "Georgia"],
    ['Inter, system-ui, sans-serif', "Inter / System"],
    ['"Times New Roman", serif', "Times New Roman"],
    ['Verdana, sans-serif', "Verdana"],
  ], () => previewTable?.style.fontFamily || 'Inter, system-ui, sans-serif', (value) => {
    if (previewTable) previewTable.style.fontFamily = value;
  });
  const headerBgControl = makeColorField(baseStyleGrid, "Header background", () => previewTable?.querySelector("thead th")?.style.backgroundColor || "#f1f5f9", (value) => {
    previewTable?.querySelectorAll("thead th").forEach((cell) => { cell.style.backgroundColor = value; });
  }, "#f1f5f9");
  const headerTextControl = makeColorField(baseStyleGrid, "Header text", () => previewTable?.querySelector("thead th")?.style.color || "#0f172a", (value) => {
    previewTable?.querySelectorAll("thead th").forEach((cell) => { cell.style.color = value; });
  }, "#0f172a");
  const bodyBgControl = makeColorField(baseStyleGrid, "Body background", () => previewTable?.querySelector("tbody td")?.style.backgroundColor || "#ffffff", (value) => {
    if (!previewTable) return;
    previewTable.setAttribute("data-doc-table-body-color", value);
    previewTable.querySelectorAll("tbody td").forEach((cell) => { cell.style.backgroundColor = value; });
    applyBodyAlternating(previewTable.getAttribute("data-doc-table-alt-rows") === "true");
  }, "#ffffff");
  const bodyTextControl = makeColorField(baseStyleGrid, "Body text", () => previewTable?.querySelector("tbody td")?.style.color || previewTable?.style.color || "#111827", (value) => {
    previewTable?.querySelectorAll("tbody td").forEach((cell) => { cell.style.color = value; });
  }, "#111827");
  const tableBgControl = makeColorField(baseStyleGrid, "Table background", () => previewTable?.style.backgroundColor || "#ffffff", (value) => {
    if (previewTable) previewTable.style.backgroundColor = value;
  }, "#ffffff");
  const altColorControl = makeColorField(baseStyleGrid, "Alternating row color", () => previewTable?.getAttribute("data-doc-table-alt-color") || "#f8fafc", (value) => {
    if (!previewTable) return;
    previewTable.setAttribute("data-doc-table-alt-color", value);
    applyBodyAlternating(previewTable.getAttribute("data-doc-table-alt-rows") === "true", value);
  }, "#f8fafc");
  const headerRowControl = makeCheckbox(
    baseStyleSection,
    "Show header row",
    () => Boolean(previewTable && findDirectChild(previewTable, "THEAD")),
    (checked) => {
      if (!previewTable) return;
      const hasHeader = Boolean(findDirectChild(previewTable, "THEAD"));
      if (checked !== hasHeader) {
        toggleTableHeaderRow(previewTable);
        preparePreviewCells();
      }
    }
  );
  const alternatingControl = makeCheckbox(baseStyleSection, "Alternate body row color", () => previewTable?.getAttribute("data-doc-table-alt-rows") === "true", (checked) => applyBodyAlternating(checked));

  const borderSection = makeSection(
    "Borders and spacing",
    "Control the outside border and horizontal/vertical grid lines independently. Turn all three off for a borderless table while keeping the table structure intact."
  );
  const borderGrid = makeRow(borderSection, "repeat(2, minmax(0,1fr))");
  const borderWidthControl = makeNumberField(borderGrid, "Border width (px)", {
    getValue: () => asFiniteNumber(previewTable?.getAttribute(DOC_TABLE_BORDER_WIDTH_ATTR), 1),
    min: 0,
    max: 12,
    step: 1,
    onCommit: (value) => {
      if (!previewTable) return value;
      previewTable.setAttribute(DOC_TABLE_BORDER_WIDTH_ATTR, String(value));
      applyBorders();
      return value;
    },
  });
  const borderStyleControl = makeSelect(borderGrid, "Border style", [["solid", "Solid"], ["dashed", "Dashed"], ["dotted", "Dotted"], ["double", "Double"], ["none", "None"]], () => previewTable?.getAttribute(DOC_TABLE_BORDER_STYLE_ATTR) || "solid", (value) => {
    if (previewTable) { previewTable.setAttribute(DOC_TABLE_BORDER_STYLE_ATTR, value); applyBorders(); }
  });
  const borderColorControl = makeColorField(borderGrid, "Border color", () => previewTable?.getAttribute(DOC_TABLE_BORDER_COLOR_ATTR) || "#cbd5e1", (value) => {
    if (previewTable) { previewTable.setAttribute(DOC_TABLE_BORDER_COLOR_ATTR, value); applyBorders(); }
  }, "#cbd5e1");

  const gridToggleRow = makeRow(borderSection, "repeat(2, minmax(0,1fr))");
  const completeGridControl = makeCheckbox(
    gridToggleRow,
    "Show complete grid",
    () =>
      booleanTableSetting(previewTable, DOC_TABLE_OUTER_BORDER_ATTR, true) &&
      booleanTableSetting(previewTable, DOC_TABLE_ROW_LINES_ATTR, true) &&
      booleanTableSetting(previewTable, DOC_TABLE_COLUMN_LINES_ATTR, true),
    (checked) => {
      if (!previewTable) return;
      previewTable.setAttribute(DOC_TABLE_OUTER_BORDER_ATTR, checked ? "true" : "false");
      previewTable.setAttribute(DOC_TABLE_ROW_LINES_ATTR, checked ? "true" : "false");
      previewTable.setAttribute(DOC_TABLE_COLUMN_LINES_ATTR, checked ? "true" : "false");
      applyBorders();
    }
  );
  const outerBorderControl = makeCheckbox(
    gridToggleRow,
    "Show outer border",
    () => booleanTableSetting(previewTable, DOC_TABLE_OUTER_BORDER_ATTR, true),
    (checked) => {
      if (!previewTable) return;
      previewTable.setAttribute(DOC_TABLE_OUTER_BORDER_ATTR, checked ? "true" : "false");
      applyBorders();
    }
  );
  const rowLinesControl = makeCheckbox(
    gridToggleRow,
    "Show row grid lines",
    () => booleanTableSetting(previewTable, DOC_TABLE_ROW_LINES_ATTR, true),
    (checked) => {
      if (!previewTable) return;
      previewTable.setAttribute(DOC_TABLE_ROW_LINES_ATTR, checked ? "true" : "false");
      applyBorders();
    }
  );
  const columnLinesControl = makeCheckbox(
    gridToggleRow,
    "Show column grid lines",
    () => booleanTableSetting(previewTable, DOC_TABLE_COLUMN_LINES_ATTR, true),
    (checked) => {
      if (!previewTable) return;
      previewTable.setAttribute(DOC_TABLE_COLUMN_LINES_ATTR, checked ? "true" : "false");
      applyBorders();
    }
  );

  // Two local refinements only. The existing four Show controls above remain
  // whole-table controls. These selectors affect the row/column containing the
  // active preview cell and can either follow the table-wide choice, force its
  // surrounding internal grid on, or force it off.
  const localGridControlRow = makeRow(borderSection, "repeat(2, minmax(0,1fr))");
  const selectedRowGridControl = makeSelect(
    localGridControlRow,
    "Selected row grid lines",
    [
      ["inherit", "Follow table"],
      ["show", "Show"],
      ["hide", "Hide"],
    ],
    () =>
      localGridOverride(
        previewRow(),
        DOC_TABLE_SELECTED_ROW_GRID_ATTR
      ),
    (value) => {
      const row = previewRow();
      if (!previewTable || !row) return;
      if (value === "show" || value === "hide") {
        row.setAttribute(DOC_TABLE_SELECTED_ROW_GRID_ATTR, value);
      } else {
        row.removeAttribute(DOC_TABLE_SELECTED_ROW_GRID_ATTR);
      }
      applyBorders();
    }
  );
  const selectedColumnGridControl = makeSelect(
    localGridControlRow,
    "Selected column grid lines",
    [
      ["inherit", "Follow table"],
      ["show", "Show"],
      ["hide", "Hide"],
    ],
    () => {
      if (!previewTable || previewColumnIndex < 0) return "inherit";
      const colgroup = ensureTableColgroup(
        previewTable,
        getTableColumnCount(previewTable),
        { inferWidths: false }
      );
      const columns = Array.from(colgroup?.children || []).filter(
        (column) => column.tagName === "COL"
      );
      return localGridOverride(
        columns[previewColumnIndex],
        DOC_TABLE_SELECTED_COLUMN_GRID_ATTR
      );
    },
    (value) => {
      if (!previewTable || previewColumnIndex < 0) return;
      const colgroup = ensureTableColgroup(
        previewTable,
        getTableColumnCount(previewTable),
        { inferWidths: false }
      );
      const columns = Array.from(colgroup?.children || []).filter(
        (column) => column.tagName === "COL"
      );
      const column = columns[previewColumnIndex] || null;
      if (!column) return;
      if (value === "show" || value === "hide") {
        column.setAttribute(DOC_TABLE_SELECTED_COLUMN_GRID_ATTR, value);
      } else {
        column.removeAttribute(DOC_TABLE_SELECTED_COLUMN_GRID_ATTR);
      }
      applyBorders();
    }
  );

  const paddingXControl = makeNumberField(borderGrid, "Horizontal cell padding", {
    getValue: () => asFiniteNumber(previewTable?.getAttribute(DOC_TABLE_PADDING_X_ATTR), 9), min: 0, max: 60, step: 1,
    onCommit: (value) => {
      if (!previewTable) return value;
      previewTable.setAttribute(DOC_TABLE_PADDING_X_ATTR, String(value));
      applyCellPadding();
      return value;
    },
  });
  const paddingYControl = makeNumberField(borderGrid, "Vertical cell padding", {
    getValue: () => asFiniteNumber(previewTable?.getAttribute(DOC_TABLE_PADDING_Y_ATTR), 7), min: 0, max: 60, step: 1,
    onCommit: (value) => {
      if (!previewTable) return value;
      previewTable.setAttribute(DOC_TABLE_PADDING_Y_ATTR, String(value));
      applyCellPadding();
      return value;
    },
  });

  const selectionSection = makeSection("Selected-area styling", "Apply a style to the entire table, header, body, selected row, selected column, or selected cell.");
  const selectionGrid = makeRow(selectionSection, "repeat(2, minmax(0,1fr))");
  let styleTargetSelect = null;
  styleTargetSelect = makeSelect(
    selectionGrid,
    "Apply to",
    [
      ["table", "All cells"],
      ["header", "Header"],
      ["body", "Body"],
      ["row", "Selected row"],
      ["column", "Selected column"],
      ["selection", "Selected cells / range"],
      ["cell", "Active cell"],
    ],
    () => previewStyleTarget,
    (value) => {
      previewStyleTarget = value || "cell";
    },
    { history: false }
  );
  const targetAlignControl = makeSelect(selectionGrid, "Text alignment", [["left", "Left"], ["center", "Center"], ["right", "Right"]], () => sampleTargetCell()?.style.textAlign || "left", (value) => applyTargetStyle("textAlign", value));
  const targetVerticalControl = makeSelect(selectionGrid, "Vertical alignment", [["top", "Top"], ["middle", "Middle"], ["bottom", "Bottom"]], () => sampleTargetCell()?.style.verticalAlign || "top", (value) => applyTargetStyle("verticalAlign", value));
  const targetWeightControl = makeSelect(selectionGrid, "Font weight", [["400", "Normal"], ["600", "Semibold"], ["700", "Bold"], ["800", "Extra bold"]], () => sampleTargetCell()?.style.fontWeight || "400", (value) => applyTargetStyle("fontWeight", value));
  const targetFontSizeControl = makeNumberField(selectionGrid, "Selected font size (px)", {
    getValue: () => asFiniteNumber(String(sampleTargetCell()?.style.fontSize || "12").replace(/px$/, ""), 12),
    min: 6, max: 72, step: 1,
    onCommit: (value) => applyTargetStyle("fontSize", `${value}px`),
  });
  const targetBgControl = makeColorField(selectionGrid, "Selected background", () => sampleTargetCell()?.style.backgroundColor || "#ffffff", (value) => applyTargetStyle("backgroundColor", value), "#ffffff");
  const targetTextControl = makeColorField(selectionGrid, "Selected text color", () => sampleTargetCell()?.style.color || "#111827", (value) => applyTargetStyle("color", value), "#111827");

  const cellBorderSection = makeSection(
    "Selected cell / range borders and number format",
    "Use the four-side border box for Excel-style per-cell borders. These overrides sit on top of the whole-table grid. Double bottom is useful for accounting totals."
  );
  const borderEditorGrid = makeRow(
    cellBorderSection,
    "minmax(128px, 0.9fr) minmax(0, 1.1fr)"
  );

  const borderBoxWrap = documentValue.createElement("div");
  Object.assign(borderBoxWrap.style, {
    display: "grid",
    placeItems: "center",
    minHeight: "128px",
  });
  const borderBox = documentValue.createElement("div");
  Object.assign(borderBox.style, {
    position: "relative",
    width: "88px",
    height: "72px",
    border: "1px dashed #cbd5e1",
    borderRadius: "8px",
    background: "#f8fafc",
  });
  const borderSideButtons = {};
  [
    ["top", { left: "14px", right: "14px", top: "-7px", height: "14px", cursor: "pointer" }],
    ["bottom", { left: "14px", right: "14px", bottom: "-7px", height: "14px", cursor: "pointer" }],
    ["left", { top: "10px", bottom: "10px", left: "-7px", width: "14px", cursor: "pointer" }],
    ["right", { top: "10px", bottom: "10px", right: "-7px", width: "14px", cursor: "pointer" }],
  ].forEach(([side, geometry]) => {
    const button = documentValue.createElement("button");
    button.type = "button";
    button.title = `${side[0].toUpperCase()}${side.slice(1)} border`;
    button.setAttribute("aria-label", button.title);
    Object.assign(button.style, {
      position: "absolute",
      padding: "0",
      border: "0",
      borderRadius: "999px",
      background: "transparent",
      ...geometry,
    });
    const line = documentValue.createElement("span");
    Object.assign(line.style, {
      position: "absolute",
      borderRadius: "999px",
      background: "#94a3b8",
      pointerEvents: "none",
      ...(side === "top" || side === "bottom"
        ? { left: "0", right: "0", top: "6px", height: "2px" }
        : { top: "0", bottom: "0", left: "6px", width: "2px" }),
    });
    button.appendChild(line);
    button.addEventListener("pointerdown", (event) => event.stopPropagation());
    button.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      capturePreviewUndoCheckpoint();
      setSelectedBorderSide(side);
      refreshPreviewUi();
    });
    borderBox.appendChild(button);
    borderSideButtons[side] = { button, line };
  });
  borderBoxWrap.appendChild(borderBox);
  borderEditorGrid.appendChild(borderBoxWrap);

  const borderControlPane = documentValue.createElement("div");
  Object.assign(borderControlPane.style, { display: "grid", gap: "8px", minWidth: "0" });
  borderEditorGrid.appendChild(borderControlPane);

  selectedBorderStyleControl = makeSelect(
    borderControlPane,
    "Selected border style",
    [
      ["solid", "Solid"],
      ["double", "Double"],
      ["dashed", "Dashed"],
      ["dotted", "Dotted"],
      ["none", "None"],
    ],
    () => previewBorderToolStyle,
    (value) => {
      previewBorderToolStyle = value;
      restyleSelectedExplicitBorders();
    }
  );
  selectedBorderWidthControl = makeNumberField(
    borderControlPane,
    "Selected border width (px)",
    {
      getValue: () => previewBorderToolWidth,
      min: 0,
      max: 12,
      step: 1,
      onCommit: (value) => {
        previewBorderToolWidth = value;
        restyleSelectedExplicitBorders();
        return value;
      },
    }
  );
  selectedBorderColorControl = makeColorField(
    borderControlPane,
    "Selected border color",
    () => previewBorderToolColor,
    (value) => {
      previewBorderToolColor = value || "#0f172a";
      restyleSelectedExplicitBorders();
    },
    "#0f172a"
  );

  const borderPresetRow = makeRow(cellBorderSection, "repeat(3, minmax(0,1fr))");
  makeButton(borderPresetRow, "All 4 sides", () => {
    setSelectedAllBorderSides(true);
    refreshPreviewUi();
  });
  makeButton(borderPresetRow, "No sides", () => {
    setSelectedAllBorderSides(false);
    refreshPreviewUi();
  });
  makeButton(borderPresetRow, "Double bottom", () => {
    setSelectedDoubleBottom();
    refreshPreviewUi();
  });

  const numberFormatRow = makeRow(cellBorderSection, "minmax(0,1fr)");
  selectedNumberFormatControl = makeSelect(
    numberFormatRow,
    "Selected numeric format",
    [
      ["general", "General"],
      ["number", "Number (2 decimals)"],
      ["accounting", "Accounting (USD)"],
    ],
    selectedNumberFormat,
    (value) => applySelectedNumberFormat(value)
  );

  const helperSection = makeSection(
    "Selection and editing",
    "Click for one cell, Ctrl/Cmd-click to add/remove cells, Shift-click to extend from the anchor, or drag across cells to highlight a rectangle. Tab moves across cells; Tab from the final data cell adds a row."
  );
  const helperButtons = makeRow(helperSection, "repeat(2, minmax(0,1fr))");
  makeButton(helperButtons, "Clear selected cell", () => {
    const cell = previewCell();
    if (!cell) return;
    cell.replaceChildren(documentValue.createElement("br"));
    previewDirty = true;
    focusPreviewCell(previewCellId);
  });
  makeButton(helperButtons, "Reset selection", () => {
    previewSelectedCellIds = previewCellId ? new Set([previewCellId]) : new Set();
    previewSelectionAnchorId = previewCellId || "";
    markPreviewSelection();
    refreshPreviewUi();
  }, { history: false });

  const syncControls = [];
  [widthUnitControl, widthValueControl, alignmentControl, tableHeightControl, columnWidthControl, rowHeightControl, tableFontSizeControl, lineHeightControl, fontFamilyControl,
   headerBgControl, headerTextControl, bodyBgControl, bodyTextControl, tableBgControl, altColorControl, headerRowControl, alternatingControl,
   borderWidthControl, borderStyleControl, borderColorControl, completeGridControl, outerBorderControl, rowLinesControl, columnLinesControl, selectedRowGridControl, selectedColumnGridControl, paddingXControl, paddingYControl,
   styleTargetSelect, targetAlignControl, targetVerticalControl, targetWeightControl, targetFontSizeControl, targetBgControl, targetTextControl,
   supplementalAlignControl, supplementalFontSizeControl, supplementalBoldControl, supplementalItalicControl, supplementalUnderlineControl,
   selectedBorderStyleControl, selectedBorderWidthControl, selectedBorderColorControl, selectedNumberFormatControl]
    .forEach((control) => { if (control?.sync) syncControls.push(control); });

  function refreshPreviewHorizontalScroll() {
    if (!previewHorizontalScroller || !previewHorizontalSpacer) return;
    if (!previewTable || !previewViewport) {
      previewHorizontalScroller.style.display = "none";
      previewHorizontalSpacer.style.width = "0px";
      return;
    }

    const viewportWidth = Math.max(0, previewViewport.clientWidth);
    const contentWidth = Math.max(
      viewportWidth,
      previewViewport.scrollWidth,
      previewStage.scrollWidth,
      (previewTable.offsetLeft || 0) + (previewTable.scrollWidth || previewTable.offsetWidth || 0) + 28
    );
    const hasOverflow = contentWidth > viewportWidth + 2;
    previewHorizontalSpacer.style.width = `${Math.ceil(contentWidth)}px`;
    previewHorizontalScroller.style.display = hasOverflow ? "block" : "none";
    if (hasOverflow) {
      const maxViewportScroll = Math.max(1, previewViewport.scrollWidth - previewViewport.clientWidth);
      const maxBarScroll = Math.max(
        1,
        previewHorizontalScroller.scrollWidth - previewHorizontalScroller.clientWidth
      );
      const ratio = Math.min(1, Math.max(0, previewViewport.scrollLeft / maxViewportScroll));
      previewHorizontalScroller.scrollLeft = ratio * maxBarScroll;
    } else {
      previewViewport.scrollLeft = 0;
      previewHorizontalScroller.scrollLeft = 0;
    }
  }

  function refreshSelectedBorderUi() {
    Object.entries(borderSideButtons || {}).forEach(([side, refs]) => {
      const enabled = selectedBorderSideEnabled(side);
      refs.line.style.background = enabled ? "#1E2A78" : "#94a3b8";
      if (side === "top" || side === "bottom") {
        refs.line.style.height = enabled ? "3px" : "2px";
      } else {
        refs.line.style.width = enabled ? "3px" : "2px";
      }
      refs.button.style.background = enabled
        ? "rgba(30,42,120,.06)"
        : "transparent";
    });
  }

  function positionPreviewResizeHandles() {
    if (!previewTable) {
      previewResizeHandles.forEach(({ button }) => { button.style.display = "none"; });
      previewResizeTooltip.style.display = "none";
      return;
    }

    const left = previewTable.offsetLeft;
    const top = previewTable.offsetTop;
    const width = previewTable.offsetWidth;
    const height = previewTable.offsetHeight;
    if (!width || !height) return;

    previewResizeHandles.forEach(({ handle: resizeHandleName, kind, button }) => {
      button.style.display = kind === "corner" ? "grid" : "flex";
      if (kind === "corner") {
        const isWest = resizeHandleName.includes("w");
        const isNorth = resizeHandleName.includes("n");
        button.style.left = `${Math.round(isWest ? left + 4 : left + width - 28)}px`;
        button.style.top = `${Math.round(isNorth ? top + 4 : top + height - 28)}px`;
      } else {
        button.style.left = `${Math.round(left + width / 2 - 60)}px`;
        button.style.top = `${Math.round(resizeHandleName === "n" ? top - 7 : top + height - 7)}px`;
      }
    });
  }

  function refreshPreviewUi() {
    if (!previewTable) {
      previewStatus.textContent = "No table preview is available.";
      refreshPreviewHistoryButtons();
      refreshPreviewHorizontalScroll();
      return;
    }
    if (previewInitialHtml) {
      previewDirty = previewComparableHtml(previewTable) !== previewInitialHtml;
    }
    const grid = previewGrid();
    const selectedText = selectedCellCoordinate();
    const selectedCount = previewSelectedCellIds.size;
    previewStatus.textContent = `${selectedText} · ${grid.rows.length} row${grid.rows.length === 1 ? "" : "s"} · ${grid.columnCount} column${grid.columnCount === 1 ? "" : "s"}${selectedCount > 1 ? ` · ${selectedCount} cells selected` : ""}${previewDirty ? " · Unsaved changes" : ""}`;
    const supplemental = activePreviewSupplemental();
    if (supplemental) {
      supplementalStatus.textContent = `${previewSupplementalKind === "title" ? "Top title" : "Bottom note"} line selected · type directly in the preview or format it here.`;
    } else {
      supplementalStatus.textContent = "Click a top title or bottom note line to format it.";
    }
    syncControls.forEach((control) => {
      try { control.sync(); } catch (_) {}
    });
    refreshPreviewHistoryButtons();
    refreshSelectedBorderUi();
    positionPreviewResizeHandles();
    refreshPreviewHorizontalScroll();
  }

  function mountPreview() {
    previewStage.querySelectorAll('table[data-doc-table-preview="true"]').forEach((node) => node.remove());
    previewTable = createLogicalPreviewClone(activeTableId);
    previewCellId = "";
    previewRowId = "";
    previewColumnIndex = -1;
    previewSelectedCellIds = new Set();
    previewSelectionAnchorId = "";
    previewSelectionDrag = null;
    previewStyleTarget = "cell";
    previewSupplementalId = "";
    previewSupplementalKind = "";
    previewUndoStack = [];
    previewRedoStack = [];
    previewInitialHtml = "";
    previewDirty = false;
    if (!previewTable) {
      refreshPreviewUi();
      return;
    }

    // AI/export tables sometimes encode a full-width title as the first spanning
    // row. Separate that from the actual THEAD in the draft so "Show header row"
    // controls the real header while the title remains independently editable.
    promoteSpanningTitleRowToCaption(previewTable);
    normalizeTableSupplementalPresentation(previewTable);

    const widthSpec = currentTableWidthSpec();
    tableWidthUnit = widthSpec.unit;
    previewStage.insertBefore(previewTable, previewResizeTooltip);
    preparePreviewCells();
    previewInitialHtml = previewComparableHtml(previewTable);

    const firstCell = previewTable.querySelector(
      `thead th, tbody td, tbody th, tr:not([${DOC_TABLE_NOTE_ROW_ATTR}="true"]) td`
    );
    if (firstCell) updatePreviewSelection(firstCell);
    refreshPreviewHistoryButtons();
    refreshPreviewUi();
  }

  function beginPreviewColumnResize(event, cell) {
    if (!previewTable || !cell) return;
    event.preventDefault();
    event.stopPropagation();
    capturePreviewUndoCheckpoint();
    const origin = originForCell(previewGrid(), cell);
    if (!origin) return;
    const columnIndex = origin.columnIndex + Math.max(1, origin.colSpan) - 1;
    let lastX = event.clientX;
    try { cell.setPointerCapture?.(event.pointerId); } catch (_) {}
    const move = (moveEvent) => {
      const delta = moveEvent.clientX - lastX;
      lastX = moveEvent.clientX;
      if (!delta) return;
      resizeTableColumn(previewTable, { columnIndex, deltaX: delta, adjacent: true });
      previewDirty = true;
      refreshPreviewUi();
    };
    const finish = () => {
      view.removeEventListener("pointermove", move, true);
      view.removeEventListener("pointerup", finish, true);
      view.removeEventListener("pointercancel", finish, true);
      previewColumnResizeCleanup = null;
    };
    previewColumnResizeCleanup = finish;
    view.addEventListener("pointermove", move, true);
    view.addEventListener("pointerup", finish, true);
    view.addEventListener("pointercancel", finish, true);
  }

  function beginPreviewRowResize(event, cell) {
    if (!previewTable || !cell) return;
    event.preventDefault();
    event.stopPropagation();
    capturePreviewUndoCheckpoint();
    const row = cell.closest("tr");
    if (!row) return;
    const rowId = row.getAttribute(DOC_ROW_ID_ATTR) || "";
    let lastY = event.clientY;
    try { cell.setPointerCapture?.(event.pointerId); } catch (_) {}
    const move = (moveEvent) => {
      const delta = moveEvent.clientY - lastY;
      lastY = moveEvent.clientY;
      if (!delta) return;
      resizeTableRow(previewTable, { rowId, deltaY: delta });
      previewDirty = true;
      refreshPreviewUi();
    };
    const finish = () => {
      view.removeEventListener("pointermove", move, true);
      view.removeEventListener("pointerup", finish, true);
      view.removeEventListener("pointercancel", finish, true);
      previewRowResizeCleanup = null;
    };
    previewRowResizeCleanup = finish;
    view.addEventListener("pointermove", move, true);
    view.addEventListener("pointerup", finish, true);
    view.addEventListener("pointercancel", finish, true);
  }

  function edgeIntent(cell, event) {
    const rect = cell?.getBoundingClientRect?.();
    if (!rect) return "cell";
    const rightDistance = Math.abs(rect.right - event.clientX);
    const bottomDistance = Math.abs(rect.bottom - event.clientY);
    if (rightDistance <= 7 && bottomDistance > 7) return "column";
    if (bottomDistance <= 7) return "row";
    return "cell";
  }

  function clearLiveResizeHover() {
    liveHoverCell = null;
    const root = resolveRoot();
    if (root && !liveColumnResizeCleanup && !liveRowResizeCleanup) {
      root.style.cursor = "";
    }
  }

  function liveTableCellFromEvent(event) {
    const root = resolveRoot();
    const target = elementFromNode(event?.target);
    const cell = target?.closest?.("th,td") || null;
    const table = cell?.closest?.(
      `table[${DOC_TABLE_ATTR}="true"], table[${DOC_WRITER_TABLE_ATTR}="true"]`
    );
    if (!root || !cell || !table || !root.contains(table)) {
      return { table: null, cell: null };
    }
    return { table, cell };
  }

  function beginLiveColumnResize(event, table, cell) {
    if (
      !table ||
      !cell ||
      editOpen ||
      liveColumnResizeCleanup ||
      liveRowResizeCleanup ||
      event.button !== 0
    ) {
      return false;
    }

    const grid = buildTableGrid(table);
    const origin = originForCell(grid, cell);
    if (!origin) return false;

    const tableId = table.getAttribute(DOC_TABLE_ID_ATTR) || "";
    if (!tableId) return false;

    event.preventDefault();
    event.stopPropagation();

    setFromTableAndCell(table, cell);
    const columnIndex =
      origin.columnIndex + Math.max(1, origin.colSpan) - 1;
    const beforeHtml = tableApi.snapshotDocument?.() || "";
    const fragments = tableApi.getLogicalFragments?.(tableId) || [table];
    const snapshots = fragments.map((fragment) => {
      const colgroup = ensureTableColgroup(
        fragment,
        getTableColumnCount(fragment),
        { inferWidths: false }
      );
      return {
        fragment,
        widths: Array.from(colgroup?.children || []).map(
          (column) => column.style.width || ""
        ),
      };
    });

    let lastX = event.clientX;
    try { cell.setPointerCapture?.(event.pointerId); } catch (_) {}
    notifyEditingChange(true, { operation: "resize-column" });

    const removeListeners = () => {
      view.removeEventListener("pointermove", move, true);
      view.removeEventListener("pointerup", finish, true);
      view.removeEventListener("pointercancel", cancel, true);
    };

    const move = (moveEvent) => {
      const delta = moveEvent.clientX - lastX;
      lastX = moveEvent.clientX;
      if (!delta) return;

      snapshots.forEach(({ fragment }) => {
        if (!fragment?.isConnected) return;
        resizeTableColumn(fragment, {
          columnIndex,
          deltaX: delta,
          adjacent: true,
        });
      });
      refresh();
    };

    const finish = () => {
      removeListeners();
      liveColumnResizeCleanup = null;
      clearLiveResizeHover();
      tableApi.commitLiveTable?.({
        tableId,
        beforeHtml,
        operation: "resize-column",
        source: "table-live-cell-edge",
        focusCellId: cell.getAttribute(DOC_CELL_ID_ATTR) || "",
      });
      notifyEditingChange(false, { operation: "resize-column" });
      view.requestAnimationFrame(refresh);
    };

    const cancel = () => {
      removeListeners();
      liveColumnResizeCleanup = null;
      clearLiveResizeHover();
      snapshots.forEach(({ fragment, widths }) => {
        if (!fragment?.isConnected) return;
        const colgroup = ensureTableColgroup(
          fragment,
          widths.length,
          { inferWidths: false }
        );
        Array.from(colgroup?.children || []).forEach((column, index) => {
          const width = widths[index] || "";
          if (width) column.style.width = width;
          else column.style.removeProperty("width");
        });
      });
      notifyEditingChange(false, { operation: "resize-column" });
      refresh();
    };

    liveColumnResizeCleanup = cancel;
    view.addEventListener("pointermove", move, true);
    view.addEventListener("pointerup", finish, true);
    view.addEventListener("pointercancel", cancel, true);
    return true;
  }

  function beginLiveRowResize(event, table, cell) {
    if (
      !table ||
      !cell ||
      editOpen ||
      liveColumnResizeCleanup ||
      liveRowResizeCleanup ||
      event.button !== 0
    ) {
      return false;
    }

    const row = cell.closest("tr");
    const tableId = table.getAttribute(DOC_TABLE_ID_ATTR) || "";
    const rowId = row?.getAttribute(DOC_ROW_ID_ATTR) || "";
    if (!row || !tableId || !rowId) return false;

    event.preventDefault();
    event.stopPropagation();

    setFromTableAndCell(table, cell);
    const beforeHtml = tableApi.snapshotDocument?.() || "";
    const fragments = tableApi.getLogicalFragments?.(tableId) || [table];
    const snapshots = fragments
      .map((fragment) => {
        const fragmentRow = findRowById(fragment, rowId);
        return fragmentRow
          ? {
              fragment,
              row: fragmentRow,
              height: fragmentRow.style.height || "",
              minHeight: fragmentRow.style.minHeight || "",
            }
          : null;
      })
      .filter(Boolean);

    let lastY = event.clientY;
    try { cell.setPointerCapture?.(event.pointerId); } catch (_) {}
    notifyEditingChange(true, { operation: "resize-row" });

    const removeListeners = () => {
      view.removeEventListener("pointermove", move, true);
      view.removeEventListener("pointerup", finish, true);
      view.removeEventListener("pointercancel", cancel, true);
    };

    const move = (moveEvent) => {
      const delta = moveEvent.clientY - lastY;
      lastY = moveEvent.clientY;
      if (!delta) return;

      snapshots.forEach(({ fragment }) => {
        if (!fragment?.isConnected) return;
        resizeTableRow(fragment, { rowId, deltaY: delta });
      });
      refresh();
    };

    const finish = () => {
      removeListeners();
      liveRowResizeCleanup = null;
      clearLiveResizeHover();
      tableApi.commitLiveTable?.({
        tableId,
        beforeHtml,
        operation: "resize-row",
        source: "table-live-cell-edge",
        focusCellId: cell.getAttribute(DOC_CELL_ID_ATTR) || "",
      });
      notifyEditingChange(false, { operation: "resize-row" });
      view.requestAnimationFrame(refresh);
    };

    const cancel = () => {
      removeListeners();
      liveRowResizeCleanup = null;
      clearLiveResizeHover();
      snapshots.forEach(({ row: fragmentRow, height, minHeight }) => {
        if (!fragmentRow?.isConnected) return;
        if (height) fragmentRow.style.height = height;
        else fragmentRow.style.removeProperty("height");
        if (minHeight) fragmentRow.style.minHeight = minHeight;
        else fragmentRow.style.removeProperty("min-height");
      });
      notifyEditingChange(false, { operation: "resize-row" });
      refresh();
    };

    liveRowResizeCleanup = cancel;
    view.addEventListener("pointermove", move, true);
    view.addEventListener("pointerup", finish, true);
    view.addEventListener("pointercancel", cancel, true);
    return true;
  }

  function previewCellUnderPointer(event) {
    const direct = elementFromNode(event?.target)?.closest?.("th,td") || null;
    if (direct && previewTable?.contains(direct)) return direct;
    try {
      const point = documentValue.elementFromPoint?.(event.clientX, event.clientY);
      const cell = point?.closest?.("th,td") || null;
      return cell && previewTable?.contains(cell) ? cell : null;
    } catch (_) {
      return null;
    }
  }

  function finishPreviewSelectionDrag(event = null) {
    const drag = previewSelectionDrag;
    previewSelectionDrag = null;
    if (drag?.pointerId != null) {
      try { previewViewport.releasePointerCapture?.(drag.pointerId); } catch (_) {}
    }
    if (drag?.moved && event) {
      try { event.preventDefault?.(); } catch (_) {}
    }
  }

  previewViewport.addEventListener("pointermove", (event) => {
    if (!previewTable) return;
    const cell = previewCellUnderPointer(event);
    if (!cell) return;

    if (previewSelectionDrag) {
      const targetId = cell.getAttribute(DOC_CELL_ID_ATTR) || "";
      const distance = Math.hypot(
        event.clientX - previewSelectionDrag.startX,
        event.clientY - previewSelectionDrag.startY
      );
      if (
        targetId &&
        !previewCellIsSupplemental(cell) &&
        (distance >= 4 || targetId !== previewSelectionDrag.anchorId)
      ) {
        previewSelectionDrag.moved = true;
        event.preventDefault();
        try { view.getSelection?.()?.removeAllRanges?.(); } catch (_) {}
        selectPreviewRectangle(previewSelectionDrag.anchorId, targetId);
        cell.style.cursor = "cell";
        return;
      }
    }

    const intent = previewCellIsSupplemental(cell)
      ? "cell"
      : edgeIntent(cell, event);
    cell.style.cursor =
      intent === "column"
        ? "col-resize"
        : intent === "row"
        ? "row-resize"
        : "text";
  });

  previewViewport.addEventListener("pointerdown", (event) => {
    if (!previewTable) return;
    const targetElement = elementFromNode(event.target);
    const titleLine = targetElement?.closest?.(`[${DOC_TABLE_TITLE_LINE_ATTR}="true"]`);
    if (titleLine && previewTable.contains(titleLine)) {
      setActivePreviewSupplemental(titleLine);
      return;
    }

    const cell = previewCellUnderPointer(event);
    if (!cell || !previewTable.contains(cell)) return;

    // Bottom-note lines are directly editable but intentionally excluded from the
    // structural merge grid. Clicking one selects it for the supplemental formatter.
    if (previewCellIsSupplemental(cell)) {
      setActivePreviewSupplemental(cell);
      return;
    }

    previewSupplementalId = "";
    previewSupplementalKind = "";

    const intent = edgeIntent(cell, event);
    if (intent === "column") {
      beginPreviewColumnResize(event, cell);
      return;
    }
    if (intent === "row") {
      beginPreviewRowResize(event, cell);
      return;
    }

    const id = cell.getAttribute(DOC_CELL_ID_ATTR) || "";
    if (!id) return;

    if (event.shiftKey && previewSelectionAnchorId) {
      updatePreviewSelection(cell, { extend: true, preserveAnchor: true });
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      updatePreviewSelection(cell, { toggle: true, preserveAnchor: true });
      return;
    }

    updatePreviewSelection(cell);
    previewSelectionDrag = {
      pointerId: event.pointerId,
      anchorId: id,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
    };
    try { previewViewport.setPointerCapture?.(event.pointerId); } catch (_) {}
  });

  view.addEventListener("pointerup", finishPreviewSelectionDrag, true);
  view.addEventListener("pointercancel", finishPreviewSelectionDrag, true);

  previewViewport.addEventListener("focusin", (event) => {
    if (!previewTable) return;
    const target = elementFromNode(event.target);
    const titleLine = target?.closest?.(`[${DOC_TABLE_TITLE_LINE_ATTR}="true"]`);
    if (titleLine && previewTable.contains(titleLine)) {
      setActivePreviewSupplemental(titleLine);
      return;
    }
    const noteLine = target?.closest?.(`[${DOC_TABLE_NOTE_LINE_ATTR}="true"]`);
    if (noteLine && previewTable.contains(noteLine)) {
      setActivePreviewSupplemental(noteLine);
    }
  });

  previewViewport.addEventListener("beforeinput", (event) => {
    const editable = elementFromNode(event.target)?.closest?.(`th,td,[${DOC_TABLE_TITLE_LINE_ATTR}="true"]`);
    if (!previewTable || !editable || !previewTable.contains(editable)) return;
    capturePreviewUndoCheckpoint();
  });

  previewViewport.addEventListener("input", (event) => {
    const editable = elementFromNode(event.target)?.closest?.(`th,td,[${DOC_TABLE_TITLE_LINE_ATTR}="true"]`);
    if (!previewTable || !editable || !previewTable.contains(editable)) return;

    if (editable.matches?.("th,td") && !previewCellIsSupplemental(editable)) {
      // Manual typing wins over a previously applied numeric presentation. The
      // user can re-apply Number/Accounting after finishing the edit.
      editable.removeAttribute(DOC_CELL_NUMBER_FORMAT_ATTR);
      editable.removeAttribute(DOC_CELL_NUMBER_VALUE_ATTR);
    }
    previewDirty = true;
    refreshPreviewUi();
  });

  previewViewport.addEventListener("keydown", (event) => {
    const cell = elementFromNode(event.target)?.closest?.("th,td");
    if (
      !previewTable ||
      !cell ||
      !previewTable.contains(cell) ||
      previewCellIsSupplemental(cell) ||
      event.key !== "Tab"
    ) {
      return;
    }

    event.preventDefault();
    const cells = Array.from(
      previewTable.querySelectorAll(
        `:scope > thead th, :scope > tbody th, :scope > tbody td`
      )
    );
    const index = cells.indexOf(cell);
    if (event.shiftKey) {
      const previous = cells[Math.max(0, index - 1)] || cells[0];
      if (previous) {
        focusPreviewCell(previous.getAttribute(DOC_CELL_ID_ATTR) || "", true);
      }
      return;
    }

    const next = cells[index + 1];
    if (next) {
      focusPreviewCell(next.getAttribute(DOC_CELL_ID_ATTR) || "", false);
      return;
    }

    capturePreviewUndoCheckpoint();
    const origin = originForCell(previewGrid(), cell);
    const result = addTableRow(previewTable, {
      where: "below",
      rowId: cell.closest("tr")?.getAttribute(DOC_ROW_ID_ATTR) || "",
      cellId: cell.getAttribute(DOC_CELL_ID_ATTR) || "",
      focusColumn: origin?.columnIndex ?? 0,
    });
    preparePreviewCells();
    previewDirty = previewDirty || result.changed;
    focusPreviewCell(result.focusCellId || "");
  });

  let syncingPreviewHorizontalScroll = false;
  previewHorizontalScroller.addEventListener("scroll", () => {
    if (syncingPreviewHorizontalScroll || !previewTable) return;
    syncingPreviewHorizontalScroll = true;
    try {
      const maxBar = Math.max(
        1,
        previewHorizontalScroller.scrollWidth -
          previewHorizontalScroller.clientWidth
      );
      const maxViewport = Math.max(
        1,
        previewViewport.scrollWidth - previewViewport.clientWidth
      );
      const ratio = previewHorizontalScroller.scrollLeft / maxBar;
      previewViewport.scrollLeft = ratio * maxViewport;
    } finally {
      syncingPreviewHorizontalScroll = false;
    }
  });

  previewViewport.addEventListener("scroll", () => {
    if (syncingPreviewHorizontalScroll || !previewTable) return;
    syncingPreviewHorizontalScroll = true;
    try {
      const maxViewport = Math.max(
        1,
        previewViewport.scrollWidth - previewViewport.clientWidth
      );
      const maxBar = Math.max(
        1,
        previewHorizontalScroller.scrollWidth -
          previewHorizontalScroller.clientWidth
      );
      const ratio = previewViewport.scrollLeft / maxViewport;
      previewHorizontalScroller.scrollLeft = ratio * maxBar;
    } finally {
      syncingPreviewHorizontalScroll = false;
    }
  });

  previewViewport.addEventListener(
    "wheel",
    (event) => {
      if (
        previewHorizontalScroller.style.display === "none" ||
        (!event.shiftKey && Math.abs(event.deltaX) <= Math.abs(event.deltaY))
      ) {
        return;
      }
      const delta = event.deltaX || event.deltaY;
      if (!delta) return;
      event.preventDefault();
      previewHorizontalScroller.scrollLeft += delta;
    },
    { passive: false }
  );


  function showPreviewResizeTooltip(button) {
    const buttonRect = button.getBoundingClientRect();
    const stageRect = previewStage.getBoundingClientRect();
    previewResizeTooltip.style.display = "block";
    previewResizeTooltip.style.left = `${Math.round(buttonRect.left - stageRect.left + buttonRect.width / 2)}px`;
    previewResizeTooltip.style.top = `${Math.round(buttonRect.top - stageRect.top - 7)}px`;
    previewResizeTooltip.style.transform = "translate(-50%, -100%)";
  }

  previewResizeHandles.forEach(({ handle: resizeHandleName, button }) => {
    button.addEventListener("mouseenter", () => showPreviewResizeTooltip(button));
    button.addEventListener("mouseleave", () => { previewResizeTooltip.style.display = "none"; });
    button.addEventListener("pointerdown", (event) => {
      if (!previewTable) return;
      event.preventDefault();
      event.stopPropagation();
      capturePreviewUndoCheckpoint();

      const startX = event.clientX;
      const startY = event.clientY;
      const startRect = previewTable.getBoundingClientRect();
      const startWidth = startRect.width;
      const startRowHeights = measuredTableRowHeights(previewTable);
      const startHeight = startRowHeights.reduce((sum, value) => sum + value, 0);

      try { button.setPointerCapture?.(event.pointerId); } catch (_) {}

      const move = (moveEvent) => {
        const deltaX = moveEvent.clientX - startX;
        const deltaY = moveEvent.clientY - startY;
        const resizeHorizontal = resizeHandleName.includes("e") || resizeHandleName.includes("w");
        const resizeVertical = resizeHandleName.includes("n") || resizeHandleName.includes("s");

        if (resizeHorizontal) {
          const widthDelta = resizeHandleName.includes("w") ? -deltaX : deltaX;
          const nextWidth = Math.max(MIN_TABLE_WIDTH_PX, startWidth + widthDelta);
          tableWidthUnit = "px";
          resizeTableWidth(previewTable, { width: nextWidth, unit: "px" });
          previewTable.style.maxWidth = "none";
        }

        if (resizeVertical) {
          // Top handle: down => negative height delta (collapse from the top).
          // Bottom handle: up => negative height delta (collapse from the bottom).
          const heightDelta = resizeHandleName.includes("n") ? -deltaY : deltaY;
          applyTableHeightFromBaseline(
            previewTable,
            startRowHeights,
            startHeight + heightDelta
          );
        }

        previewDirty = true;
        refreshPreviewUi();
      };

      const finish = () => {
        view.removeEventListener("pointermove", move, true);
        view.removeEventListener("pointerup", finish, true);
        view.removeEventListener("pointercancel", finish, true);
        previewResizeCleanup = null;
      };

      previewResizeCleanup = finish;
      view.addEventListener("pointermove", move, true);
      view.addEventListener("pointerup", finish, true);
      view.addEventListener("pointercancel", finish, true);
    });
  });

  function openEditor() {
    const table = tableForControls();
    if (!table || !activeTableId) return;
    editBeforeHtml = tableApi.snapshotDocument?.() || "";
    editOpen = true;
    table.setAttribute(DOC_WRITER_TABLE_EDIT_OPEN_ATTR, "true");
    notifyEditingChange(true, { operation: "edit" });
    editHint.style.display = "none";
    overlay.style.display = "grid";
    editButton.textContent = "Edit";
    mountPreview();
  }

  function closeEditor({ notify = true } = {}) {
    const wasOpen = editOpen;
    editOpen = false;
    hideDeleteConfirmation();
    overlay.style.display = "none";
    const table = tableForControls();
    table?.removeAttribute(DOC_WRITER_TABLE_EDIT_OPEN_ATTR);
    previewResizeCleanup?.(); previewResizeCleanup = null;
    previewColumnResizeCleanup?.(); previewColumnResizeCleanup = null;
    previewRowResizeCleanup?.(); previewRowResizeCleanup = null;
    previewSelectionDrag = null;
    previewUndoStack = [];
    previewRedoStack = [];
    previewInitialHtml = "";
    previewTable = null;
    previewStage.querySelectorAll('table[data-doc-table-preview="true"]').forEach((node) => node.remove());
    previewHorizontalScroller.style.display = "none";
    previewHorizontalSpacer.style.width = "0px";
    refreshPreviewHistoryButtons();
    if (wasOpen && notify) notifyEditingChange(false, { operation: "edit" });
    refresh();
  }

  function savePreview() {
    if (!editOpen || !previewTable || !activeTableId) return;
    const root = resolveRoot();
    if (!root) return;
    const fragments = tableApi.getLogicalFragments?.(activeTableId) || [];
    if (!fragments.length) return;

    const finalTable = previewTable.cloneNode(true);
    stripPreviewOnlyState(finalTable);
    finalTable.setAttribute(DOC_TABLE_ID_ATTR, activeTableId);
    finalTable.style.maxWidth = "100%";
    normalizeCanvasTable(finalTable, {
      source: fragments[0].getAttribute(DOC_TABLE_SOURCE_ATTR) || "reopened",
      inferWidths: false,
      preserveTableId: true,
    });

    // Math commits its edited object upward; it does not first replace the live
    // Canvas node and then ask Canvas to notice. Do the same for tables. This
    // keeps the old live table in place until Canvas accepts the one-object
    // transaction, which closes the historical "table disappeared on Save"
    // failure window and gives Canvas one clean before/after Undo entry.
    let result = null;
    if (typeof tableApi.commitTableDraft === "function") {
      result = tableApi.commitTableDraft({
        tableId: activeTableId,
        table: finalTable,
        beforeHtml: editBeforeHtml,
        operation: "edit-table",
        source: "table-edit-dialog",
        focusCellId: "",
      });
    } else {
      // Compatibility fallback for a host using an older table API.
      fragments[0].replaceWith(finalTable);
      fragments.slice(1).forEach((fragment) => fragment.remove());
      result = tableApi.commitLiveTable?.({
        tableId: activeTableId,
        beforeHtml: editBeforeHtml,
        operation: "edit-table",
        source: "table-edit-dialog",
        focusCellId: "",
      });
    }

    // Keep the Editor's object-edit lock through the one document commit. Release
    // only after the table transaction has completed.
    closeEditor({ notify: false });
    notifyEditingChange(false, { operation: "edit" });
    return result;
  }

  function hideDeleteConfirmation() {
    deleteConfirmOpen = false;
    deleteConfirmLayer.style.display = "none";
  }

  function showDeleteConfirmation() {
    if (!editOpen || !activeTableId) return;
    deleteConfirmOpen = true;
    deleteConfirmLayer.style.display = "grid";
    view.requestAnimationFrame(() => {
      try {
        deleteConfirmCancelButton.focus({ preventScroll: true });
      } catch (_) {}
    });
  }

  function performDeleteLiveTable() {
    if (!activeTableId) return;
    hideDeleteConfirmation();
    tableApi.deleteTable?.({ tableId: activeTableId, source: "table-edit-dialog" });
    closeEditor({ notify: false });
    notifyEditingChange(false);
    activeTableId = "";
    activeCellId = "";
    activeRowId = "";
    activeColumnIndex = -1;
  }

  undoButton.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    undoPreviewChange();
  });
  redoButton.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    redoPreviewChange();
  });
  closeButton.addEventListener("click", (event) => { event.preventDefault(); event.stopPropagation(); closeEditor(); });
  cancelButton.addEventListener("click", (event) => { event.preventDefault(); event.stopPropagation(); closeEditor(); });
  saveButton.addEventListener("click", (event) => { event.preventDefault(); event.stopPropagation(); savePreview(); });
  deleteTableButton.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    showDeleteConfirmation();
  });
  deleteConfirmCancelButton.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    hideDeleteConfirmation();
  });
  deleteConfirmDeleteButton.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    performDeleteLiveTable();
  });
  deleteConfirmLayer.addEventListener("pointerdown", (event) => {
    event.stopPropagation();
    if (event.target === deleteConfirmLayer) hideDeleteConfirmation();
  });
  deleteConfirmCard.addEventListener("pointerdown", (event) => event.stopPropagation());
  deleteConfirmCard.addEventListener("click", (event) => event.stopPropagation());

  const stopDialogEvent = (event) => event.stopPropagation();
  overlay.addEventListener("pointerdown", stopDialogEvent);
  overlay.addEventListener("mousedown", stopDialogEvent);
  overlay.addEventListener("click", stopDialogEvent);
  overlay.addEventListener("keydown", (event) => {
    event.stopPropagation();

    const isMac = /Mac|iPhone|iPad|iPod/.test(
      view.navigator?.platform || view.navigator?.userAgent || ""
    );
    const accelerator = isMac ? event.metaKey : event.ctrlKey;
    const key = String(event.key || "").toLowerCase();

    if (accelerator && key === "z") {
      event.preventDefault();
      if (event.shiftKey) redoPreviewChange();
      else undoPreviewChange();
      return;
    }
    if (accelerator && key === "y") {
      event.preventDefault();
      redoPreviewChange();
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      if (deleteConfirmOpen) {
        hideDeleteConfirmation();
      } else {
        closeEditor();
      }
    }
  });

  function refresh() {
    const root = resolveRoot();
    if (!root) {
      chrome.style.display = "none";
      resizeHandles.forEach(({ button }) => { button.style.display = "none"; });
      resizeTooltip.style.display = "none";
      editHint.style.display = "none";
      if (!editOpen) overlay.style.display = "none";
      return;
    }

    const context = tableApi.getActiveContext?.() || {};
    if (context.tableId && context.table) setFromTableAndCell(context.table, context.cell || null);

    const table = tableForControls();
    if (!table || !root.contains(table)) {
      if (!editOpen) activeTableId = "";
      chrome.style.display = "none";
      resizeHandles.forEach(({ button }) => { button.style.display = "none"; });
      resizeTooltip.style.display = "none";
      editHint.style.display = "none";
      return;
    }

    const rect = table.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    // Match DocWriterMath's object chrome: Move + Edit are one balanced centered
    // pair, close enough to read as belonging to this table but never touching it.
    const controlRailWidth = Math.max(110, Math.min(380, rect.width * 0.45));
    chrome.style.display = "block";
    chrome.style.left = `${Math.round(rect.left + rect.width / 2 - controlRailWidth / 2)}px`;
    chrome.style.top = `${Math.round(Math.max(4, rect.top - 40))}px`;
    chrome.style.width = `${Math.round(controlRailWidth)}px`;

    resizeHandles.forEach(({ handle: resizeHandleName, kind, button }) => {
      button.style.display = kind === "corner" ? "grid" : "flex";
      if (kind === "corner") {
        const isWest = resizeHandleName.includes("w");
        const isNorth = resizeHandleName.includes("n");
        button.style.left = `${Math.round(isWest ? rect.left + 4 : rect.right - 28)}px`;
        button.style.top = `${Math.round(isNorth ? rect.top + 4 : rect.bottom - 28)}px`;
      } else {
        button.style.left = `${Math.round(rect.left + rect.width / 2 - 60)}px`;
        button.style.top = `${Math.round(resizeHandleName === "n" ? rect.top - 7 : rect.bottom - 7)}px`;
      }
    });

    if (editOpen) table.setAttribute(DOC_WRITER_TABLE_EDIT_OPEN_ATTR, "true");
    else table.removeAttribute(DOC_WRITER_TABLE_EDIT_OPEN_ATTR);
  }

  function close() {
    if (editOpen) closeEditor();
    else refresh();
  }

  function scrollableAncestor(root) {
    let node = root?.parentElement || null;
    while (node && node !== documentValue.body) {
      try {
        const style = view.getComputedStyle(node);
        if (/(auto|scroll)/.test(`${style.overflowY} ${style.overflow}`)) return node;
      } catch (_) {}
      node = node.parentElement;
    }
    return documentValue.scrollingElement || documentValue.documentElement;
  }

  function beginDrag(event) {
    if (!activeTableId || editOpen) return;
    event.preventDefault();
    event.stopPropagation();
    const contextSnapshot = { tableId: activeTableId, cellId: activeCellId, rowId: activeRowId, columnIndex: activeColumnIndex };
    if (typeof onRequestMove === "function") {
      onRequestMove(event, contextSnapshot, tableApi);
      return;
    }
    const surface = scrollableAncestor(resolveRoot());
    let lastX = event.clientX;
    let lastY = event.clientY;
    handle.style.cursor = "grabbing";
    const move = (moveEvent) => {
      lastX = moveEvent.clientX;
      lastY = moveEvent.clientY;
      if (surface?.getBoundingClientRect) {
        const rect = surface === documentValue.scrollingElement || surface === documentValue.documentElement
          ? { top: 0, bottom: view.innerHeight }
          : surface.getBoundingClientRect();
        const delta = tableApi.getDragAutoScrollDelta?.(lastY, rect) || 0;
        if (delta) {
          if (surface === documentValue.scrollingElement || surface === documentValue.documentElement) view.scrollBy(0, delta);
          else surface.scrollTop += delta;
        }
      }
      refresh();
    };
    const end = () => {
      view.removeEventListener("pointermove", move, true);
      view.removeEventListener("pointerup", end, true);
      view.removeEventListener("pointercancel", cancel, true);
      handle.style.cursor = "grab";
      dragCleanup = null;
      const target = tableApi.resolveDropTargetAtPoint?.(lastX, lastY, { tableId: contextSnapshot.tableId });
      if (target?.targetNode) {
        tableApi.moveTable?.({ tableId: contextSnapshot.tableId, targetNode: target.targetNode, position: "before", source: "table-drag-handle" });
      }
      view.requestAnimationFrame(refresh);
    };
    const cancel = () => {
      view.removeEventListener("pointermove", move, true);
      view.removeEventListener("pointerup", end, true);
      view.removeEventListener("pointercancel", cancel, true);
      handle.style.cursor = "grab";
      dragCleanup = null;
      refresh();
    };
    dragCleanup = cancel;
    view.addEventListener("pointermove", move, true);
    view.addEventListener("pointerup", end, true);
    view.addEventListener("pointercancel", cancel, true);
  }

  function beginResize(event, resizeHandleName = "se") {
    if (!activeTableId || editOpen) return;
    event.preventDefault();
    event.stopPropagation();

    const table = tableForControls();
    if (!table) return;

    const contextSnapshot = {
      tableId: activeTableId,
      cellId: activeCellId,
      rowId: activeRowId,
      columnIndex: activeColumnIndex,
      handle: resizeHandleName,
    };

    // Keep the existing host delegation for the four CORNER handles, which are
    // already working correctly. The top/bottom middle handles must stay inside
    // DocWriterTable because some hosts only implement corner resizing and silently
    // ignore "n"/"s", which made these two handles appear dead.
    const isVerticalEdgeHandle =
      resizeHandleName === "n" || resizeHandleName === "s";
    if (typeof onRequestResize === "function" && !isVerticalEdgeHandle) {
      onRequestResize(event, contextSnapshot, tableApi);
      return;
    }

    const beforeHtml = tableApi.snapshotDocument?.() || "";
    notifyEditingChange(true, {
      operation:
        resizeHandleName === "n" || resizeHandleName === "s"
          ? "resize-table-height"
          : "resize-table",
    });
    const fragments = tableApi.getLogicalFragments?.(contextSnapshot.tableId) || [];
    const initialFragmentState = fragments.map((fragment) => ({
      fragment,
      width: fragment.style.width || "",
      rowHeights: measuredTableRowHeights(fragment),
      startHeight: measuredTableRowHeights(fragment).reduce(
        (sum, value) => sum + value,
        0
      ),
      rows: Array.from(fragment.rows || []).map((row) => ({
        row,
        height: row.style.height || "",
        minHeight: row.style.minHeight || "",
      })),
    }));

    const startX = event.clientX;
    const startY = event.clientY;
    const startWidth = table.getBoundingClientRect().width;

    const page = table.closest?.(`[${DOC_CANVAS_PAGE_ATTR}="true"]`) || table.parentElement;
    let maxWidth = page?.getBoundingClientRect?.().width || startWidth;
    try {
      const style = page ? view.getComputedStyle(page) : null;
      if (style) {
        maxWidth -= asFiniteNumber(style.paddingLeft, 0) + asFiniteNumber(style.paddingRight, 0);
      }
    } catch (_) {}
    maxWidth = Math.max(MIN_TABLE_WIDTH_PX, maxWidth);

    const resizeHorizontal =
      resizeHandleName.includes("e") || resizeHandleName.includes("w");
    const resizeVertical =
      resizeHandleName.includes("n") || resizeHandleName.includes("s");

    const move = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      initialFragmentState.forEach(({ fragment, startHeight, rowHeights }) => {
        if (!fragment?.isConnected) return;

        if (resizeHorizontal) {
          const widthDelta = resizeHandleName.includes("w") ? -deltaX : deltaX;
          const nextWidth = clamp(
            startWidth + widthDelta,
            MIN_TABLE_WIDTH_PX,
            maxWidth
          );
          resizeTableWidth(fragment, {
            width: nextWidth,
            maxWidth,
            unit: "px",
          });
        }

        if (resizeVertical) {
          // Top handle: down => negative height delta (collapse from the top).
          // Bottom handle: up => negative height delta (collapse from the bottom).
          const heightDelta = resizeHandleName.includes("n") ? -deltaY : deltaY;
          applyTableHeightFromBaseline(
            fragment,
            rowHeights,
            startHeight + heightDelta
          );
        }
      });

      refresh();
    };

    const finish = () => {
      view.removeEventListener("pointermove", move, true);
      view.removeEventListener("pointerup", finish, true);
      view.removeEventListener("pointercancel", cancel, true);
      resizeCleanup = null;

      tableApi.commitLiveTable?.({
        tableId: contextSnapshot.tableId,
        beforeHtml,
        operation: resizeVertical && !resizeHorizontal ? "resize-table-height" : "resize-table",
        source: `table-resize-handle-${resizeHandleName}`,
        focusCellId: contextSnapshot.cellId,
      });
      notifyEditingChange(false, {
        operation:
          resizeVertical && !resizeHorizontal
            ? "resize-table-height"
            : "resize-table",
      });
      view.requestAnimationFrame(refresh);
    };

    const cancel = () => {
      view.removeEventListener("pointermove", move, true);
      view.removeEventListener("pointerup", finish, true);
      view.removeEventListener("pointercancel", cancel, true);
      resizeCleanup = null;

      initialFragmentState.forEach(({ fragment, width, rows }) => {
        try {
          if (!fragment?.isConnected) return;
          if (width) fragment.style.width = width;
          else fragment.style.removeProperty("width");
          rows.forEach(({ row, height, minHeight }) => {
            if (!row?.isConnected) return;
            if (height) row.style.height = height;
            else row.style.removeProperty("height");
            if (minHeight) row.style.minHeight = minHeight;
            else row.style.removeProperty("min-height");
          });
        } catch (_) {}
      });
      notifyEditingChange(false, {
        operation:
          resizeVertical && !resizeHorizontal
            ? "resize-table-height"
            : "resize-table",
      });
      refresh();
    };

    resizeCleanup = cancel;
    view.addEventListener("pointermove", move, true);
    view.addEventListener("pointerup", finish, true);
    view.addEventListener("pointercancel", cancel, true);
  }

  handle.addEventListener("pointerdown", beginDrag);

  function showResizeTooltip(button) {
    const rect = button.getBoundingClientRect();
    resizeTooltip.style.display = "block";
    resizeTooltip.style.left = `${Math.round(rect.left + rect.width / 2)}px`;
    resizeTooltip.style.top = `${Math.round(rect.top - 7)}px`;
    resizeTooltip.style.transform = "translate(-50%, -100%)";
  }

  resizeHandles.forEach(({ handle: resizeHandleName, button }) => {
    button.addEventListener("pointerdown", (event) => beginResize(event, resizeHandleName));
    button.addEventListener("mouseenter", () => showResizeTooltip(button));
    button.addEventListener("mouseleave", () => { resizeTooltip.style.display = "none"; });
  });

  // Open on pointer-down so the host Editor receives the editing lock before
  // contentEditable blur/pagination can run, matching DocWriterChart behavior.
  editButton.addEventListener("pointerdown", (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!editOpen) openEditor();
  });
  editButton.addEventListener("click", (event) => { event.preventDefault(); event.stopPropagation(); });

  const onRootPointerMove = (event) => {
    if (editOpen || liveColumnResizeCleanup || liveRowResizeCleanup) {
      editHint.style.display = "none";
      return;
    }

    const root = resolveRoot();
    const target = elementFromNode(event.target);
    const hoveredTable = target?.closest?.(
      `table[${DOC_TABLE_ATTR}="true"], table[${DOC_WRITER_TABLE_ATTR}="true"]`
    );

    if (root && hoveredTable && root.contains(hoveredTable)) {
      editHint.style.display = "block";
      editHint.style.left = `${Math.round(event.clientX + 14)}px`;
      editHint.style.top = `${Math.round(event.clientY + 16)}px`;
    } else {
      editHint.style.display = "none";
    }

    const { table, cell } = liveTableCellFromEvent(event);
    if (!table || !cell) {
      clearLiveResizeHover();
      return;
    }

    liveHoverCell = cell;
    const intent = edgeIntent(cell, event);
    if (root) {
      root.style.cursor =
        intent === "column"
          ? "col-resize"
          : intent === "row"
          ? "row-resize"
          : "text";
    }
    if (intent !== "cell") editHint.style.display = "none";
  };

  const onRootPointerLeave = () => {
    editHint.style.display = "none";
    if (!liveColumnResizeCleanup && !liveRowResizeCleanup) {
      clearLiveResizeHover();
    }
  };

  const onRootPointerDown = (event) => {
    editHint.style.display = "none";
    const root = resolveRoot();
    const target = elementFromNode(event.target);
    const table = target?.closest?.(`table[${DOC_TABLE_ATTR}="true"], table[${DOC_WRITER_TABLE_ATTR}="true"]`);
    if (!table || !root?.contains(table)) return;
    const cell = target?.closest?.("th, td");
    const activeCell = cell && table.contains(cell) ? cell : null;
    setFromTableAndCell(table, activeCell);

    if (activeCell && event.button === 0 && !editOpen) {
      const intent = edgeIntent(activeCell, event);
      if (intent === "column" && beginLiveColumnResize(event, table, activeCell)) {
        return;
      }
      if (intent === "row" && beginLiveRowResize(event, table, activeCell)) {
        return;
      }
    }

    view.requestAnimationFrame(refresh);
  };

  const onSelectionChange = () => {
    if (editOpen || liveColumnResizeCleanup || liveRowResizeCleanup) return;
    const root = resolveRoot();
    if (!root) return;
    const selection = root?.ownerDocument?.defaultView?.getSelection?.() || null;
    const element = elementFromNode(selection?.anchorNode);
    const table = element?.closest?.(`table[${DOC_TABLE_ATTR}="true"], table[${DOC_WRITER_TABLE_ATTR}="true"]`);
    if (table && root.contains(table)) {
      const cell = element?.closest?.("th, td");
      setFromTableAndCell(table, cell && table.contains(cell) ? cell : null);
      view.requestAnimationFrame(refresh);
    } else {
      activeTableId = "";
      activeCellId = "";
      activeRowId = "";
      activeColumnIndex = -1;
      view.requestAnimationFrame(refresh);
    }
  };

  const rootAtMount = resolveRoot();
  // Pointer-down stays in capture so a resize edge can take ownership before
  // contentEditable moves the caret. Pointer-move is bubble-phase so this table
  // cursor decision runs after Canvas's generic text-cursor pass.
  rootAtMount?.addEventListener("pointermove", onRootPointerMove, false);
  rootAtMount?.addEventListener("pointerleave", onRootPointerLeave, false);
  rootAtMount?.addEventListener("pointerdown", onRootPointerDown, true);
  documentValue.addEventListener("selectionchange", onSelectionChange);
  view.addEventListener("resize", refresh);
  view.addEventListener("scroll", refresh, true);

  function destroy() {
    if (editOpen) notifyEditingChange(false);
    editOpen = false;
    dragCleanup?.();
    resizeCleanup?.();
    previewResizeCleanup?.();
    previewColumnResizeCleanup?.();
    previewRowResizeCleanup?.();
    liveColumnResizeCleanup?.();
    liveRowResizeCleanup?.();
    clearLiveResizeHover();
    rootAtMount?.removeEventListener("pointermove", onRootPointerMove, false);
    rootAtMount?.removeEventListener("pointerleave", onRootPointerLeave, false);
    rootAtMount?.removeEventListener("pointerdown", onRootPointerDown, true);
    documentValue.removeEventListener("selectionchange", onSelectionChange);
    view.removeEventListener("pointerup", finishPreviewSelectionDrag, true);
    view.removeEventListener("pointercancel", finishPreviewSelectionDrag, true);
    view.removeEventListener("resize", refresh);
    view.removeEventListener("scroll", refresh, true);
    chrome.remove();
    resizeHandles.forEach(({ button }) => button.remove());
    resizeTooltip.remove();
    editHint.remove();
    overlay.remove();
  }

  // Keep a compact mobile layout without changing the document table itself.
  const applyResponsiveDialogLayout = () => {
    if (view.innerWidth < 900) {
      bodyGrid.style.gridTemplateColumns = "minmax(0, 1fr)";
      previewPane.style.position = "relative";
    } else {
      bodyGrid.style.gridTemplateColumns = "minmax(310px, 390px) minmax(0, 1fr)";
      previewPane.style.position = "sticky";
    }
    view.requestAnimationFrame(refreshPreviewHorizontalScroll);
  };
  view.addEventListener("resize", applyResponsiveDialogLayout);
  applyResponsiveDialogLayout();
  view.requestAnimationFrame(refresh);

  return {
    refresh,
    close,
    destroy() {
      view.removeEventListener("resize", applyResponsiveDialogLayout);
      destroy();
    },
    getState: () => ({
      activeTableId,
      activeCellId,
      activeRowId,
      activeColumnIndex,
      editOpen,
      previewCellId,
      previewRowId,
      previewColumnIndex,
      previewDirty,
    }),
  };
}

export function distributeTableColumns(table) {
  if (!table) return false;
  const count = getTableColumnCount(table);
  if (!count) return false;
  const colgroup = ensureTableColgroup(table, count, { inferWidths: false });
  Array.from(colgroup.children || []).forEach((column) => {
    column.style.width = `${100 / count}%`;
  });

  // Distribute the columns inside the table's CURRENT width. This command must
  // never turn a deliberately small/medium/px table back into a 100%-wide table.
  table.style.tableLayout = "fixed";
  table.setAttribute("data-doc-table-resized", "true");
  return true;
}

export function toggleTableHeaderRow(table) {
  if (!table) return false;

  // A full-width first row is often an AI-generated title, not the real column
  // header. Separate it first so header on/off never consumes the title.
  promoteSpanningTitleRowToCaption(table);
  normalizeTableSupplementalPresentation(table);

  const existingHead = findDirectChild(table, "THEAD");

  if (existingHead) {
    const body =
      table.tBodies?.[0] ||
      table.appendChild(table.ownerDocument.createElement("tbody"));
    const bodyColor =
      table.getAttribute("data-doc-table-body-color") || "#ffffff";
    const bodyTextColor = table.style.color || "#111827";

    Array.from(existingHead.rows || [])
      .reverse()
      .forEach((row) => {
        Array.from(row.cells || []).forEach((cell) => {
          const normalCell = table.ownerDocument.createElement("td");
          Array.from(cell.attributes || []).forEach((attribute) => {
            normalCell.setAttribute(attribute.name, attribute.value);
          });

          // Keep the exact header styling available for a future re-enable, but
          // make "Show header row = off" visibly look like ordinary body data now.
          const cachedHeaderStyle = String(cell.getAttribute("style") || "");
          if (cachedHeaderStyle) {
            normalCell.setAttribute(
              DOC_CELL_HEADER_STYLE_CACHE_ATTR,
              cachedHeaderStyle
            );
          }
          normalCell.innerHTML = cell.innerHTML;
          normalCell.style.backgroundColor = bodyColor;
          normalCell.style.color = bodyTextColor;
          normalCell.style.fontWeight = "400";
          normalCell.style.verticalAlign = "top";
          cell.replaceWith(normalCell);
        });
        body.insertBefore(row, body.firstChild);
      });

    existingHead.remove();
    normalizeCanvasTable(table, { inferWidths: false });
    return true;
  }

  const firstBodyRow = table.querySelector(
    `:scope > tbody > tr:not([${DOC_TABLE_NOTE_ROW_ATTR}="true"])`
  );
  if (!firstBodyRow) return false;

  const head = table.ownerDocument.createElement("thead");
  Array.from(firstBodyRow.cells || []).forEach((cell) => {
    const heading = table.ownerDocument.createElement("th");
    const cachedHeaderStyle = String(
      cell.getAttribute(DOC_CELL_HEADER_STYLE_CACHE_ATTR) || ""
    );

    Array.from(cell.attributes || []).forEach((attribute) => {
      if (attribute.name === DOC_CELL_HEADER_STYLE_CACHE_ATTR) return;
      heading.setAttribute(attribute.name, attribute.value);
    });
    heading.innerHTML = cell.innerHTML;

    if (cachedHeaderStyle) {
      heading.setAttribute("style", cachedHeaderStyle);
    } else {
      heading.style.backgroundColor =
        heading.style.backgroundColor || "#f1f5f9";
      heading.style.color = heading.style.color || "#0f172a";
      heading.style.fontWeight = heading.style.fontWeight || "700";
      heading.style.verticalAlign = heading.style.verticalAlign || "middle";
    }

    cell.replaceWith(heading);
  });

  head.appendChild(firstBodyRow);
  table.insertBefore(head, findDirectChild(table, "TBODY") || null);
  normalizeCanvasTable(table, { inferWidths: false });
  return true;
}


function readColumnWidthsPx(table) {
  const columnCount = getTableColumnCount(table);
  const colgroup = ensureTableColgroup(table, columnCount, { inferWidths: false });
  const tableWidth = Math.max(
    MIN_TABLE_WIDTH_PX,
    table.getBoundingClientRect?.().width || asFiniteNumber(table.style.width, 0) || 600
  );
  return Array.from(colgroup.children || []).map((column) => {
    const inline = String(column.style.width || "").trim();
    if (inline.endsWith("%")) {
      return Math.max(
        MIN_COLUMN_WIDTH_PX,
        (asFiniteNumber(inline.slice(0, -1), 0) / 100) * tableWidth
      );
    }
    if (inline.endsWith("px")) {
      return Math.max(
        MIN_COLUMN_WIDTH_PX,
        asFiniteNumber(inline.slice(0, -2), MIN_COLUMN_WIDTH_PX)
      );
    }
    return Math.max(
      MIN_COLUMN_WIDTH_PX,
      column.getBoundingClientRect?.().width || tableWidth / columnCount
    );
  });
}

export function applyColumnWidths(table, widths, { unit = "px" } = {}) {
  if (!table || !Array.isArray(widths) || !widths.length) return false;
  const colgroup = ensureTableColgroup(table, widths.length, { inferWidths: false });
  Array.from(colgroup.children || []).forEach((column, index) => {
    const width = Math.max(MIN_COLUMN_WIDTH_PX, asFiniteNumber(widths[index], 0));
    column.style.width = unit === "%" ? `${width}%` : `${width}px`;
  });
  table.style.tableLayout = "fixed";
  table.setAttribute("data-doc-table-resized", "true");
  return true;
}

export function resizeTableColumn(
  table,
  { columnIndex = 0, deltaX = 0, adjacent = true } = {}
) {
  if (!table) return false;
  const widths = readColumnWidthsPx(table);
  if (!widths[columnIndex]) return false;

  if (adjacent && widths[columnIndex + 1] !== undefined) {
    const maximumGrowth = widths[columnIndex + 1] - MIN_COLUMN_WIDTH_PX;
    const maximumShrink = widths[columnIndex] - MIN_COLUMN_WIDTH_PX;
    const appliedDelta = clamp(deltaX, -maximumShrink, maximumGrowth);
    widths[columnIndex] += appliedDelta;
    widths[columnIndex + 1] -= appliedDelta;
  } else {
    widths[columnIndex] = Math.max(
      MIN_COLUMN_WIDTH_PX,
      widths[columnIndex] + deltaX
    );
  }

  return applyColumnWidths(table, widths, { unit: "px" });
}

export function resizeTableWidth(
  table,
  { width = null, deltaX = 0, maxWidth = null, unit = "px" } = {}
) {
  if (!table) return false;
  const current =
    table.getBoundingClientRect?.().width ||
    asFiniteNumber(String(table.style.width || "").replace(/px$/, ""), 0) ||
    MIN_TABLE_WIDTH_PX;
  let next = width == null ? current + deltaX : asFiniteNumber(width, current);
  if (unit === "%") {
    next = clamp(next, 10, 100);
    table.style.width = `${next}%`;
  } else {
    next = Math.max(MIN_TABLE_WIDTH_PX, next);
    if (Number.isFinite(maxWidth)) next = Math.min(next, maxWidth);
    table.style.width = `${next}px`;
  }
  table.style.maxWidth = "100%";
  table.setAttribute("data-doc-table-resized", "true");
  return true;
}

function measuredTableRowHeights(table) {
  return Array.from(table?.rows || []).map((row) =>
    Math.max(
      MIN_ROW_HEIGHT_PX,
      row.getBoundingClientRect?.().height ||
        asFiniteNumber(String(row.style.height || "").replace(/px$/i, ""), 0) ||
        MIN_ROW_HEIGHT_PX
    )
  );
}

function applyTableHeightFromBaseline(table, baselineHeights, targetHeight) {
  if (!table) return false;
  const rows = Array.from(table.rows || []);
  if (!rows.length) return false;
  const baseline = rows.map((row, index) =>
    Math.max(
      MIN_ROW_HEIGHT_PX,
      asFiniteNumber(baselineHeights?.[index], 0) ||
        row.getBoundingClientRect?.().height ||
        MIN_ROW_HEIGHT_PX
    )
  );
  const baselineTotal = baseline.reduce((sum, value) => sum + value, 0);
  const minimumTotal = rows.length * MIN_ROW_HEIGHT_PX;
  const targetTotal = Math.max(
    minimumTotal,
    asFiniteNumber(targetHeight, baselineTotal)
  );

  let nextHeights = baseline.slice();
  if (targetTotal < baselineTotal) {
    let remainingShrink = baselineTotal - targetTotal;
    let active = baseline.map((_, index) => index);
    while (remainingShrink > 0.01 && active.length) {
      const capacityTotal = active.reduce(
        (sum, index) => sum + Math.max(0, nextHeights[index] - MIN_ROW_HEIGHT_PX),
        0
      );
      if (capacityTotal <= 0.01) break;
      const before = remainingShrink;
      active.forEach((index) => {
        const capacity = Math.max(0, nextHeights[index] - MIN_ROW_HEIGHT_PX);
        if (!capacity) return;
        const share = capacity / capacityTotal;
        const amount = Math.min(capacity, before * share);
        nextHeights[index] -= amount;
        remainingShrink -= amount;
      });
      active = active.filter(
        (index) => nextHeights[index] > MIN_ROW_HEIGHT_PX + 0.01
      );
      if (Math.abs(before - remainingShrink) < 0.01) break;
    }
  } else if (targetTotal > baselineTotal) {
    const extra = targetTotal - baselineTotal;
    const weightTotal = baseline.reduce((sum, value) => sum + Math.max(1, value), 0) || rows.length;
    nextHeights = baseline.map(
      (value) => value + extra * (Math.max(1, value) / weightTotal)
    );
  }

  rows.forEach((row, index) => {
    const next = Math.max(MIN_ROW_HEIGHT_PX, nextHeights[index] || MIN_ROW_HEIGHT_PX);
    row.style.height = `${next}px`;
    // Do not pin min-height to the previous expanded value; that was the regression
    // that could make the top/bottom middle handles appear unable to collapse.
    row.style.minHeight = `${MIN_ROW_HEIGHT_PX}px`;
    row.style.maxHeight = "none";
    Array.from(row.cells || []).forEach((cell) => {
      cell.style.height = "auto";
      cell.style.maxHeight = "none";
      cell.style.overflow = "visible";
    });
  });
  table.setAttribute("data-doc-table-resized", "true");
  return true;
}

export function resizeTableHeight(
  table,
  { height = null, deltaY = 0 } = {}
) {
  if (!table) return false;
  const baseline = measuredTableRowHeights(table);
  if (!baseline.length) return false;
  const currentTotal = baseline.reduce((sum, value) => sum + value, 0);
  const requested =
    height == null
      ? currentTotal + asFiniteNumber(deltaY, 0)
      : asFiniteNumber(height, currentTotal);
  return applyTableHeightFromBaseline(table, baseline, requested);
}


export function resizeTableRow(
  table,
  { rowId = "", rowIndex = null, height = null, deltaY = 0 } = {}
) {
  if (!table) return false;
  const row = rowId
    ? findRowById(table, rowId)
    : Number.isInteger(rowIndex)
    ? table.rows[rowIndex] || null
    : null;
  if (!row) return false;
  const current =
    row.getBoundingClientRect?.().height ||
    asFiniteNumber(String(row.style.height || "").replace(/px$/, ""), 0) ||
    MIN_ROW_HEIGHT_PX;
  const next = Math.max(
    MIN_ROW_HEIGHT_PX,
    height == null ? current + deltaY : asFiniteNumber(height, current)
  );
  row.style.height = `${next}px`;
  row.style.minHeight = `${next}px`;
  row.style.maxHeight = "none";
  Array.from(row.cells || []).forEach((cell) => {
    cell.style.height = "auto";
    cell.style.maxHeight = "none";
    cell.style.overflow = "visible";
  });
  return true;
}

export function setTableAlignment(table, alignment = "center") {
  return applyTableAlignmentPresentation(table, alignment);
}

export function placeCaretInCell(cell, atEnd = false) {
  if (!cell) return false;
  try {
    const range = cell.ownerDocument.createRange();
    range.selectNodeContents(cell);
    range.collapse(atEnd);
    const selection = cell.ownerDocument.defaultView?.getSelection?.();
    if (!selection) return false;
    selection.removeAllRanges();
    selection.addRange(range);
    return true;
  } catch (_) {
    return false;
  }
}

export function getActiveTableContext(root, selection = null) {
  if (!root) {
    return {
      active: false,
      table: null,
      cell: null,
      tableId: "",
      cellId: "",
      rowId: "",
      rowIndex: -1,
      columnIndex: -1,
    };
  }

  const liveSelection =
    selection || root.ownerDocument.defaultView?.getSelection?.() || null;
  const selectedElement = elementFromNode(liveSelection?.anchorNode);
  let cell = selectedElement?.closest?.("th, td") || null;
  if (cell && !root.contains(cell)) cell = null;
  if (!cell) {
    cell = root.querySelector(`[${DOC_CELL_SELECTED_ATTR}="true"]`);
  }

  let table = cell?.closest("table") || null;
  if (!table) {
    table = root.querySelector(`[${DOC_TABLE_SELECTED_ATTR}="true"]`);
  }
  if (table && !root.contains(table)) table = null;

  const grid = table ? buildTableGrid(table) : null;
  const origin = grid && cell ? originForCell(grid, cell) : null;

  return {
    active: Boolean(table),
    table,
    cell,
    tableId: table?.getAttribute(DOC_TABLE_ID_ATTR) || "",
    cellId: cell?.getAttribute(DOC_CELL_ID_ATTR) || "",
    rowId: cell?.closest("tr")?.getAttribute(DOC_ROW_ID_ATTR) || "",
    rowIndex: origin?.rowIndex ?? -1,
    columnIndex: origin?.columnIndex ?? -1,
    rowSpan: origin?.rowSpan ?? 1,
    colSpan: origin?.colSpan ?? 1,
  };
}

const TABLE_TEXT_SPLIT_TAGS = new Set([
  "ADDRESS",
  "BLOCKQUOTE",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "P",
  "PRE",
]);

function tableById(root, tableId) {
  if (!root || !tableId) return null;
  return root.querySelector(
    `table[${DOC_TABLE_ID_ATTR}="${escapeCssValue(tableId)}"]`
  );
}

function selectionRangeInsideRoot(root, selection) {
  if (!root || !selection || selection.rangeCount === 0) return null;
  try {
    const range = selection.getRangeAt(0).cloneRange();
    const startInside =
      range.startContainer === root || root.contains(range.startContainer);
    const endInside = range.endContainer === root || root.contains(range.endContainer);
    return startInside && endInside ? range : null;
  } catch (_) {
    return null;
  }
}

function selectionRangeFullyInsideCell(cell, selection) {
  if (!cell || !selection || selection.rangeCount === 0) return null;
  try {
    const range = selection.getRangeAt(0).cloneRange();
    const startInside =
      range.startContainer === cell || cell.contains(range.startContainer);
    const endInside = range.endContainer === cell || cell.contains(range.endContainer);
    return startInside && endInside ? range : null;
  } catch (_) {
    return null;
  }
}

function textOffsetToRangeStart(root, range) {
  if (!root || !range) return 0;
  try {
    const probe = root.ownerDocument.createRange();
    probe.selectNodeContents(root);
    probe.setEnd(range.startContainer, range.startOffset);
    return probe.toString().length;
  } catch (_) {
    return 0;
  }
}

function shouldProtectCellBoundaryDelete(cell, selection, key) {
  const range = selectionRangeFullyInsideCell(cell, selection);
  if (!range) return true;

  // A normal text selection wholly inside one cell is safe for the browser to
  // delete. What we prevent are selections/carets that can escape the cell and
  // let contentEditable merge/remove table structure.
  if (!range.collapsed) return false;

  const totalTextLength = String(cell.textContent || "").length;
  const offset = textOffsetToRangeStart(cell, range);
  if (key === "Backspace") return offset <= 0;
  if (key === "Delete") return offset >= totalTextLength;
  return false;
}

function canvasPageFromNode(root, node) {
  if (!root || !node) return null;
  const element = elementFromNode(node);
  const page = element?.closest?.(`[${DOC_CANVAS_PAGE_ATTR}="true"]`) || null;
  return page && root.contains(page) ? page : null;
}

function lastCanvasPage(root) {
  if (!root) return null;
  const pages = Array.from(
    root.querySelectorAll(`:scope > [${DOC_CANVAS_PAGE_ATTR}="true"]`)
  );
  return pages[pages.length - 1] || pages[0] || root;
}

function directPageChild(page, node) {
  if (!page || !node || node === page) return null;
  let element = elementFromNode(node);
  while (element && element.parentElement && element.parentElement !== page) {
    element = element.parentElement;
  }
  return element?.parentElement === page ? element : null;
}

function tableInsertionBlockIsEmpty(block) {
  if (!block) return true;
  const text = String(block.textContent || "")
    .replace(/\u00a0/g, " ")
    .trim();
  if (text) return false;
  return !block.querySelector?.(
    "img,table,ul,ol,video,audio,svg,canvas,iframe,object,embed"
  );
}

function ensureBlankEditingBoundary(block) {
  if (!block || !tableInsertionBlockIsEmpty(block)) return;
  block.replaceChildren(block.ownerDocument.createElement("br"));
}

function ensureEscapeParagraphAfterTable(table) {
  if (!table?.parentNode) return null;
  const next = table.nextSibling;
  if (next) return next;
  const paragraph = table.ownerDocument.createElement("p");
  paragraph.appendChild(table.ownerDocument.createElement("br"));
  table.parentNode.insertBefore(paragraph, table.nextSibling);
  return paragraph;
}

function placeCaretInFirstTableCell(table) {
  const firstCell = table?.querySelector?.("th, td") || null;
  if (!firstCell) return false;
  return placeCaretInCell(firstCell, false);
}

function defaultInsertNodeAtSelection(root, node, options = {}) {
  if (!root || !node) return false;

  const documentValue = root.ownerDocument;
  const selection =
    options.selection || documentValue.defaultView?.getSelection?.() || null;
  let range = selectionRangeInsideRoot(root, selection);

  try {
    if (!range) {
      const page = lastCanvasPage(root);
      if (!page) return false;
      page.appendChild(node);
      ensureEscapeParagraphAfterTable(node);
      placeCaretInFirstTableCell(node);
      return root.contains(node);
    }

    if (!range.collapsed) {
      range.deleteContents();
      range.collapse(true);
    }

    let page = canvasPageFromNode(root, range.startContainer);
    if (!page) {
      page = lastCanvasPage(root);
      if (!page) return false;
      range = documentValue.createRange();
      range.selectNodeContents(page);
      range.collapse(false);
    }

    const startElement = elementFromNode(range.startContainer);
    const containingTable = startElement?.closest?.("table") || null;
    const protectedBlock =
      startElement?.closest?.('[data-doc-protected-block="true"]') || null;

    if (containingTable && root.contains(containingTable)) {
      const tableId = containingTable.getAttribute(DOC_TABLE_ID_ATTR) || "";
      const fragments = tableId
        ? getLogicalTableFragments(root, tableId)
        : [containingTable];
      const anchor = fragments[fragments.length - 1] || containingTable;
      anchor.parentNode?.insertBefore(node, anchor.nextSibling);
    } else if (protectedBlock && root.contains(protectedBlock)) {
      protectedBlock.parentNode?.insertBefore(node, protectedBlock.nextSibling);
    } else if (range.startContainer === page) {
      page.insertBefore(node, page.childNodes[range.startOffset] || null);
    } else {
      const directChild = directPageChild(page, range.startContainer);
      const simpleTextBlock =
        directChild &&
        TABLE_TEXT_SPLIT_TAGS.has(directChild.tagName) &&
        !directChild.querySelector?.(
          "img,table,ul,ol,video,audio,svg,canvas,iframe,object,embed"
        );

      if (simpleTextBlock && directChild.contains(range.startContainer)) {
        const tailRange = documentValue.createRange();
        tailRange.selectNodeContents(directChild);
        tailRange.setStart(range.startContainer, range.startOffset);
        const tail = tailRange.extractContents();

        const afterBlock = directChild.cloneNode(false);
        afterBlock.removeAttribute("id");
        afterBlock.appendChild(tail);
        ensureBlankEditingBoundary(directChild);
        ensureBlankEditingBoundary(afterBlock);
        directChild.parentNode?.insertBefore(node, directChild.nextSibling);
        node.parentNode?.insertBefore(afterBlock, node.nextSibling);
      } else if (directChild?.parentNode === page) {
        directChild.parentNode.insertBefore(node, directChild.nextSibling);
      } else {
        page.appendChild(node);
      }
    }

    ensureEscapeParagraphAfterTable(node);
    placeCaretInFirstTableCell(node);
    return root.contains(node);
  } catch (_) {
    try {
      if (node.parentNode && !root.contains(node)) node.remove();
    } catch (_) {}
    return false;
  }
}

function tableExistsInSerializedHtml(documentValue, html, tableId) {
  if (!documentValue || !tableId || !html) return false;
  try {
    const holder = documentValue.createElement("div");
    holder.innerHTML = String(html || "");
    return Boolean(tableById(holder, tableId));
  } catch (_) {
    return false;
  }
}

function normalizeTableInsertRequest(options = {}) {
  const source = normalizeSource(options.source || "toolbar");
  const rows = Math.max(1, Number.parseInt(String(options.rows ?? 3), 10) || 1);
  const columns = Math.max(
    1,
    Number.parseInt(String(options.columns ?? 3), 10) || 1
  );

  const rowLimit = source === "toolbar" ? MANUAL_CREATE_MAX_ROWS : IMPORT_MAX_ROWS;
  const columnLimit =
    source === "toolbar" ? MANUAL_CREATE_MAX_COLUMNS : IMPORT_MAX_COLUMNS;

  if (rows > rowLimit || columns > columnLimit) {
    return {
      valid: false,
      source,
      rows,
      columns,
      reason: "table-size-safety-limit",
    };
  }

  return { valid: true, source, rows, columns, reason: "" };
}


export function createDocWriterTable({
  getRoot = null,
  serializeDocument = null,
  commitDocument = null,
  onChange = null,
  requestRepagination = null,
  focusCell = null,
  getSelection = null,
  insertNodeAtSelection = null,
  enableObjectControls = true,
  onRequestTableMove = null,
  onRequestTableResize = null,
  confirmDeleteTable = null,
  onEditingChange = null,
} = {}) {
  const resolveRoot = () =>
    typeof getRoot === "function" ? getRoot() : getRoot || null;

  // Lifecycle state belongs to the DocWriterTable instance. These variables must
  // live in this factory scope because attach()/detach()/refreshControls() use them.
  let controlsController = null;
  let boundKeyboardRoot = null;
  let boundKeyboardHandler = null;

  const selectionProvider = (root) =>
    typeof getSelection === "function"
      ? getSelection()
      : root?.ownerDocument?.defaultView?.getSelection?.() || null;

  const focusCellById = (cellId, atEnd = false) => {
    if (!cellId) return false;
    if (typeof focusCell === "function") {
      return focusCell(cellId, atEnd) !== false;
    }
    const root = resolveRoot();
    const cell = root?.querySelector(
      `[${DOC_CELL_ID_ATTR}="${escapeCssValue(cellId)}"]`
    );
    return placeCaretInCell(cell, atEnd);
  };

  const repaginate = (details = {}) => {
    if (typeof requestRepagination === "function") {
      requestRepagination({
        reason: "table",
        preserveSelection: true,
        ...details,
      });
    }
  };

  const snapshotHostDocument = (root = resolveRoot()) => {
    if (!root) return { html: "", method: "none" };

    // In Canvas mode, the parent serializer is authoritative. If it returns an
    // empty string, Table does NOT replace it with its own competing document
    // serializer. The only fallback below is for standalone/non-Canvas use where
    // no host serializer exists at all.
    if (typeof serializeDocument === "function") {
      try {
        return {
          html: String(serializeDocument(root) || ""),
          method: "host",
        };
      } catch (_) {
        return { html: "", method: "host-error" };
      }
    }

    try {
      return {
        html: String(serializeCanvasTableDocument(root) || ""),
        method: "standalone-fallback",
      };
    } catch (_) {
      return { html: "", method: "none" };
    }
  };

  const cloneLogicalTableForMutation = (root, tableId) => {
    if (!root || !tableId) return { holder: null, table: null };

    const fragments = getLogicalTableFragments(root, tableId);
    if (!fragments.length) return { holder: null, table: null };

    const holder = root.ownerDocument.createElement("div");
    fragments.forEach((fragment) => {
      holder.appendChild(fragment.cloneNode(true));
    });

    const table = reassembleLogicalTable(holder, tableId, {
      removeFragments: true,
      normalize: true,
    });

    return { holder, table };
  };

  const serializeLogicalTableEnvelope = (root, tableId) => {
    const cloned = cloneLogicalTableForMutation(root, tableId);
    return cloned.table ? String(cloned.table.outerHTML || "") : "";
  };

  const serializeLogicalTableEnvelopes = (root, tableIds = []) => {
    if (!root) return "";
    const ids = Array.from(new Set((tableIds || []).filter(Boolean)));
    return ids
      .map((tableId) => serializeLogicalTableEnvelope(root, tableId))
      .filter(Boolean)
      .join("");
  };

  const commit = (tableHtml, details = {}) => {
    const root = resolveRoot();
    const objectHtml = String(tableHtml || "");
    let committedDocumentHtml = "";

    if (typeof commitDocument === "function") {
      try {
        const result = commitDocument(objectHtml, {
          ...details,
          objectHtml,
          objectType: "table",
        });
        if (typeof result === "string") {
          committedDocumentHtml = result;
        } else if (result && typeof result.html === "string") {
          committedDocumentHtml = result.html;
        }
      } catch (_) {
        committedDocumentHtml = "";
      }
    } else {
      // Standalone mode: the live table mutation already happened in `root`.
      // Never replace root.innerHTML with the table envelope. If a caller needs a
      // full document notification, obtain it through the host/standalone snapshot.
      committedDocumentHtml = snapshotHostDocument(root).html;
      if (typeof onChange === "function") {
        onChange(committedDocumentHtml || objectHtml, {
          ...details,
          objectHtml,
          objectType: "table",
        });
      }
    }

    // Repagination is a document-layout request to the host, not another table
    // document commit.
    repaginate({
      ...details,
      objectHtml,
      objectType: "table",
    });

    if (details.focusCellId) {
      const restore = () =>
        focusCellById(details.focusCellId, details.focusAtEnd);
      if (typeof window !== "undefined") {
        window.requestAnimationFrame(() =>
          window.requestAnimationFrame(restore)
        );
      } else {
        restore();
      }
    }

    return committedDocumentHtml || objectHtml;
  };

  const activeContext = () => {
    const root = resolveRoot();
    return getActiveTableContext(root, selectionProvider(root));
  };

  const insertNodeRobustly = (root, node, options = {}) => {
    if (!root || !node) {
      return {
        inserted: false,
        table: null,
        method: "none",
        reason: "missing-root-or-node",
      };
    }

    const tableId = node.getAttribute?.(DOC_TABLE_ID_ATTR) || "";
    const resolveInsertedTable = () =>
      tableById(root, tableId) || (root.contains(node) ? node : null);

    if (typeof insertNodeAtSelection === "function") {
      try {
        const result = insertNodeAtSelection(root, node, options);
        const insertedTable = resolveInsertedTable();

        if (insertedTable) {
          return {
            inserted: true,
            table: insertedTable,
            method: "host",
            reason:
              result === false ? "host-inserted-despite-false-return" : "",
          };
        }
      } catch (_) {
        // Fall through to the deterministic table-owned insertion path.
      }
    }

    try {
      if (node.parentNode && !root.contains(node)) node.remove();
    } catch (_) {}

    const selection = selectionProvider(root);
    const inserted = defaultInsertNodeAtSelection(root, node, {
      ...options,
      selection,
    });
    const insertedTable = resolveInsertedTable();

    return {
      inserted: Boolean(inserted && insertedTable),
      table: insertedTable,
      method: inserted && insertedTable ? "docwriter-table-fallback" : "none",
      reason: inserted && insertedTable ? "" : "table-insertion-failed",
    };
  };

  const mutate = (tableOrId, mutator, details = {}) => {
    const root = resolveRoot();
    if (!root || typeof mutator !== "function") {
      return { changed: false, html: "", focusCellId: "" };
    }

    const currentContext = activeContext();
    const tableId =
      typeof tableOrId === "string"
        ? tableOrId
        : tableOrId?.getAttribute?.(DOC_TABLE_ID_ATTR) ||
          currentContext.tableId;
    if (!tableId) return { changed: false, html: "", focusCellId: "" };

    const operation = String(details.operation || "table-mutation");
    const allowTableRemoval =
      details.allowTableRemoval === true ||
      operation === TABLE_COMMANDS.DELETE_TABLE;

    // Undo metadata comes from the Canvas serializer. It is optional for the
    // table mutation itself and is never used as the mutation workspace.
    const beforeSnapshot = snapshotHostDocument(root);

    const { holder, table: logicalTable } =
      cloneLogicalTableForMutation(root, tableId);
    if (!holder || !logicalTable) {
      return {
        changed: false,
        html: "",
        focusCellId: "",
        reason: "table-not-found",
      };
    }

    const result = mutator(logicalTable, holder) || {};
    const changed = result.changed !== false;
    if (!changed) {
      return {
        changed: false,
        html: beforeSnapshot.html,
        beforeHtml: beforeSnapshot.html,
        focusCellId: result.focusCellId || details.focusCellId || "",
        operation,
        ...result,
      };
    }

    normalizeCanvasTables(holder, {
      source: "reopened",
      inferWidths: false,
    });

    const preservedTable = tableById(holder, tableId);
    if (!preservedTable && !allowTableRemoval) {
      return {
        changed: false,
        html: beforeSnapshot.html,
        beforeHtml: beforeSnapshot.html,
        focusCellId: details.focusCellId || "",
        operation,
        reason: "table-preservation-guard",
      };
    }

    // This is intentionally an OBJECT envelope, not the document.
    const tableHtml = preservedTable
      ? String(preservedTable.outerHTML || "")
      : "";

    const transactionId = createDocumentObjectId("tabletx");
    const committedHtml = commit(tableHtml, {
      ...details,
      tableId,
      operation,
      transactionType: "table",
      transactionId,
      beforeHtml: beforeSnapshot.html,
      afterHtml: tableHtml,
      serializationMethod: "table-object",
      focusCellId: result.focusCellId || details.focusCellId || "",
      focusAtEnd: result.focusAtEnd ?? details.focusAtEnd ?? false,
    });

    const committedRoot = resolveRoot();
    const committedTable = committedRoot
      ? tableById(committedRoot, tableId)
      : null;

    if (!allowTableRemoval && !committedTable) {
      return {
        ...result,
        changed: false,
        html: committedHtml,
        beforeHtml: beforeSnapshot.html,
        operation,
        transactionId,
        focusCellId: result.focusCellId || details.focusCellId || "",
        reason: "table-lost-after-commit",
      };
    }

    if (allowTableRemoval && committedTable) {
      return {
        ...result,
        changed: false,
        html: committedHtml,
        beforeHtml: beforeSnapshot.html,
        operation,
        transactionId,
        focusCellId: "",
        reason: "explicit-table-delete-did-not-commit",
      };
    }

    return {
      changed: true,
      html: committedHtml,
      beforeHtml: beforeSnapshot.html,
      operation,
      transactionId,
      focusCellId: result.focusCellId || details.focusCellId || "",
      ...result,
    };
  };

  const insertTable = (options = {}) => {
    const root = resolveRoot();
    if (!root) {
      return {
        changed: false,
        table: null,
        firstCellId: "",
        focusCellId: "",
        reason: "editor-root-unavailable",
      };
    }

    const beforeSnapshot = snapshotHostDocument(root);

    const request = normalizeTableInsertRequest(options);
    if (!request.valid) {
      return {
        changed: false,
        table: null,
        firstCellId: "",
        focusCellId: "",
        reason: request.reason,
      };
    }

    let table = null;
    try {
      table = createCanvasTable(
        {
          ...options,
          rows: request.rows,
          columns: request.columns,
          source: request.source,
        },
        root.ownerDocument
      );
    } catch (_) {
      return {
        changed: false,
        table: null,
        firstCellId: "",
        focusCellId: "",
        reason: "table-construction-failed",
      };
    }

    const tableId = table.getAttribute(DOC_TABLE_ID_ATTR) || "";
    const firstCellId =
      table.querySelector("th, td")?.getAttribute(DOC_CELL_ID_ATTR) || "";

    const insertion = insertNodeRobustly(root, table, {
      ...options,
      rows: request.rows,
      columns: request.columns,
      source: request.source,
    });
    if (!insertion.inserted || !insertion.table) {
      return {
        changed: false,
        table: null,
        firstCellId: "",
        focusCellId: "",
        reason: insertion.reason || "table-insertion-failed",
        insertionMethod: insertion.method,
      };
    }

    const liveTable = normalizeCanvasTable(insertion.table, {
      source: request.source,
      inferWidths: true,
    });
    ensureEscapeParagraphAfterTable(liveTable);

    const tableHtml = String(liveTable?.outerHTML || "");
    if (!tableHtml) {
      return {
        changed: false,
        table: liveTable,
        firstCellId,
        focusCellId: firstCellId,
        reason: "table-object-serialization-failed",
        insertionMethod: insertion.method,
      };
    }

    const transactionId = createDocumentObjectId("tabletx");
    const committedHtml = commit(tableHtml, {
      tableId,
      focusCellId: firstCellId,
      operation: "insert-table",
      transactionType: "table",
      transactionId,
      beforeHtml: beforeSnapshot.html,
      afterHtml: tableHtml,
      source: request.source,
      insertionMethod: insertion.method,
      serializationMethod: "table-object",
      rows: request.rows,
      columns: request.columns,
    });

    const committedTable = tableById(resolveRoot(), tableId);
    if (!committedTable) {
      return {
        changed: false,
        table: null,
        tableId,
        firstCellId,
        focusCellId: "",
        html: committedHtml,
        beforeHtml: beforeSnapshot.html,
        reason: "table-lost-after-commit",
        transactionId,
        insertionMethod: insertion.method,
        serializationMethod: "table-object",
      };
    }

    return {
      changed: true,
      table: committedTable,
      tableId,
      firstCellId,
      focusCellId: firstCellId,
      html: committedHtml,
      beforeHtml: beforeSnapshot.html,
      transactionId,
      reason: "",
      insertionMethod: insertion.method,
      serializationMethod: "table-object",
    };
  };

  const importTable = (value, options = {}) => {
    const root = resolveRoot();
    if (!root) return { changed: false, tables: [], kind: "none" };

    const beforeSnapshot = snapshotHostDocument(root);
    const source = normalizeSource(options.source || "ai");
    const recognized = recognizeTableInput(value, {
      source,
      documentValue: root.ownerDocument,
    });
    if (!recognized.tables.length) return { changed: false, ...recognized };

    const insertedTables = [];
    const insertedIds = [];
    let firstCellId = "";
    let firstInsertionMethod = "";

    for (const recognizedTable of recognized.tables) {
      const clone = recognizedTable.cloneNode(true);
      normalizeCanvasTable(clone, {
        source,
        inferWidths: true,
        preserveTableId: true,
      });

      const insertion = insertNodeRobustly(root, clone, {
        ...options,
        source,
      });
      if (!insertion.inserted || !insertion.table) continue;

      const liveTable = normalizeCanvasTable(insertion.table, {
        source,
        inferWidths: true,
      });
      ensureEscapeParagraphAfterTable(liveTable);

      const tableId = liveTable.getAttribute(DOC_TABLE_ID_ATTR) || "";
      if (tableId) insertedIds.push(tableId);
      if (!firstCellId) {
        firstCellId =
          liveTable
            .querySelector("th, td")
            ?.getAttribute(DOC_CELL_ID_ATTR) || "";
        firstInsertionMethod = insertion.method;
      }
      insertedTables.push(liveTable);
    }

    if (!insertedTables.length) {
      return {
        changed: false,
        ...recognized,
        tables: [],
        reason: "table-insertion-failed",
      };
    }

    const tableHtml = insertedTables
      .map((table) => String(table.outerHTML || ""))
      .filter(Boolean)
      .join("");

    const transactionId = createDocumentObjectId("tabletx");
    const committedHtml = commit(tableHtml, {
      tableId: insertedIds[0] || "",
      tableIds: insertedIds,
      focusCellId: firstCellId,
      operation: "import-table",
      transactionType: "table",
      transactionId,
      beforeHtml: beforeSnapshot.html,
      afterHtml: tableHtml,
      source,
      insertionMethod: firstInsertionMethod,
      serializationMethod: "table-object",
    });

    const committedRoot = resolveRoot();
    const committedTables = insertedIds
      .map((tableId) => tableById(committedRoot, tableId))
      .filter(Boolean);

    if (committedTables.length !== insertedIds.length) {
      return {
        changed: false,
        ...recognized,
        tables: committedTables,
        html: committedHtml,
        beforeHtml: beforeSnapshot.html,
        firstCellId,
        focusCellId: "",
        reason: "table-lost-after-commit",
        transactionId,
        insertionMethod: firstInsertionMethod,
        serializationMethod: "table-object",
      };
    }

    return {
      changed: true,
      ...recognized,
      tables: committedTables,
      html: committedHtml,
      beforeHtml: beforeSnapshot.html,
      transactionId,
      firstCellId,
      focusCellId: firstCellId,
      reason: "",
      insertionMethod: firstInsertionMethod,
      serializationMethod: "table-object",
    };
  };


  const commitTableDraft = ({
    tableId = "",
    table = null,
    beforeHtml = "",
    operation = "edit-table",
    source = "table-edit-dialog",
    focusCellId = "",
    focusAtEnd = false,
  } = {}) => {
    const root = resolveRoot();
    const id = String(tableId || table?.getAttribute?.(DOC_TABLE_ID_ATTR) || "");
    if (!root || !id || !table) {
      return {
        changed: false,
        html: "",
        tableId: id,
        reason: !root
          ? "editor-root-unavailable"
          : !id
          ? "missing-table-id"
          : "missing-table-draft",
      };
    }

    const existingFragments = getLogicalTableFragments(root, id);
    if (!existingFragments.length) {
      return {
        changed: false,
        html: snapshotHostDocument(root).html,
        tableId: id,
        reason: "table-not-found-before-draft-commit",
      };
    }

    const previousHtml =
      String(beforeHtml || "") || snapshotHostDocument(root).html;
    const draft = table.cloneNode(true);
    draft.setAttribute(DOC_TABLE_ID_ATTR, id);
    draft.removeAttribute("data-doc-table-preview");
    draft.removeAttribute(DOC_WRITER_TABLE_EDIT_OPEN_ATTR);
    draft.querySelectorAll("th,td").forEach((cell) => {
      cell.removeAttribute("contenteditable");
      cell.removeAttribute("spellcheck");
      cell.removeAttribute("data-doc-table-preview-selected");
      cell.removeAttribute("data-doc-table-preview-multiselect");
      cell.style.removeProperty("outline");
      cell.style.removeProperty("outline-offset");
      cell.style.removeProperty("cursor");
    });

    normalizeCanvasTable(draft, {
      source:
        draft.getAttribute(DOC_TABLE_SOURCE_ATTR) ||
        existingFragments[0].getAttribute(DOC_TABLE_SOURCE_ATTR) ||
        "reopened",
      inferWidths: false,
      preserveTableId: true,
    });
    draft.style.maxWidth = "100%";

    const tableHtml = String(draft.outerHTML || "");
    if (!tableHtml) {
      return {
        changed: false,
        html: snapshotHostDocument(root).html,
        beforeHtml: previousHtml,
        tableId: id,
        reason: "table-draft-serialization-failed",
      };
    }

    const currentTableHtml = serializeLogicalTableEnvelope(root, id);
    if (currentTableHtml && currentTableHtml === tableHtml) {
      return {
        changed: false,
        html: snapshotHostDocument(root).html,
        beforeHtml: previousHtml,
        tableId: id,
        operation,
        reason: "no-change",
      };
    }

    // In Canvas mode the host owns the replacement. Standalone mode has no host
    // replacement callback, so apply only the logical table locally before the
    // normal standalone notification path.
    if (typeof commitDocument !== "function") {
      const replacement = draft.cloneNode(true);
      existingFragments[0].replaceWith(replacement);
      existingFragments.slice(1).forEach((fragment) => fragment.remove());
    }

    const transactionId = createDocumentObjectId("tabletx");
    const committedHtml = commit(tableHtml, {
      tableId: id,
      operation,
      transactionType: "table",
      transactionId,
      beforeHtml: previousHtml,
      afterHtml: tableHtml,
      focusCellId,
      focusAtEnd,
      serializationMethod: "table-object",
      source,
    });

    const committedTable = tableById(resolveRoot(), id);
    if (!committedTable) {
      return {
        changed: false,
        html: committedHtml,
        beforeHtml: previousHtml,
        tableId: id,
        operation,
        transactionId,
        reason: "table-lost-after-draft-commit",
      };
    }

    return {
      changed: true,
      html: committedHtml,
      beforeHtml: previousHtml,
      tableId: id,
      operation,
      transactionId,
      table: committedTable,
      reason: "",
    };
  };


  const commitLiveTable = ({
    tableId = "",
    beforeHtml = "",
    operation = "table-live-mutation",
    source = "docwriter-table",
    focusCellId = "",
    focusAtEnd = false,
  } = {}) => {
    const root = resolveRoot();
    if (!root) {
      return {
        changed: false,
        html: "",
        reason: "editor-root-unavailable",
      };
    }

    const id = String(tableId || activeContext().tableId || "");
    if (!id) {
      return { changed: false, html: "", reason: "missing-table-id" };
    }

    const previousHtml = String(beforeHtml || "");
    const currentSnapshot = snapshotHostDocument(root);
    if (
      previousHtml &&
      currentSnapshot.html &&
      previousHtml === currentSnapshot.html
    ) {
      return {
        changed: false,
        html: currentSnapshot.html,
        beforeHtml: previousHtml,
        tableId: id,
        operation,
        reason: "no-change",
      };
    }

    const tableHtml = serializeLogicalTableEnvelope(root, id);
    if (!tableHtml) {
      return {
        changed: false,
        html: currentSnapshot.html,
        reason: "table-object-serialization-failed-after-live-mutation",
      };
    }

    const transactionId = createDocumentObjectId("tabletx");
    const committedHtml = commit(tableHtml, {
      tableId: id,
      operation,
      transactionType: "table",
      transactionId,
      beforeHtml: previousHtml,
      afterHtml: tableHtml,
      focusCellId,
      focusAtEnd,
      serializationMethod: "table-object",
      source,
    });

    const committedTable = tableById(resolveRoot(), id);
    if (!committedTable) {
      return {
        changed: false,
        html: committedHtml,
        beforeHtml: previousHtml,
        tableId: id,
        operation,
        transactionId,
        reason: "table-lost-after-commit",
      };
    }

    return {
      changed: true,
      html: committedHtml,
      beforeHtml: previousHtml,
      tableId: id,
      operation,
      transactionId,
      table: committedTable,
      reason: "",
    };
  };


  const withActive = (callback) => {
    const context = activeContext();
    if (!context.active) return { changed: false, html: "" };
    return callback(context);
  };

  const handleKeyDown = (event) => {
    if (!event) return false;
    const root = resolveRoot();
    if (!root) return false;
    const context = activeContext();
    if (!context.tableId) return false;

    const isMac =
      typeof navigator !== "undefined" &&
      /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || "");
    const accelerator = isMac ? event.metaKey : event.ctrlKey;

    // Backspace/Delete are text-editing keys inside a table. They are never
    // allowed to become implicit structural deletion. If the whole table is
    // selected, or the caret is at a boundary where contentEditable could
    // escape the current cell, consume the key. Ordinary text/range deletion
    // wholly inside a cell is left native so typing remains natural.
    if (["Backspace", "Delete"].includes(event.key)) {
      if (!context.cell) {
        event.preventDefault();
        return true;
      }
      const selection = selectionProvider(root);
      if (shouldProtectCellBoundaryDelete(context.cell, selection, event.key)) {
        event.preventDefault();
        return true;
      }
      return false;
    }

    if (!context.cell) return false;

    // Word-like table navigation: Tab advances cells and Tab from the final
    // logical cell appends one new row. Plain Enter is intentionally not
    // intercepted so users can create another paragraph/line inside the cell
    // and the row grows naturally with its content.
    if (event.key === "Tab") {
      event.preventDefault();
      const cells = getLogicalTableCells(root, context.tableId);
      const currentIndex = cells.findIndex(
        (cell) => cell.getAttribute(DOC_CELL_ID_ATTR) === context.cellId
      );

      if (event.shiftKey) {
        const previous = cells[Math.max(0, currentIndex - 1)] || cells[0] || null;
        if (previous) placeCaretInCell(previous, true);
        return true;
      }

      const next = cells[currentIndex + 1] || null;
      if (next) {
        placeCaretInCell(next, false);
        return true;
      }

      mutate(
        context.tableId,
        (table) =>
          addTableRow(table, {
            where: "below",
            rowId: context.rowId,
            cellId: context.cellId,
            focusColumn: 0,
          }),
        {
          operation: TABLE_COMMANDS.INSERT_ROW_BELOW,
          source: "keyboard-tab",
        }
      );
      return true;
    }

    // Keep Ctrl/Cmd+Enter as an explicit convenience for adding a row while
    // plain Enter remains multiline cell editing.
    if (event.key === "Enter" && accelerator) {
      event.preventDefault();
      mutate(
        context.tableId,
        (table) =>
          addTableRow(table, {
            where: "below",
            rowId: context.rowId,
            cellId: context.cellId,
            focusColumn: context.columnIndex,
          }),
        {
          operation: TABLE_COMMANDS.INSERT_ROW_BELOW,
          source: "keyboard-accelerator",
        }
      );
      return true;
    }

    return false;
  };

  return {
    normalize(options = {}) {
      const root = resolveRoot();
      if (!root) return [];
      return normalizeCanvasTables(root, options);
    },
    parseMarkdownTable,
    recognizeTableInput,
    createTable: (options = {}) =>
      createCanvasTable(options, resolveRoot()?.ownerDocument),
    insertTable,
    importTable,
    getActiveContext: activeContext,
    getLogicalFragments(tableOrId) {
      return getLogicalTableFragments(resolveRoot(), tableOrId);
    },
    getLogicalCells(tableOrId) {
      return getLogicalTableCells(resolveRoot(), tableOrId);
    },
    reassemble(tableOrId) {
      const root = resolveRoot();
      if (!root) return null;
      return reassembleLogicalTable(root, tableOrId, {
        removeFragments: true,
        normalize: true,
      });
    },
    reassembleAndRepaginate(tableOrId = null) {
      const root = resolveRoot();
      if (!root) return { changed: false, html: "" };

      const beforeSnapshot = snapshotHostDocument(root);
      let tables = [];

      if (tableOrId) {
        const table = reassembleLogicalTable(root, tableOrId, {
          removeFragments: true,
          normalize: true,
        });
        if (table) tables = [table];
      } else {
        tables = reassembleAllLogicalTables(root, {
          removeFragments: true,
          normalize: true,
        });
      }

      if (!tables.length) {
        return {
          changed: false,
          html: beforeSnapshot.html,
          beforeHtml: beforeSnapshot.html,
          reason: "table-not-found",
        };
      }

      const tableId =
        typeof tableOrId === "string"
          ? tableOrId
          : tables[0]?.getAttribute?.(DOC_TABLE_ID_ATTR) || "";
      const tableHtml = tables
        .map((table) => String(table.outerHTML || ""))
        .filter(Boolean)
        .join("");
      const transactionId = createDocumentObjectId("tabletx");
      const committedHtml = commit(tableHtml, {
        tableId,
        operation: "reassemble-table",
        transactionType: "table",
        transactionId,
        beforeHtml: beforeSnapshot.html,
        afterHtml: tableHtml,
        serializationMethod: "table-object",
      });

      return {
        changed: true,
        html: committedHtml,
        beforeHtml: beforeSnapshot.html,
        tableId,
        transactionId,
      };
    },
    mutate,
    getInteractionModel(options = {}) {
      const context = activeContext();
      const root = resolveRoot();
      let table = context.table;
      let cell = context.cell;
      if (options.tableId && root) {
        table = root.querySelector(
          `table[${DOC_TABLE_ID_ATTR}="${escapeCssValue(options.tableId)}"]`
        ) || table;
      }
      if (options.cellId && table) {
        cell = findCellById(table, options.cellId) || cell;
      }
      return getTableInteractionModel(table, cell, {
        selectedCellIds: options.selectedCellIds || [],
      });
    },
    getContextActions(options = {}) {
      const model = this.getInteractionModel(options);
      return model.actions;
    },
    insertRow(options = {}) {
      const context = activeContext();
      const tableId = options.tableId || context.tableId;
      if (!tableId) return { changed: false, html: "" };
      const where = options.where === "above" ? "above" : "below";
      return mutate(
        tableId,
        (table) =>
          addTableRow(table, {
            where,
            rowId: options.rowId || context.rowId,
            cellId: options.cellId || context.cellId,
            focusColumn: Number.isInteger(options.focusColumn)
              ? options.focusColumn
              : context.columnIndex,
          }),
        {
          operation:
            where === "above"
              ? TABLE_COMMANDS.INSERT_ROW_ABOVE
              : TABLE_COMMANDS.INSERT_ROW_BELOW,
          source: options.source || "table-command",
        }
      );
    },
    insertColumn(options = {}) {
      const context = activeContext();
      const tableId = options.tableId || context.tableId;
      if (!tableId) return { changed: false, html: "" };
      const where = options.where === "left" ? "left" : "right";
      return mutate(
        tableId,
        (table) =>
          addTableColumn(table, {
            where,
            cellId: options.cellId || context.cellId,
            columnIndex: Number.isInteger(options.columnIndex)
              ? options.columnIndex
              : context.columnIndex,
          }),
        {
          operation:
            where === "left"
              ? TABLE_COMMANDS.INSERT_COLUMN_LEFT
              : TABLE_COMMANDS.INSERT_COLUMN_RIGHT,
          source: options.source || "table-command",
        }
      );
    },
    splitCell(options = {}) {
      const context = activeContext();
      const tableId = options.tableId || context.tableId;
      const cellId = options.cellId || context.cellId;
      if (!tableId || !cellId) return { changed: false, html: "" };
      const direction = options.direction === "rows" ? "rows" : "columns";
      return mutate(
        tableId,
        (table) => splitTableCell(table, { cellId, direction }),
        {
          operation:
            direction === "rows"
              ? TABLE_COMMANDS.SPLIT_CELL_ROWS
              : TABLE_COMMANDS.SPLIT_CELL_COLUMNS,
          source: options.source || "table-command",
        }
      );
    },
    mergeCells(options = {}) {
      const context = activeContext();
      const tableId = options.tableId || context.tableId;
      if (!tableId) return { changed: false, html: "" };
      return mutate(
        tableId,
        (table) => mergeTableCells(table, { cellIds: options.cellIds || [] }),
        {
          operation: TABLE_COMMANDS.MERGE_CELLS,
          source: options.source || "table-command",
        }
      );
    },
    moveTable(options = {}) {
      const root = resolveRoot();
      const context = activeContext();
      const tableId = options.tableId || context.tableId;
      if (!root || !tableId || !options.targetNode) {
        return { changed: false, html: "", reason: "missing-move-target" };
      }

      const beforeSnapshot = snapshotHostDocument(root);

      const result = moveLogicalTable(root, tableId, options.targetNode, {
        position: options.position || "before",
      });
      if (!result.changed) return { ...result, html: beforeSnapshot.html };

      const tableHtml = serializeLogicalTableEnvelope(root, tableId);
      if (!tableHtml) {
        return {
          ...result,
          changed: false,
          html: beforeSnapshot.html,
          reason: "table-object-serialization-failed-after-move",
        };
      }

      const transactionId = createDocumentObjectId("tabletx");
      const committedHtml = commit(tableHtml, {
        tableId,
        operation: "move-table",
        transactionType: "table",
        transactionId,
        beforeHtml: beforeSnapshot.html,
        afterHtml: tableHtml,
        preserveSelection: false,
        serializationMethod: "table-object",
        source: options.source || "table-drag",
      });

      const committedTable = tableById(resolveRoot(), tableId);
      if (!committedTable) {
        return {
          ...result,
          changed: false,
          table: null,
          html: committedHtml,
          beforeHtml: beforeSnapshot.html,
          transactionId,
          reason: "table-lost-after-commit",
        };
      }

      return {
        ...result,
        table: committedTable,
        html: committedHtml,
        beforeHtml: beforeSnapshot.html,
        transactionId,
        reason: "",
      };
    },
    runCommand(command, options = {}) {
      switch (command) {
        case TABLE_COMMANDS.INSERT_ROW_ABOVE:
          return this.insertRow({ ...options, where: "above" });
        case TABLE_COMMANDS.INSERT_ROW_BELOW:
          return this.insertRow({ ...options, where: "below" });
        case TABLE_COMMANDS.INSERT_COLUMN_LEFT:
          return this.insertColumn({ ...options, where: "left" });
        case TABLE_COMMANDS.INSERT_COLUMN_RIGHT:
          return this.insertColumn({ ...options, where: "right" });
        case TABLE_COMMANDS.SPLIT_CELL_COLUMNS:
          return this.splitCell({ ...options, direction: "columns" });
        case TABLE_COMMANDS.SPLIT_CELL_ROWS:
          return this.splitCell({ ...options, direction: "rows" });
        case TABLE_COMMANDS.MERGE_CELLS:
          return this.mergeCells(options);
        case TABLE_COMMANDS.DELETE_ROW:
          return this.deleteRow(options);
        case TABLE_COMMANDS.DELETE_COLUMN:
          return this.deleteColumn(options);
        case TABLE_COMMANDS.DELETE_TABLE:
          return this.deleteTable(options);
        case TABLE_COMMANDS.DISTRIBUTE_COLUMNS:
          return this.distributeColumns(options);
        case TABLE_COMMANDS.TOGGLE_HEADER_ROW:
          return this.toggleHeaderRow(options);
        case TABLE_COMMANDS.ALIGN_LEFT:
          return this.alignTable("left", options);
        case TABLE_COMMANDS.ALIGN_CENTER:
          return this.alignTable("center", options);
        case TABLE_COMMANDS.ALIGN_RIGHT:
          return this.alignTable("right", options);
        default:
          return { changed: false, html: "", reason: "unknown-table-command" };
      }
    },
    addRowAbove() {
      return this.insertRow({ where: "above" });
    },
    addRowBelow(options = {}) {
      return this.insertRow({ ...options, where: "below" });
    },
    addColumnLeft() {
      return this.insertColumn({ where: "left" });
    },
    addColumnRight() {
      return this.insertColumn({ where: "right" });
    },
    deleteRow(options = {}) {
      const context = activeContext();
      const tableId = options.tableId || context.tableId;
      if (!tableId) return { changed: false, html: "" };
      return mutate(
        tableId,
        (table) =>
          deleteTableRow(table, {
            rowId: options.rowId || context.rowId,
            cellId: options.cellId || context.cellId,
          }),
        { operation: TABLE_COMMANDS.DELETE_ROW, source: options.source || "table-command" }
      );
    },
    deleteColumn(options = {}) {
      const context = activeContext();
      const tableId = options.tableId || context.tableId;
      if (!tableId) return { changed: false, html: "" };
      return mutate(
        tableId,
        (table) =>
          deleteTableColumn(table, {
            cellId: options.cellId || context.cellId,
            columnIndex: Number.isInteger(options.columnIndex)
              ? options.columnIndex
              : context.columnIndex,
          }),
        { operation: TABLE_COMMANDS.DELETE_COLUMN, source: options.source || "table-command" }
      );
    },
    deleteTable(options = {}) {
      const context = activeContext();
      const tableId = options.tableId || context.tableId;
      if (!tableId) return { changed: false, html: "" };
      return mutate(
        tableId,
        (table) => {
          table.remove();
          return { changed: true, focusCellId: "", removeTable: true };
        },
        {
          operation: TABLE_COMMANDS.DELETE_TABLE,
          source: options.source || "table-command",
          allowTableRemoval: true,
        }
      );
    },
    distributeColumns(options = {}) {
      const context = activeContext();
      const tableId = options.tableId || context.tableId;
      if (!tableId) return { changed: false, html: "" };
      return mutate(
        tableId,
        (table) => ({ changed: distributeTableColumns(table) }),
        { operation: TABLE_COMMANDS.DISTRIBUTE_COLUMNS, source: options.source || "table-command" }
      );
    },
    toggleHeaderRow(options = {}) {
      const context = activeContext();
      const tableId = options.tableId || context.tableId;
      if (!tableId) return { changed: false, html: "" };
      return mutate(
        tableId,
        (table) => ({
          changed: toggleTableHeaderRow(table),
          focusCellId: options.cellId || context.cellId,
        }),
        { operation: TABLE_COMMANDS.TOGGLE_HEADER_ROW, source: options.source || "table-command" }
      );
    },
    resizeColumn(options = {}) {
      const context = activeContext();
      const tableId = options.tableId || context.tableId;
      if (!tableId) return { changed: false, html: "" };
      return mutate(
        tableId,
        (table) => ({
          changed: resizeTableColumn(table, {
            columnIndex: Number.isInteger(options.columnIndex)
              ? options.columnIndex
              : context.columnIndex,
            deltaX: options.deltaX || 0,
            adjacent: options.adjacent !== false,
          }),
          focusCellId: options.cellId || context.cellId,
        }),
        { operation: "resize-column", source: options.source || "table-resize" }
      );
    },
    resizeRow(options = {}) {
      const context = activeContext();
      const tableId = options.tableId || context.tableId;
      if (!tableId) return { changed: false, html: "" };
      return mutate(
        tableId,
        (table) => ({
          changed: resizeTableRow(table, {
            rowId: options.rowId || context.rowId,
            rowIndex: options.rowIndex,
            height: options.height,
            deltaY: options.deltaY || 0,
          }),
          focusCellId: options.cellId || context.cellId,
        }),
        { operation: "resize-row", source: options.source || "table-resize" }
      );
    },
    resizeTable(options = {}) {
      const context = activeContext();
      const tableId = options.tableId || context.tableId;
      if (!tableId) return { changed: false, html: "" };
      return mutate(
        tableId,
        (table) => ({
          changed: resizeTableWidth(table, options),
          focusCellId: options.cellId || context.cellId,
        }),
        { operation: "resize-table", source: options.source || "table-resize" }
      );
    },
    alignTable(alignment = "left", options = {}) {
      const context = activeContext();
      const tableId = options.tableId || context.tableId;
      if (!tableId) return { changed: false, html: "" };
      return mutate(
        tableId,
        (table) => ({
          changed: setTableAlignment(table, alignment),
          focusCellId: options.cellId || context.cellId,
        }),
        {
          operation:
            alignment === "center"
              ? TABLE_COMMANDS.ALIGN_CENTER
              : alignment === "right"
              ? TABLE_COMMANDS.ALIGN_RIGHT
              : TABLE_COMMANDS.ALIGN_LEFT,
          source: options.source || "table-command",
        }
      );
    },
    commitTableDraft,
    commitLiveTable,
    attach(options = {}) {
      const root = resolveRoot();
      if (!root) return () => {};

      if (boundKeyboardRoot && boundKeyboardHandler) {
        boundKeyboardRoot.removeEventListener("keydown", boundKeyboardHandler, true);
      }
      boundKeyboardRoot = root;
      boundKeyboardHandler = (event) => {
        handleKeyDown(event);
      };
      root.addEventListener("keydown", boundKeyboardHandler, true);

      controlsController?.destroy?.();
      controlsController = null;
      const controlsEnabled = options.enableObjectControls ?? enableObjectControls;
      if (controlsEnabled) {
        controlsController = createDocWriterTableControls({
          getRoot: resolveRoot,
          tableApi: this,
          onRequestMove: options.onRequestTableMove ?? onRequestTableMove,
          onRequestResize: options.onRequestTableResize ?? onRequestTableResize,
          confirmDeleteTable: options.confirmDeleteTable ?? confirmDeleteTable,
          onEditingChange: options.onEditingChange ?? onEditingChange,
        });
      }

      return () => this.detach();
    },
    detach() {
      if (boundKeyboardRoot && boundKeyboardHandler) {
        boundKeyboardRoot.removeEventListener("keydown", boundKeyboardHandler, true);
      }
      boundKeyboardRoot = null;
      boundKeyboardHandler = null;
      controlsController?.destroy?.();
      controlsController = null;
    },
    refreshControls() {
      controlsController?.refresh?.();
    },
    closeEditPanel() {
      controlsController?.close?.();
    },
    getControlsState() {
      return controlsController?.getState?.() || { activeTableId: "", editOpen: false };
    },
    handleKeyDown,
    resolveDropTargetAtPoint(clientX, clientY, options = {}) {
      return resolveTableDropTargetAtPoint(resolveRoot(), clientX, clientY, options);
    },
    getDragAutoScrollDelta(pointerY, scrollRect, options = {}) {
      return getTableDragAutoScrollDelta(pointerY, scrollRect, options);
    },
    snapshotDocument() {
      return snapshotHostDocument(resolveRoot()).html;
    },
    serialize() {
      // Compatibility alias. Whole-document serialization belongs to the host.
      return snapshotHostDocument(resolveRoot()).html;
    },
    serializeCanonical() {
      // Compatibility alias retained for older callers. It delegates to Canvas
      // instead of constructing a second table-owned document.
      return snapshotHostDocument(resolveRoot()).html;
    },
  };
}

export default createDocWriterTable;
